import {
  useEffect,
  useState,
  useRef,
  useContext,
  useLayoutEffect,
  use,
} from "react";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { useWebSearch } from "./webSearchContext"; // Import your context  -
import { supabase } from "../supabaseClient";
import { ApiTokenContext } from "../Context/ApiTokenContext";
import Record from "./Record";
import MicButton from "./common/MicButton";
import { AudioButton } from "./audio_video/AudioButton";
import VoiceAssistantUI from "./audio_video/VoiceAssistantUI";
import UploadFile from "./UploadFile";
import moment from "moment-timezone";
import ModelSearch from "./common/ModelSearch";
import ConfirmationPopup from "./common/ConfirmationPopup";
import AnimatedText from "./TypingHtmlRespons"; // Assuming the AnimatedText component is in the same directory
import WelcomeChatbot from "./WelcomeChatbot"; // Import WelcomeChatbot component
import React from "react";

// A2UI - Rich interactive UI rendering
import { A2UIRenderer, isA2UIContent } from "../a2ui";
// Generated Image Card for AI image generation
import GeneratedImageCard from "./common/GeneratedImageCard";
import { saveDraft, getDraft, deleteDraft } from "./common/DraftHistory";
import Swal from "sweetalert2";
import { Fab, Action } from "react-tiny-fab";
import "react-tiny-fab/dist/styles.css";

import { useVoice } from "../Context/VoiceContext";

import {
  storeChatHistory,
  storeQueueHistory,
  getChatHistoy,
  uploadFileAndGetUrl,
  uploadAudioAndGetUrl,
  clearChatHistory,
} from "../chatHistory"; // Import the createChatSession function

import {
  checkCurrentUserSession,
  getSessionType,
  createChatSession,
} from "../chatSession"; // Import the createChatSession function
import "../assets/css/chatbot.css"; // Import the new CSS file
import "../assets/css/robot.css"; // Import the new CSS file
import SessionPopup from "./common/SessionPopup"; // Import SessionPopup component show={show} onHide={handleClose}
import ServerErrorModal from "./common/ServerErrorModal"; // Import SessionPopup component show={show} onHide={handleClose}
import AddToSessionModal from "./Addtosessionmodal";
import NotesModal from "./NotesModal";
import ReplyModal from "./ReplyModal";
import QuickIdeaPopup from "./common/QuickIdeaPopup";
import DebugDetailsPanel from "./common/DebugDetailsPanel";
import ModeSelector, { getSelectedMode } from "./common/ModeSelector";
import ContextSettingsModal from "./common/ContextSettingsModal";
import { saveCustomContextLimits } from "../utils/contextLimits";
import RechargeModal from "./billing/RechargeModal";

import { firstMessage } from "../utils/chatMessage"; // Import firstMessage function
import { 
  parseStreamEvent, 
  categorizeStreamEvent, 
  getToolStatusMessage,
  isDebugUIEnabled,
  StreamEventAccumulator 
} from "../utils/uiMessageStream"; // UI Message Stream parser
import { SelectedItemContext } from "./common/ModelSearch";
import Typist from "react-typist-component";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

// DOMPurify configuration for safe HTML rendering
const sanitizeConfig = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'b',
                  'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'code', 'pre',
                  'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'span', 'div'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
};

const sanitizeHTML = (html) => DOMPurify.sanitize(html, sanitizeConfig);
const MORNING_CATCHUP_SESSION_STORAGE_KEY =
  "dismissedMorningCatchUpNotificationIds";

const normalizeNotificationId = (value) =>
  value === null || value === undefined ? null : String(value);

const loadDismissedMorningCatchUpIds = () => {
  if (typeof window === "undefined") return [];

  try {
    const rawValue = window.sessionStorage.getItem(
      MORNING_CATCHUP_SESSION_STORAGE_KEY,
    );
    if (!rawValue) return [];

    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((value) => normalizeNotificationId(value))
      .filter(Boolean);
  } catch {
    return [];
  }
};

// Helper function to detect and handle session navigation from AI response
const checkAndNavigateSession = async (messageText) => {
  // Debug: Log what we're checking
  console.log('[ChatBot] Checking navigation in:', messageText.substring(0, 150));

  // Check for multiple session navigation patterns:
  // - "Opening aa... done!" (most common - LLM simplified format)
  // - "Opening "name"... done!"
  // - "📂 Opening **"name"**... Navigating now!"
  const patterns = [
    /opening\s+([a-zA-Z0-9_\-\s]+?)(?:\.{2,3}|…)\s*(?:done|navigat)/i,  // Simple: Opening name... done
    /opening\s*"([^"]+)"(?:\.{2,3}|…)\s*(?:done|navigat)/i,             // With quotes: Opening "name"... done
    /opening\s*\*\*"?([^"*]+)"?\*\*(?:\.{2,3}|…)\s*(?:done|navigat)/i,  // Markdown: Opening **name**... done
  ];

  let sessionName = null;
  for (const pattern of patterns) {
    const match = messageText.match(pattern);
    if (match) {
      sessionName = match[1].trim();
      console.log('[ChatBot] Pattern matched, extracted session name:', sessionName);
      break;
    }
  }

  if (sessionName) {
    console.log('[ChatBot] Session navigation detected for:', sessionName);

    // Find the session by name and set cookie
    try {
      const { data: sessions } = await supabase
        .from('chat_session')
        .select('id, name')
        .ilike('name', `%${sessionName}%`)
        .limit(1);

      if (sessions && sessions.length > 0) {
        const targetSession = sessions[0];
        console.log('[ChatBot] Navigating to session:', targetSession.id, targetSession.name);
        Cookies.set('chat_session_id', targetSession.id);

        // Small delay to ensure cookie is set, then reload
        setTimeout(() => {
          window.location.reload();
        }, 500);
        return true;
      }
    } catch (error) {
      console.error('[ChatBot] Error navigating to session:', error);
    }
  }
  return false;
};

// Helper function to get file icon based on file extension
const getFileIcon = (fileName, size = 24, color = "currentColor") => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  const iconStyles = { width: size, height: size, flexShrink: 0 };

  // PDF Icon - Red
  if (extension === "pdf") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H11V8H7V10ZM15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#E53935"/>
        <path d="M14 3V9H20L14 3Z" fill="#FFCDD2"/>
        <text x="12" y="19" fontSize="6" fontWeight="bold" fill="white" textAnchor="middle">PDF</text>
      </svg>
    );
  }

  // Excel/Spreadsheet Icon - Green
  if (["xlsx", "xls", "csv"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#43A047"/>
        <path d="M14 3V9H20L14 3Z" fill="#C8E6C9"/>
        <path d="M7 13H9V15H7V13ZM10 13H12V15H10V13ZM13 13H15V15H13V13ZM7 16H9V18H7V16ZM10 16H12V18H10V16ZM13 16H15V18H13V16Z" fill="white"/>
      </svg>
    );
  }

  // Word Document Icon - Blue
  if (["docx", "doc"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#1976D2"/>
        <path d="M14 3V9H20L14 3Z" fill="#BBDEFB"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H13V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Text File Icon - Gray
  if (extension === "txt") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#757575"/>
        <path d="M14 3V9H20L14 3Z" fill="#E0E0E0"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H12V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Default Document Icon
  return (
    <svg viewBox="0 0 24 24" fill={color} style={iconStyles}>
      <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2ZM19 20H5V4H14V9H19V20Z"/>
    </svg>
  );
};

// Helper function to get file type from file name
const getFileTypeFromName = (fileName) => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  if (["jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") {
    return "pdf";
  }
  if (["xlsx", "xls"].includes(extension)) {
    return "excel";
  }
  if (extension === "csv") {
    return "csv";
  }
  if (["docx", "doc"].includes(extension)) {
    return "word";
  }
  if (extension === "txt") {
    return "txt";
  }
  return "document";
};

// Helper function to extract Attachment_URL from message text
// Returns { url: string, cleanText: string, fileType: string } or null if no attachment
const extractAttachmentFromText = (text) => {
  if (!text) return null;
  
  // Match pattern: Attachment_URL: [url] or Attachment_URL:[url]
  const match = text.match(/Attachment_URL:\s*\[([^\]]+)\]/i);
  if (!match) return null;
  
  const url = match[1].trim();
  // Remove the Attachment_URL part from text
  const cleanText = text.replace(/\n?\s*Attachment_URL:\s*\[([^\]]+)\]/i, '').trim();
  
  // Determine file type from URL
  const urlLower = url.toLowerCase();
  let fileType = 'document';
  
  if (/\.(jpg|jpeg|png|gif|bmp|tiff|webp)(\?|$)/i.test(urlLower)) {
    fileType = 'image';
  } else if (/\.pdf(\?|$)/i.test(urlLower)) {
    fileType = 'pdf';
  } else if (/\.(xlsx|xls)(\?|$)/i.test(urlLower)) {
    fileType = 'excel';
  } else if (/\.csv(\?|$)/i.test(urlLower)) {
    fileType = 'csv';
  } else if (/\.(docx|doc)(\?|$)/i.test(urlLower)) {
    fileType = 'word';
  } else if (/\.txt(\?|$)/i.test(urlLower)) {
    fileType = 'txt';
  } else if (url.includes('supabase') && url.includes('/images/')) {
    // Supabase image storage URLs may not have extension
    fileType = 'image';
  }
  
  return { url, cleanText, fileType };
};

const ChatBot = ({
  setClearChat,
  setSetMessages,
  toggleTheme,
  isDarkMode,
  notifications = [],
  summary,
  ChatEventChannel,
  IsSidebardNotificationVisible,
  IsSidebarVisible,
  onSetLoading,
  onQueueSessionRemove,
  setQueueRefreshKey,
  onOpenQueueModal,
  onZoomeyCall,
  onResetSessionBtn,
  onChatSessionActivated,
  userId,
  userName,
  uiContext,
  // Voice Assistant control - from App.jsx via OpenAIRealtimeTest ref
  onTriggerVoice,  // (mode: 'audio' | 'webcam' | 'screen') => void
  onStopVoice,     // () => void
  onStopVoiceVideo, // () => void - stop only video/screen, keep voice active
  isVoiceActive,   // boolean - whether Voice Assistant is currently active
  isVoiceVideoEnabled, // boolean - whether camera/screen is active
  isVoiceScreenSharing, // boolean - true if screen share, false if camera
}) => {
  // Debug: Check if userId is received
  console.log("ChatBot received userId:", userId);

  const [showClearQueueConfirm, setShowClearQueueConfirm] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [catchUpLoading, setCatchUpLoading] = useState(false);
  const [catchUpToast, setCatchUpToast] = useState(null);
  const [showCatchUpProgressModal, setShowCatchUpProgressModal] = useState(false);
  const [showCatchUpSettings, setShowCatchUpSettings] = useState(false);
  const [showMorningCatchUpModal, setShowMorningCatchUpModal] = useState(false);
  const [showQuickIdeaPopup, setShowQuickIdeaPopup] = useState(false);
  const [quickIdeaToast, setQuickIdeaToast] = useState(null);
  const [fetchedMorningCatchUpNotification, setFetchedMorningCatchUpNotification] =
    useState(null);
  const [dismissedMorningCatchUpIds, setDismissedMorningCatchUpIds] = useState(
    () => loadDismissedMorningCatchUpIds(),
  );
  const [morningCatchUpClock, setMorningCatchUpClock] = useState(() =>
    Date.now(),
  );
  const [catchUpSettings, setCatchUpSettings] = useState({
    tasks: false, lists: false, notes: false, groceries: false,
    reminders: false, scheduled_tasks: false, queue: false,
    gmail_work: false, gmail_personal: false,
    outlook_work: false, outlook_personal: false,
    calendar_work: false, calendar_personal: false,
    memories: false, web_search: false, second_brain: false, custom_instructions: "",
    brief_mode: "smart",
    morning_catchup_enabled: true, morning_catchup_hour: 9, morning_catchup_minute: 0, morning_catchup_ampm: "AM",
  });
  const [catchUpSettingsLoading, setCatchUpSettingsLoading] = useState(false);
  const [connectedIntegrations, setConnectedIntegrations] = useState(new Set());
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false); // State for refresh button
  const [animateSyncBtn, setAnimateSyncBtn] = useState(false);
  const [queueRequests, setQueueRequests] = useState([]); // State for queue requests
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [ShowMentions, setShowMentions] = useState(false);
  const [Mentions, setMentions] = useState([]);
  const [selectedMention, setSelectedMention] = useState(null); // { label: "@Notion", action: "notion" }
  const [directKey, setIsDirect] = useState("");
  const [showServerError, setShowServerError] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState(null);
  const [audioInput, setAudioInput] = useState(null);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatName, setChatName] = useState(null);
  const [ChatAsistantId, setChatAsistantId] = useState(null);
  const [chatSessionType, setChatSessionType] = useState(null);
  const [chatSessionParentId, setChatSessionParentId] = useState(null);
  const [showFormPopup, setShowFormPopup] = useState(false);
  const [isNewSession, setIsNewSession] = useState(false);
  const [sessionToRename, setSessionToRename] = useState(null);
  const [sessionCurrentMessage, setSessionCurrentMessage] = useState(null);
  const [isPlayingId, setPlayingId] = useState(null);
  const [isPlaying, setPlaying] = useState(false);
  const { webSearch, setWebSearch } = useWebSearch(); // Access the context
  const [queue, setQueue] = useState(false);
  // Dual queue mode: 'none' | 'connected' | 'unconnected'
  const [queueMode, setQueueMode] = useState('none');
  const [queuePopupType, setQueuePopupType] = useState(null); // 'connected' | 'unconnected'
  const [longPressTimer, setLongPressTimer] = useState(null);
  const LONG_PRESS_DURATION = 500; // ms for long-press detection
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showModalSearch, setShowModalSearch] = useState(true);
  const [responseId, setResponseId] = useState(null);
  const [isUserQueyLoading, setIsUserQueyLoading] = useState(false);
  const [Rendered, setRendered] = useState(false);
  const [ScrollToBottomVar, setScrollToBottom] = useState(false);
  const [isUserManuallyScroll, setIsUserManuallyScroll] = useState(false);
  const [ResponseText, setResponseText] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false); // Add streaming state
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const prevStreamingRef = useRef(false);

  // Dispatch event when streaming ends so SidebarCredits can refresh
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming) {
      window.dispatchEvent(new Event("zoomey-credits-refresh"));
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming]);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [queueDetails, setQueueDetails] = useState("");
  // Queue popup file upload states
  const [queuePopupFile, setQueuePopupFile] = useState(null);
  const [queuePopupFilePreview, setQueuePopupFilePreview] = useState(null);
  const queuePopupFileInputRef = useRef(null);
  // Queue popup mentions states
  const [queueShowMentions, setQueueShowMentions] = useState(false);
  const [queueMentions, setQueueMentions] = useState([]);
  const [queueHighlightedIndex, setQueueHighlightedIndex] = useState(-1);
  const [voicePopupVisibility, setVoicePopupVisibility] = useState(false);
  const [voiceRecordingConnected, setVoiceRecordingConnected] = useState(false);
  const [voiceRecordingConnectedFailed, setVoiceRecordingConnectedFailed] =
    useState(false);
  const [copyToTextArea, setCopyToTextArea] = useState(false);
  const [cancelAudio, setCancelAudio] = useState(false);
  const [audioRecording, setAudioRecording] = useState(false);
  
  // Mode selector state - "simple" (default) or "smart"
  const [selectedMode, setSelectedMode] = useState(() => getSelectedMode());

  // Full Context custom limits
  const [fullContextEnabled, setFullContextEnabled] = useState(false);
  const [showContextSettings, setShowContextSettings] = useState(false);
  const [customContextLimits, setCustomContextLimits] = useState(null);

  const [responseInformMessage, setResponseInformMessage] =
    useState("Thinking");
  const [isMessagesLoading, setIsMessagesLoading] = useState(true);
  
  // Track tools used during current streaming response (for DEBUG_UI)
  const toolsUsedRef = useRef([]);

  const [isTitleEdited, setIsTitleEdited] = useState(false); // Track if user manually edited title

  const [showScrollUp, setShowScrollUp] = useState(false);
  const [zoomeyApiUrl, setZoomeyAdkUrl] = useState(
    process.env.REACT_APP_ZOOMEY_API_URL
  );

  const [showAddToSessionModal, setShowAddToSessionModal] = useState(false);
  const [showScrollBottomButton, setShowScrollBottomButton] = useState(false);
  const [ApiSessionId, setApiSessionId] = useState(() => {
    // Get the value from localStorage, or null if not set
    return localStorage.getItem("ApiSessionID") || false;
  });

  const [showNotesModal, setShowNotesModal] = useState(false); // Add this state
  const [autoScrolling, setAutoScrolling] = useState(true);
  const [addToChat, setAddToChat] = useState(null);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [previousScrollPositionNew, setPreviousScrollPositionNew] = useState(0);
  const quickIdeaToastTimerRef = useRef(null);
  const intervalIdRef = useRef(null);
  const isManualScrollTop = useRef(false);
  const isFirstScrollUp = useRef(true); // Initially true
  const textareaRef = useRef(null);
  const mentionChipRef = useRef(null);
  const backspaceOnEmptyCount = useRef(0);
  var FirstLoad = true;
  const MAX_ROWS = 7;
  const navigate = useNavigate();
  const location = useLocation();

  // Handle Google OAuth login_update call when redirected with sign_in=completed
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const signInCompleted = params.get('sign_in');

    if (signInCompleted === 'completed') {
      const callLoginUpdate = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            await fetch(`${process.env.REACT_APP_ZOOMEY_API_URL}login_update`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
            });
          }
        } catch (err) {
          console.error("Login update failed:", err);
        }
      };
      callLoginUpdate();

      // Remove the query parameter from URL
      params.delete('sign_in');
      const newUrl = params.toString()
        ? `${location.pathname}?${params.toString()}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, navigate]);

  const {
    // Legacy multimodal
    client,
    isConnected,
    sessionId,
    isModelSpeaking,
    onTextReceived,
    onUserTextReceived,
    isModelFunctionCall,
    connect,
    startRecording,
    stopRecording,
    initializeWebcam,
    initializeScreenShare,
    stopVideo,
    showVoicePopup,
    setShowVoicePopup,
    voiceAnimationState,
    voicePosition,
    setVoicePosition,
    startVoice,
    stopVoice,
    captureAndUploadScreenshot,
  } = useVoice();

  const [startVoiceRecording, setStartVoiceRecording] = useState(false);
  const videoRef = useRef(null);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [activeVideoMode, setActiveVideoMode] = useState(null);
  const voicePopupRef = useRef(null);

  const [sessionLoaded, setSessionLoaded] = useState(false);

  const isScreenSharingSupported = () => {
    return navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia;
  };

  useEffect(() => {
    // setSessionLoaded(onChatSessionActivated);
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [onChatSessionActivated]);

  useEffect(() => {
    if (onTextReceived && Array.isArray(onTextReceived)) {
      // Add 3 second delay
      const timer = setTimeout(() => {
        // Join the array of text chunks into a single string
        const modelResponse = onTextReceived.join("");

        let messageDate = moment
          .utc()
          .tz(userTimeZone ? userTimeZone : "America/Toronto")
          .format("MM-DD-YYYY hh:mm A");

        // Always add new bot message instead of updating
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: modelResponse,
            isUser: false,
            isHtml: true,
            default: false,
            time: messageDate,
            isTyping: false,
          },
        ]);

        // Store to database immediately
        storeChatHistory(
          chatSessionId,
          modelResponse,
          "text",
          false,
          null, // path
          null, // queue_id
          userId // user_id
        );

        // Auto-scroll if enabled
        if (autoScrollingRef.current && chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      }, 2000); // 3 second delay

      // Cleanup function to clear timeout if dependencies change
      return () => clearTimeout(timer);
    }
  }, [onTextReceived]);

  // Replace the existing useEffect for onUserTextReceived
  useEffect(() => {
    if (onUserTextReceived && Array.isArray(onUserTextReceived)) {
      // Agar audioInput active hai, transcript UI mein na dikhaye
      if (audioInput) return; // <-- ADD THIS LINE

      // Join the array of text chunks into a single string
      const userText = onUserTextReceived.join("");
      let messageDate = moment
        .utc()
        .tz(userTimeZone ? userTimeZone : "America/Toronto")
        .format("MM-DD-YYYY hh:mm A");

      // Always add new user message
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: userText,
          isUser: true,
          isHtml: true,
          default: false,
          time: messageDate,
          isTyping: false,
        },
      ]);

      // Store user message to database immediately
      storeChatHistory(
        chatSessionId,
        userText,
        "text",
        true,
        null,
        null,
        userId
      );

      // Auto-scroll if enabled
      if (autoScrollingRef.current && chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }
  }, [onUserTextReceived, audioInput]);

  const handleCameraToggle = async () => {
    if (isVoiceVideoEnabled && !isVoiceScreenSharing) {
      // Stop camera if already active
      onStopVoiceVideo?.();
    } else {
      // Trigger Voice Assistant with webcam mode via OpenAI Realtime
      onTriggerVoice?.('webcam');
    }
  };

  const handleScreenToggle = async () => {
    if (isVoiceVideoEnabled && isVoiceScreenSharing) {
      // Stop screen share if already active
      onStopVoiceVideo?.();
    } else {
      // Trigger Voice Assistant with screen share mode via OpenAI Realtime
      onTriggerVoice?.('screen');
    }
  };

  // Handle audio button click - toggles Voice Assistant
  const handleAudioClick = () => {
    if (isVoiceActive) {
      onStopVoice?.();
    } else {
      onTriggerVoice?.('audio');
    }
  };

  // Handle screenshot capture via voice command or button
  const handleCaptureScreenshot = async () => {
    if (!isVideoActive || activeVideoMode !== "screen") {
      console.warn("[ChatBot] Screenshot only available during screen share");
      return null;
    }
    return await captureAndUploadScreenshot();
  };

  const stopVoiceAssistant = () => {
    // Stop video if active
    if (isVideoActive) {
      stopVideo();
      setIsVideoActive(false);
      setActiveVideoMode(null);
    }
    setStartVoiceRecording(false);
    stopRecording();
    stopVoice();
    setShowVoicePopup(false);
  };

  useEffect(() => {
    if (onZoomeyCall) {
      stopVoiceAssistant();
    }
  }, [onZoomeyCall]);

  useEffect(() => {
    const startVideoStreamFunction = async () => {
      if (isVideoActive && activeVideoMode === "webcam") {
        // Initialize webcam with OpenAI Realtime
        const success = await initializeWebcam(videoRef.current);
        if (success) {
          await startRecording();
          if (isConnected) {
            client.startVideoStream(1);
          }
        }
      }

      if (isVideoActive && activeVideoMode === "screen") {
        // Initialize screen share with OpenAI Realtime
        const success = await initializeScreenShare(videoRef.current);
        if (success) {
          await startRecording();
          if (isConnected) {
            client.startVideoStream(1);
          }
        }
      }
    };
    startVideoStreamFunction();
  }, [isVideoActive, activeVideoMode]);

  // Cookies.remove('is_call');
  const { showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage } =
    usePublicKeyInvalid();

  // Cleanup useEffect for AbortController
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      if (quickIdeaToastTimerRef.current) {
        window.clearTimeout(quickIdeaToastTimerRef.current);
      }
    };
  }, []);

  let initializeChatSessionRun = true;
  // Add new state for messages and input
  // const [messages, setMessages] = useState([]);

  // const [messages, setMessages] = useState([
  //   { text: 'Hi! 👋 How can I help you today?', isUser: false, default: true , time: false}
  // ])
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");

  // Add ref for chat container
  const chatContainerRef = useRef(null);
  const autoScrollingRef = useRef(null);
  const topMessageRef = useRef(null);

  // use for (handle multiple response printed issue)
  const messageIndexRef = useRef(null);

  // Ref to track current streaming content for stop functionality
  const currentStreamingContentRef = useRef("");

  // Ref to prevent multiple simultaneous loads
  const isLoadingRef = useRef(false);
  // Ref to track if we're adjusting scroll programmatically
  const isAdjustingScrollRef = useRef(false);
  // Ref to track previous scroll position
  const previousScrollTopRef = useRef(0);

  // Add loading state
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1); // Current page for pagination
  const [hasMore, setHasMore] = useState(true);
  const prevPageRef = useRef(page);
  // Add new state for selected file
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [showQueryInProgressWarning, setShowQueryInProgressWarning] = useState(false);
  const controllerRef = useRef(null);
  const currentResponseIdRef = useRef(null); // Track current response ID for stop functionality
  const sessionModelCookie = Cookies.get("session_model_name");
  const parsedSessionModel = (() => {
    if (!sessionModelCookie) return null;
    try {
      const parsed = JSON.parse(sessionModelCookie);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  })();
  const selectedItem =
    parsedSessionModel && typeof parsedSessionModel.id === "string"
      ? parsedSessionModel.id
      : undefined;
  const hasExplicitCustomModel =
    parsedSessionModel?.source === "manual" && typeof selectedItem === "string" && selectedItem.trim().length > 0;
  const [showScrollDown, setShowScrollDown] = useState(true);
  const [show, setShow] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [queueTitle, setQueueTitle] = useState("");
  const [queueMessage, setQueueMessage] = useState("");
  // const [apiToken, setApiToken] = useState(null);
  const { apiToken, setApiToken } = useContext(ApiTokenContext);

  const actionLists = {
    "@Notion": "notion",
    "@Task": "task",
    "@Lists": "lists",
    "@Notes": "notes",
    "@Contacts": "contacts",
    "@Groceries": "groceries",
    "@GoogleCalendar": "google_calendar",
    "@Gmail": "gmail",
    "@GoogleDrive": "google_drive",
    "@GoogleDocs": "google_docs",
    "@GoogleSheets": "google_sheets",
    "@DeepResearch": "deep_research",
    "@WebSearch": "web_search",
    "@YoutubeScraping": "youtube_scraping",
    "@InstagramScraping": "instagram_scraping",
    "@ScrapeUrl": "scrape_url",
    "@Reminders": "reminders",
    "@SecondBrain": "second_brain",
    "@TikTok": "tik_tok",
    "@Topics": "sessions",
    "@Meetings": "meetings",
    "@SMS": "sms",
    "@KnowledgeBase": "knowledge_base",
    "@Files": "files",
    "@Notifications": "notifications",
    "@ImageGeneration": "image_generation",
    "@ScheduledTasks": "scheduled_tasks",
    "@Ideas": "ideas",
  };

  // const zoomeyToolShortMessages = {
  //   search_assistant: "Searching in web",
  //   tasks_lists_notes_agent: "Managing your tasks, lists, or notes",
  //   reminders_agent: "Managing your reminders",
  //   contact_agent: "Managing your contacts",
  //   session_agent: "Managing your sessions",
  //   notion_agent: "Managing Notion workspace",
  //   retrieval_agent: "Retrieving your meeting data",
  //   scraping_agent: "Reading content from the provided link",
  //   calendar_agent: "Managing your Google Calendar",
  //   gmail_agent: "Managing with your Gmail",
  //   get_history: "Fetching our conversation history",
  //   read_image_file: "Analyzing your image",
  //   search_in_history: "Searching our conversation history",
  //   execute_db_query: "Analyzing with supabase",
  //   deep_research: "Doing deep research…",
  // };

  const zoomeyToolShortMessages = {
    search_agent: "Retrieving web results",
    tasks_lists_notes_agent: "Managing tasks & notes",
    reminders_agent: "Processing reminders",
    contact_agent: "Managing contacts",
    session_agent: "Handling sessions",
    notion_assistant: "Syncing with Notion",
    retrival_assistant: "Fetching meeting data",
    scraping_agent: "Reading web content",
    calendar_agent: "Processing calendar",
    gmail_agent: "Processing Gmail",
    get_history: "Loading chat history",
    read_image_file: "Analyzing image",
    search_in_history: "Searching conversations",
    execute_db_query: "Querying database",
    deep_research: "Deep researching",
    add_to_second_brain_project: "Adding to Second Brain Project",
    get_to_second_brain: "Retrieving from Second Brain",
    search_in_second_brain: "Searching in Second Brain",
    add_to_second_brain_area: "Adding to Second Brain Area",
    add_to_second_brain_resource: "Adding to Second Brain Resource",
    add_to_second_brain_archive: "Adding to Second Brain Archive",
  };

  function formatMentionSpacing(text) {
    // Ensures a space after colon for @option: userquery
    return text.replace(/(@\w+):\s*/g, "$1: ");
  }

  // Add ref for queue textarea
  const queueTextareaRef = useRef(null);
  // --- Add this state at the top with other states ---
  const [isManualQueueResize, setIsManualQueueResize] = useState(false);
  const [queueTextareaHeight, setQueueTextareaHeight] = useState(44); // 44px min height

  // Focus textarea when modal opens
  React.useEffect(() => {
    if (showQueueModal && queueTextareaRef.current) {
      queueTextareaRef.current.focus();
    }
  }, [showQueueModal]);

  // --- Update auto-resize effect for queue textarea ---
  useEffect(() => {
    if (queueTextareaRef.current && !isManualQueueResize) {
      queueTextareaRef.current.style.height = "auto";
      queueTextareaRef.current.style.height =
        queueTextareaRef.current.scrollHeight + "px";
      setQueueTextareaHeight(queueTextareaRef.current.offsetHeight);
    }
  }, [queueMessage, showQueueModal, isManualQueueResize]);

  // Add these states at the top (with other states)
  const [isManualInputResize, setIsManualInputResize] = useState(false);
  const [inputTextareaHeight, setInputTextareaHeight] = useState(44);

  // Update auto-resize effect for main chat textarea
  useEffect(() => {
    if (textareaRef.current && !isManualInputResize) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
      setInputTextareaHeight(textareaRef.current.offsetHeight);
    }
  }, [inputText, isManualInputResize]);

  // Adjust textarea left padding when mention chip appears/disappears (DOM only, no re-render)
  // Uses setProperty with 'important' to beat CSS class !important rules
  useEffect(() => {
    if (!textareaRef.current) return;
    if (selectedMention && mentionChipRef.current) {
      const chipW = mentionChipRef.current.offsetWidth;
      textareaRef.current.style.setProperty("padding-left", `${chipW + 14}px`, "important");
    } else {
      textareaRef.current.style.setProperty("padding-left", "12px", "important");
    }
  }, [selectedMention]);

  const handleMainSessionClick = async () => {
    Cookies.remove("chat_session_id"); // Remove the chat_session_id cookie
    // navigate('/chat-bot');
    window.location.reload(); // Navigate to the chat-bot route
  };

  const handleCatchUpClick = async () => {
    if (catchUpLoading) return;
    setCatchUpLoading(true);
    try {
      const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
      const res = await fetch(`${NEXT_API_URL}/api/catch-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: userId,
          timezone: userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowCatchUpProgressModal(true);
      } else {
        setCatchUpToast({ type: "error", message: data.error || "Failed to generate catch-up brief." });
        setTimeout(() => setCatchUpToast(null), 5000);
      }
    } catch (err) {
      console.error("[catch-up] Request failed:", err);
      setCatchUpToast({ type: "error", message: "Something went wrong. Please try again." });
      setTimeout(() => setCatchUpToast(null), 5000);
    } finally {
      setCatchUpLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.sessionStorage.setItem(
        MORNING_CATCHUP_SESSION_STORAGE_KEY,
        JSON.stringify(dismissedMorningCatchUpIds.slice(-20)),
      );
    } catch {
      // Ignore session storage errors for this optional UI state.
    }
  }, [dismissedMorningCatchUpIds]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const intervalId = window.setInterval(() => {
      setMorningCatchUpClock(Date.now());
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setFetchedMorningCatchUpNotification(null);
      return;
    }

    let isActive = true;

    const loadLatestMorningCatchUp = async () => {
      try {
        const { data, error } = await supabase
          .from("notifications")
          .select("id, title, message, created_at, is_read")
          .eq("user_id", userId)
          .eq("title", "Daily Morning Catchup")
          .eq("is_read", false)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (isActive) setFetchedMorningCatchUpNotification(data || null);
      } catch (error) {
        console.error(
          "[morning-catchup] Failed to load latest catch-up notification:",
          error,
        );
        if (isActive) setFetchedMorningCatchUpNotification(null);
      }
    };

    loadLatestMorningCatchUp();

    return () => {
      isActive = false;
    };
  }, [userId]);

  const morningCatchUpCandidates = Array.isArray(notifications)
    ? [...notifications]
    : [];

  if (
    fetchedMorningCatchUpNotification &&
    !morningCatchUpCandidates.some(
      (notification) => notification.id === fetchedMorningCatchUpNotification.id,
    )
  ) {
    morningCatchUpCandidates.unshift(fetchedMorningCatchUpNotification);
  }

  const latestMorningCatchUpNotification = morningCatchUpCandidates
    .filter(
      (notification) =>
        notification?.title === "Daily Morning Catchup" &&
        !notification?.is_read,
    )
    .sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime(),
    )[0] || null;

  // Today's latest morning catchup (read or unread) - for sun icon click after reading
  const todaysMorningCatchUp = morningCatchUpCandidates
    .filter(
      (notification) =>
        notification?.title === "Daily Morning Catchup",
    )
    .sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime(),
    )[0] || null;

  const morningCatchUpTimezone =
    userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  let targetMorningCatchUpHour = catchUpSettings.morning_catchup_hour ?? 9;
  if (
    catchUpSettings.morning_catchup_ampm === "PM" &&
    targetMorningCatchUpHour !== 12
  ) {
    targetMorningCatchUpHour += 12;
  }
  if (
    catchUpSettings.morning_catchup_ampm === "AM" &&
    targetMorningCatchUpHour === 12
  ) {
    targetMorningCatchUpHour = 0;
  }

  const currentMorningCatchUpMoment = moment(morningCatchUpClock).tz(
    morningCatchUpTimezone,
  );
  const currentMorningCatchUpTarget = currentMorningCatchUpMoment
    .clone()
    .hour(targetMorningCatchUpHour)
    .minute(catchUpSettings.morning_catchup_minute ?? 0)
    .second(0)
    .millisecond(0);
  const currentMorningCatchUpDiffMinutes =
    currentMorningCatchUpMoment.diff(
      currentMorningCatchUpTarget,
      "minutes",
      true,
    );
  const isWithinMorningCatchUpWindow =
    catchUpSettings.morning_catchup_enabled &&
    currentMorningCatchUpDiffMinutes >= 0 &&
    currentMorningCatchUpDiffMinutes < 5;
  const latestMorningCatchUpIsForToday = latestMorningCatchUpNotification
    ? moment(latestMorningCatchUpNotification.created_at)
        .tz(morningCatchUpTimezone)
        .isSame(currentMorningCatchUpMoment, "day")
    : false;

  // Check if today's catchup exists (read or unread)
  const hasTodaysMorningCatchUp = todaysMorningCatchUp
    ? moment(todaysMorningCatchUp.created_at)
        .tz(morningCatchUpTimezone)
        .isSame(currentMorningCatchUpMoment, "day")
    : false;

  // Sun icon stays visible as long as there's an unread morning catchup for today
  const unreadMorningCatchUpNotification =
    latestMorningCatchUpIsForToday &&
    latestMorningCatchUpNotification &&
    !dismissedMorningCatchUpIds.includes(
      normalizeNotificationId(latestMorningCatchUpNotification.id),
    )
      ? latestMorningCatchUpNotification
      : null;

  // Auto-popup modal only within the 5-min time window
  const activeMorningCatchUpNotification =
    isWithinMorningCatchUpWindow && unreadMorningCatchUpNotification
      ? unreadMorningCatchUpNotification
      : null;

  const handleMorningCatchUpClose = async () => {
    const notification = unreadMorningCatchUpNotification || todaysMorningCatchUp;
    const notificationId = normalizeNotificationId(
      notification?.id ||
        activeMorningCatchUpNotification?.id ||
        latestMorningCatchUpNotification?.id,
    );

    if (notificationId) {
      setDismissedMorningCatchUpIds((prev) => {
        if (prev.includes(notificationId)) return prev;
        return [...prev, notificationId].slice(-20);
      });
    }

    // Mark as read in DB if unread
    if (notification && !notification.is_read) {
      try {
        await supabase
          .from("notifications")
          .update({ is_read: true })
          .eq("id", notification.id)
          .eq("user_id", userId);
      } catch (err) {
        console.error("[morning-catchup] Failed to mark as read:", err);
      }
    }

    setShowMorningCatchUpModal(false);
  };

  useEffect(() => {
    if (!userId) return;
    const loadCatchUpSettings = async () => {
      try {
        const { data } = await supabase
          .from("catch_up_settings")
          .select("*")
          .eq("user_id", userId)
          .single();
        if (data) {
          setCatchUpSettings({
            tasks: data.tasks ?? false,
            lists: data.lists ?? false,
            notes: data.notes ?? false,
            groceries: data.groceries ?? false,
            reminders: data.reminders ?? false,
            scheduled_tasks: data.scheduled_tasks ?? false,
            queue: data.queue ?? false,
            gmail_work: data.gmail_work ?? false,
            gmail_personal: data.gmail_personal ?? false,
            outlook_work: data.outlook_work ?? false,
            outlook_personal: data.outlook_personal ?? false,
            calendar_work: data.calendar_work ?? false,
            calendar_personal: data.calendar_personal ?? false,
            memories: data.memories ?? false,
            second_brain: data.second_brain ?? false,
            web_search: data.web_search ?? false,
            custom_instructions: data.custom_instructions || "",
            brief_mode: data.brief_mode || "smart",
            morning_catchup_enabled: data.morning_catchup_enabled ?? true,
            morning_catchup_hour: data.morning_catchup_hour ?? 9,
            morning_catchup_minute: data.morning_catchup_minute ?? 0,
            morning_catchup_ampm: data.morning_catchup_ampm || "AM",
          });
        }
      } catch (err) {
        console.error("[catch-up-settings] Load error:", err);
      }
    };
    loadCatchUpSettings();
  }, [userId]);

  // Load custom context limits from profile
  useEffect(() => {
    if (!userId) return;
    const loadContextLimits = async () => {
      try {
        const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
        const res = await fetch(`${NEXT_API_URL}/api/user/profile?user_id=${userId}`);
        const data = await res.json();
        if (data.success && data.profile?.custom_context_limits) {
          const saved = data.profile.custom_context_limits;
          setFullContextEnabled(saved.enabled === true);
          const { enabled, ...limits } = saved;
          if (Object.keys(limits).length > 0) setCustomContextLimits(limits);
          saveCustomContextLimits(saved);
        }
      } catch (err) {
        console.error("[context-limits] Load error:", err);
      }
    };
    loadContextLimits();
  }, [userId]);

  useEffect(() => {
    if (!showCatchUpSettings || !userId) return;
    const fetchIntegrationStatus = async () => {
      setIntegrationsLoading(true);
      try {
        const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
        const res = await fetch(`${NEXT_API_URL}/api/integrations/status?user_id=${userId}`);
        const data = await res.json();
        if (data.success) {
          const connected = new Set();
          for (const conn of data.connections || []) {
            if (conn.app === "gmail" && conn.account_type) connected.add(`gmail_${conn.account_type}`);
            if (conn.app === "outlook" && conn.account_type) connected.add(`outlook_${conn.account_type}`);
            if (conn.app === "google_calendar" && conn.account_type) connected.add(`calendar_${conn.account_type}`);
          }
          setConnectedIntegrations(connected);
        }
      } catch (err) {
        console.error("[catch-up-settings] Integration status fetch error:", err);
      } finally {
        setIntegrationsLoading(false);
      }
    };
    fetchIntegrationStatus();
  }, [showCatchUpSettings, userId]);

  const INTEGRATION_TOGGLE_KEYS = new Set([
    "gmail_work", "gmail_personal", "outlook_work", "outlook_personal",
    "calendar_work", "calendar_personal",
  ]);

  const handleSaveCatchUpSettings = async () => {
    if (!userId) return;
    setCatchUpSettingsLoading(true);
    try {
      const { error } = await supabase
        .from("catch_up_settings")
        .upsert({
          user_id: userId,
          ...catchUpSettings,
          updated_at: new Date().toISOString(),
        }, { onConflict: "user_id" });
      if (error) throw error;
      setShowCatchUpSettings(false);
      setCatchUpToast({ type: "success", message: "Catch-up settings saved!" });
      setTimeout(() => setCatchUpToast(null), 3000);
    } catch (err) {
      console.error("[catch-up-settings] Save error:", err);
      setCatchUpToast({ type: "error", message: "Failed to save settings." });
      setTimeout(() => setCatchUpToast(null), 3000);
    } finally {
      setCatchUpSettingsLoading(false);
    }
  };

  const allCatchUpSourcesDisabled = !catchUpSettings.tasks && !catchUpSettings.lists &&
    !catchUpSettings.notes && !catchUpSettings.groceries && !catchUpSettings.reminders &&
    !catchUpSettings.scheduled_tasks && !catchUpSettings.queue && !catchUpSettings.gmail_work && !catchUpSettings.gmail_personal &&
    !catchUpSettings.outlook_work && !catchUpSettings.outlook_personal && !catchUpSettings.calendar_work &&
    !catchUpSettings.calendar_personal && !catchUpSettings.memories &&
    !catchUpSettings.second_brain && !catchUpSettings.web_search;

  const CATCHUP_SOURCE_OPTIONS = [
    { key: "tasks", label: "Tasks", icon: "bi-check2-square" },
    { key: "lists", label: "Lists", icon: "bi-list-ul" },
    { key: "notes", label: "Notes", icon: "bi-journal-text" },
    { key: "groceries", label: "Groceries", icon: "bi-cart3" },
    { key: "reminders", label: "Reminders", icon: "bi-alarm" },
    { key: "scheduled_tasks", label: "Scheduled Tasks", icon: "bi-clock-history" },
    { key: "queue", label: "Queue", icon: "bi-inbox" },
    { key: "gmail_work", label: "Gmail (Work)", icon: "bi-envelope" },
    { key: "gmail_personal", label: "Gmail (Personal)", icon: "bi-envelope-open" },
    { key: "outlook_work", label: "Outlook (Work)", icon: "bi-mailbox" },
    { key: "outlook_personal", label: "Outlook (Personal)", icon: "bi-mailbox2" },
    { key: "calendar_work", label: "Calendar (Work)", icon: "bi-calendar-event" },
    { key: "calendar_personal", label: "Calendar (Personal)", icon: "bi-calendar-heart" },
    { key: "memories", label: "Memory", icon: "bi-robot" },
    { key: "second_brain", label: "Second Brain", icon: "bi-lightbulb" },
    { key: "web_search", label: "Web Search", icon: "bi-globe" },
  ];

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  function cleanInputText(inputText) {
    const actionLists = {
      "@Notion": "notion",
      "@Task": "task",
      "@Lists": "lists",
      "@Notes": "notes",
      "@Contacts": "contacts",
      "@GoogleCalendar": "google_calendar",
      "@Gmail": "gmail",
      "@DeepResearch": "deep_research",
      "@WebSearch": "web_search",
      "@YoutubeScraping": "youtube_scraping",
      "@InstagramScraping": "instagram_scraping",
      "@ScrapeUrl": "scrape_url",
      "@Reminders": "reminders",
      "@SecondBrain": "second_brain",
      "@TikTok": "tik_tok",
      "@Topics": "sessions",
      "@Meetings": "meetings",
      "@SMS": "sms",
      "@Ideas": "ideas",
    };

    // Convert to lower case keys for easier matching
    const actionKeys = Object.keys(actionLists).map((k) => k.toLowerCase());

    // Get the first @action in the string
    const firstMatch = inputText.match(/@[\w\s]+/);
    const firstAction = firstMatch ? firstMatch[0] : null;

    let result = inputText;

    if (firstAction) {
      result = result.replace(/([\w@][\w\s@]*?):/g, (match, p1, offset) => {
        const lowerP1 = p1.trim().toLowerCase();
        // Skip the first action
        if (match.startsWith(firstAction)) return match;
        // If this is an action key, remove it
        if (actionKeys.includes(`@${lowerP1}`)) return "";
        return match;
      });

      // Remove all matching actions (after the first one) up to colon
      // result = result.replace(/([\w\s@]+?):/g, (match, p1, offset) => {
      //   const lowerP1 = p1.trim().toLowerCase();
      //   // Skip the first action
      //   if (match.startsWith(firstAction)) return match;
      //   // If this is an action key, remove it
      //   if (actionKeys.includes(`@${lowerP1}`)) return '';
      //   return match;
      // });
    }

    return result.trim();
  }
  function linkifyAndPreserveHTML(htmlString) {
    const splitRegex = /(https?:\/\/[^\s]+)/;
    const testRegex = /^https?:\/\//;
    const container = document.createElement("div");
    container.innerHTML = htmlString.replace(/\n/g, "<br />");

    function cleanUrl(url) {
      const trailingPunctuation = /[)\]},.:;!?'"]+$/;
      let cleanedUrl = url;
      let trailingChars = "";
      
      const match = url.match(trailingPunctuation);
      if (match) {
        trailingChars = match[0];
        cleanedUrl = url.slice(0, -trailingChars.length);
      }
      
      return { url: cleanedUrl, trailing: trailingChars };
    }

    function processNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue;
        const parts = text.split(splitRegex);

        if (parts.length === 1) return;

        const fragment = document.createDocumentFragment();

        parts.forEach((part) => {
          if (testRegex.test(part)) {
            const { url, trailing } = cleanUrl(part);
            const a = document.createElement("a");
            a.href = url;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.textContent = url;
            fragment.appendChild(a);
            if (trailing) {
              fragment.appendChild(document.createTextNode(trailing));
            }
          } else {
            fragment.appendChild(document.createTextNode(part));
          }
        });

        node.replaceWith(fragment);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName.toLowerCase() !== "a") {
          Array.from(node.childNodes).forEach(processNode);
        }
      }
    }

    Array.from(container.childNodes).forEach(processNode);

    return container.innerHTML;
  }

  useEffect(() => {
    setIsLoading(false);
    setIsUserQueyLoading(false);
    setIsSending(false);
     setIsStreaming(false); // <-- Add this line
  }, []);

  // Add this handler
  const handleQueueModalClose = () => {
    setShowQueueModal(false);
    // Clear queue popup file state
    setQueuePopupFile(null);
    setQueuePopupFilePreview(null);
    setQueueShowMentions(false);
  };

  // Update handleQueueRequestClick - now preserves current input
  const handleQueueRequestClick = () => {
    // If there's text in the main input, move it to the queue popup
    if (inputText.trim()) {
      setQueueMessage(inputText.trim());
      setInputText(""); // Clear main input after moving to popup
    } else {
      setQueueMessage(""); // Clear description if main is empty
    }
    setQueueTitle(""); // Clear title
    setIsTitleEdited(false); // Reset edit flag
    setQueuePopupFile(null);
    setQueuePopupFilePreview(null);
    setShowQueueModal(true);
  };
  
  // Handle queue popup file selection
  const handleQueuePopupFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setQueuePopupFile(file);
    
    // Create preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setQueuePopupFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setQueuePopupFilePreview(null);
    }
  };
  
  // Remove queue popup file
  const handleQueuePopupFileRemove = () => {
    setQueuePopupFile(null);
    setQueuePopupFilePreview(null);
    if (queuePopupFileInputRef.current) {
      queuePopupFileInputRef.current.value = "";
    }
  };
  
  // Handle queue message change with mentions support
  const handleQueueMessageChangeWithMentions = async (e) => {
    const value = e.target.value;
    setQueueMessage(value);
    
    // Check for @ mentions
    if (value.includes("@")) {
      const textarea = queueTextareaRef.current;
      const cursorPosition = textarea ? textarea.selectionStart : value.length;
      const textBeforeCursor = value.slice(0, cursorPosition);
      const atIndex = textBeforeCursor.lastIndexOf("@");
      
      if (atIndex !== -1) {
        const searchText = textBeforeCursor.slice(atIndex + 1).toLowerCase();
        const allMentions = [
          "@Task", "@Notes", "@Gmail", "@Calendar", "@Contacts",
          "@Notion", "@DeepResearch", "@WebSearch", "@Reminders",
          "@SecondBrain", "@Topics", "@ScrapeUrl", "@Lists", "@Ideas"
        ];
        
        const filteredMentions = allMentions.filter(m =>
          m.toLowerCase().includes("@" + searchText)
        );
        
        if (filteredMentions.length > 0) {
          setQueueMentions(filteredMentions);
          setQueueShowMentions(true);
          setQueueHighlightedIndex(0);
        } else {
          setQueueShowMentions(false);
        }
      }
    } else {
      setQueueShowMentions(false);
    }
    
    // Save draft
    if (userId) {
      await saveDraft(`queue_new_${userId}`, value);
    }
  };
  
  // Handle queue mention selection
  const handleQueueMentionSelect = (mention) => {
    let cleanText = queueMessage.replace(/@[^:]*:/g, "").trim();
    const textarea = queueTextareaRef.current;
    const value = queueMessage;
    const cursorPosition = textarea ? textarea.selectionStart : value.length;
    const atIndex = value.lastIndexOf("@", cursorPosition - 1);
    
    if (atIndex !== -1) {
      let endIndex = atIndex + 1;
      while (endIndex < value.length && /[\w]/.test(value[endIndex])) {
        endIndex++;
      }
      const before = value.slice(0, atIndex);
      const after = value.slice(endIndex);
      cleanText = (before + after).replace(/@[^:]*:/g, "").trim();
    }
    
    const newValue = cleanText ? `${mention}:${cleanText}` : `${mention}:`;
    setQueueMessage(newValue);
    setQueueShowMentions(false);
    
    setTimeout(() => {
      if (queueTextareaRef.current) {
        queueTextareaRef.current.focus();
        queueTextareaRef.current.setSelectionRange(newValue.length, newValue.length);
      }
    }, 0);
  };

  useEffect(() => {
    const fetchQueueDrafts = async () => {
      if (showQueueModal && userId) {
        const savedTitle = await getDraft(`queue_new_title_${userId}`);
        const savedMessage = await getDraft(`queue_new_${userId}`);
        if (savedTitle) setQueueTitle(savedTitle);
        if (savedMessage) setQueueMessage(savedMessage);
      }
    };
    fetchQueueDrafts();
  }, [showQueueModal, userId]);

  // Update onChange for description
  const handleQueueMessageChange = async (e) => {
    const value = e.target.value;
    setQueueMessage(value);
    // If user hasn't manually edited title, update title with first 3 words
    if (!isTitleEdited) {
      const firstThreeWords = value.split(" ").slice(0, 3).join(" ");
      setQueueTitle(firstThreeWords);
    }

    if (userId) {
      await saveDraft(`queue_new_${userId}`, value);
    }
  };

  // Update onChange for title
  const handleQueueTitleChange = async (e) => {
    setQueueTitle(e.target.value);
    setIsTitleEdited(true); // User manually edited title
    // Save draft if userId is available
    if (userId) {
      await saveDraft(`queue_new_title_${userId}`, e.target.value);
    }
  };

  const handleQueueDetailsChange = (e) => setQueueDetails(e.target.value);

  // new code
  const handleQueueSend = async () => {
    // TODO: Replace this with your actual send logic (API call, etc.)
    if (!queueMessage.trim()) {
      // alert("Please enter a message.");
      return;
    }

    // Save values before clearing state
    const messageToSend = queueMessage.trim();
    const detailsToSend = queueDetails.trim();
    // Auto-generate title from message (first 5 words) if no title provided
    const titleToSend = queueTitle.trim() || messageToSend.split(/\s+/).slice(0, 5).join(' ');

    // Close modal and clear state
    setShowQueueModal(false);
    setQueueTitle("");
    setQueueMessage("");
    setQueueDetails(""); // Clear details
    if (userId) {
      await deleteDraft(`queue_new_title_${userId}`);
      await deleteDraft(`queue_new_${userId}`);
    }
    // Pass details to storeQueueHistory
    // Use chatSessionId for connected queue, null for unconnected (standalone) queue
    const sessionIdForQueue = queuePopupType === 'connected' ? chatSessionId : null;
    const { dataQueue, chatQuery } = await storeQueueHistory(
      sessionIdForQueue,
      titleToSend,
      messageToSend,
      detailsToSend,
      userId
    );

    // Add the new queue request to sidebar list with queue_chat data
    if (dataQueue && dataQueue[0]) {
      const queueRequestWithChat = {
        ...dataQueue[0],
        queue_chat: chatQuery[0] || null
      };
      setQueueRequests((prev) => [...prev, queueRequestWithChat]);
      setQueueRefreshKey((prev) => prev + 1);
    }

    const payload = {
      user_id: userId,
      session_id: dataQueue[0].id.toString(),
      store_id: chatQuery[0].id.toString(),
      user_query: messageToSend,
      details: detailsToSend,
      mode: selectedMode,
      customContextLimits: fullContextEnabled && customContextLimits ? customContextLimits : undefined,
      fullContext: fullContextEnabled || undefined,
    };

    // For CONNECTED queue popup, add message to chat UI and store in chat history
    if (queuePopupType === 'connected' && dataQueue && dataQueue[0]) {
      const messageDate = moment
        .utc()
        .tz(userTimeZone ? userTimeZone : "America/Toronto")
        .format("MM-DD-YYYY hh:mm A");

      // Add message to chat UI
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: messageToSend,
          isUser: true,
          isHtml: true,
          default: false,
          time: messageDate,
          isTyping: true,
          isQueue: dataQueue[0].id,
        },
      ]);

      // Store in chat history
      await storeChatHistory(
        chatSessionId,
        messageToSend,
        "text",
        true,
        null,
        dataQueue[0].id,
        userId
      );
    }

    // Refresh token before API call
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      setApiToken(session.access_token);
    }

    // Ensure proper URL construction
    const baseUrl = (zoomeyApiUrl || '').replace(/\/$/, ''); // Remove trailing slash if any
    await fetch(`${baseUrl}/single_query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    }).then(async (response) => {
      if (response.status === 200) {
        setShowQueueModal(false);
      } else {
        // alert("Something went wrong with your server.");
      }
    });
  };
  const createApiSession = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const TIMEOUT_MS = 15000; // 15 second timeout

    // Helper function to fetch with timeout
    const fetchWithTimeout = async (url, options, timeout) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    };

    try {
      // Refresh token before API call
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.log("No auth session, redirecting to login");
        setSessionLoaded(true);
        return;
      }
      setApiToken(session.access_token);

      const response = await fetchWithTimeout(
        zoomeyApiUrl +
          `/apps/main_agent/users/${userId}/sessions/${chatSessionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            state: {
              key1: "value1",
              key2: 42,
            },
          }),
        },
        TIMEOUT_MS
      );

      if (response.ok || response.status === 200 || response.status === 400) {
        // 400 means session already exists - that's fine
        setSessionLoaded(true);
        localStorage.setItem("ApiSessionID", true);
        setApiSessionId(true);
        setTimeout(() => {
          scrollToBottom();
        }, 100);
        return;
      }

      // If we get here, response was not ok - try to parse error
      try {
        const data = await response.json();
        console.log("Session creation response:", response.status, data);
      } catch (e) {
        console.log("Session creation response:", response.status);
      }

      // Retry on failure
      if (retryCount < MAX_RETRIES) {
        console.log(`Session creation failed, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return createApiSession(retryCount + 1);
      }

      // Max retries reached - show session loaded anyway to not block UI
      console.log("Max retries reached, loading UI anyway");
      setSessionLoaded(true);
      localStorage.setItem("ApiSessionID", true);
      setApiSessionId(true);
    } catch (error) {
      console.log("Session creation error:", error.name, error.message);

      // Handle abort (timeout)
      if (error.name === 'AbortError') {
        console.log("Request timed out");
      }

      // Retry on error
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying session creation (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return createApiSession(retryCount + 1);
      }

      // Max retries reached
      console.log("Server may be down after retries");
      setSessionLoaded(true);
      setShowServerError(true);

      // server err
      // return(
      //   <Modal show={true} animation={false} centered>
      //     <Modal.Header closeButton>
      //       <Modal.Title>ADK Server</Modal.Title>
      //     </Modal.Header>
      //     <Modal.Body>
      //       <h3> Something went wrong with ADK server</h3>
      //     </Modal.Body>

      //     <Modal.Footer>
      //       <Button variant='secondary'>Close</Button>
      //     </Modal.Footer>
      //   </Modal>

      // )
    }
  };
  // const sendMessageToADK = async (message) => {

  // }
  const handleQueueToggle = async () => {
    const newQueueValue = !queue;
    setQueue(newQueueValue);

    // Clear uploaded file when enabling queue to prevent previously uploaded files from being sent with queue
    if (newQueueValue) {
      setSelectedFile(null);
      setPreviewFile(null);
    }

    if (chatSessionId) {
      await supabase
        .from("chat_session")
        .update({ is_queue: newQueueValue })
        .eq("id", chatSessionId)
        .eq("user_auth", userId);
      // If disabling queue, notify parent/sidebar to remove from sidebar
      // if (!newQueueValue && typeof onQueueSessionRemove === "function") {
      //   onQueueSessionRemove(chatSessionId);
      // }
    }
    // After sending message on queue, delete draft text
    if (chatSessionId && userId) {
      const key = `session_${chatSessionId}_${userId}`;
      await deleteDraft(key);
    }
  };

  // Connected Queue Toggle Handler
  const handleConnectedQueueToggle = () => {
    setQueueMode(prev => prev === 'connected' ? 'none' : 'connected');
    // Clear uploaded file when enabling queue mode
    if (queueMode !== 'connected') {
      setSelectedFile(null);
      setPreviewFile(null);
    }
  };

  // Unconnected (Standalone) Queue Toggle Handler
  const handleUnconnectedQueueToggle = () => {
    setQueueMode(prev => prev === 'unconnected' ? 'none' : 'unconnected');
    // Clear uploaded file when enabling queue mode
    if (queueMode !== 'unconnected') {
      setSelectedFile(null);
      setPreviewFile(null);
    }
  };

  // Connected Queue Popup Handler
  const handleConnectedQueuePopup = () => {
    // If there's text in the main input, move it to the queue popup
    if (inputText.trim()) {
      setQueueMessage(inputText.trim());
      setInputText("");
    } else {
      setQueueMessage("");
    }
    setQueueTitle("");
    setIsTitleEdited(false);
    setQueuePopupFile(null);
    setQueuePopupFilePreview(null);
    setQueuePopupType('connected');
    setShowQueueModal(true);
  };

  // Unconnected (Standalone) Queue Popup Handler
  const handleUnconnectedQueuePopup = () => {
    // If there's text in the main input, move it to the queue popup
    if (inputText.trim()) {
      setQueueMessage(inputText.trim());
      setInputText("");
    } else {
      setQueueMessage("");
    }
    setQueueTitle("");
    setIsTitleEdited(false);
    setQueuePopupFile(null);
    setQueuePopupFilePreview(null);
    setQueuePopupType('unconnected');
    setShowQueueModal(true);
  };

  // Touch handlers for mobile long-press on Connected Queue
  const handleConnectedTouchStart = () => {
    const timer = setTimeout(() => {
      handleConnectedQueuePopup();
    }, LONG_PRESS_DURATION);
    setLongPressTimer(timer);
  };

  const handleConnectedTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Touch handlers for mobile long-press on Unconnected Queue
  const handleUnconnectedTouchStart = () => {
    const timer = setTimeout(() => {
      handleUnconnectedQueuePopup();
    }, LONG_PRESS_DURATION);
    setLongPressTimer(timer);
  };

  const handleUnconnectedTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  useEffect(() => {
    const fetchQueueStatus = async () => {
      if (chatSessionId) {
        const { data, error } = await supabase
          .from("chat_session")
          .select("is_queue")
          .eq("id", chatSessionId)
          .eq("user_auth", userId)
          .single();
        if (data) setQueue(data.is_queue);
      }
    };
    fetchQueueStatus();
  }, [chatSessionId]);
  // ...existing code...
  useEffect(() => {
    if (chatSessionId != null && userId != null) {
      createApiSession();
    }
    // Note: ApiSessionId removed from deps to prevent infinite loop
    // createApiSession() sets ApiSessionId, which would retrigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionId, userId]);

  useEffect(() => {
    if (ShowMentions) {
      if (highlightedIndex == -1 || Mentions.length == 1) {
        setHighlightedIndex(0); // Set the first element as selected by default
      }

      const handleKeyDown = (e) => {
        // ESC key to close the mentions popup
        if (e.key === "Escape") {
          e.preventDefault();
          setShowMentions(false);
          setHighlightedIndex(-1);
          // Keep focus on textarea
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev < Mentions.length - 1 ? prev + 1 : 0; // Loop back to the first element
            if (dropdownRef.current) {
              // Access items container (second child after header)
              const itemsContainer = dropdownRef.current.children[1];
              if (itemsContainer && itemsContainer.children[nextIndex]) {
                itemsContainer.children[nextIndex].scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                });
              }
            }
            return nextIndex;
          });
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : Mentions.length - 1; // Loop back to the last element
            if (dropdownRef.current) {
              // Access items container (second child after header)
              const itemsContainer = dropdownRef.current.children[1];
              if (itemsContainer && itemsContainer.children[nextIndex]) {
                itemsContainer.children[nextIndex].scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                });
              }
            }
            return nextIndex;
          });
        }
        // Tab or Enter key to select the highlighted mention
        if ((e.key === "Tab" || (e.key === "Enter" && !e.shiftKey))) {
          e.preventDefault(); // Prevent default tab/enter behavior
          const chosenMention = Mentions[highlightedIndex];

          // Remove any existing @ mentions and get clean text
          let cleanText = inputText.replace(/@[^:]*:/g, "").trim();

          // Find the current @ search text to remove
          const textarea = textareaRef.current;
          const value = inputText;
          const selectionStart = textarea
            ? textarea.selectionStart
            : value.length;
          const atIndex = value.lastIndexOf("@", selectionStart - 1);

          if (atIndex !== -1) {
            let endIndex = atIndex + 1;
            while (endIndex < value.length && /[\w]/.test(value[endIndex])) {
              endIndex++;
            }
            const before = value.slice(0, atIndex);
            const after = value.slice(endIndex);
            cleanText = (before + after).replace(/@[^:]*:/g, "").trim();
          }

          // Set as chip; keep only the clean text in textarea
          setSelectedMention({ label: chosenMention, action: actionLists[chosenMention] });
          setInputText(cleanText);

          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(cleanText.length, cleanText.length);
              textareaRef.current.focus();
            }
          }, 0);

          setShowMentions(false);
        }
      };

      // Attach keydown listener when dropdown is open
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [ShowMentions, highlightedIndex, Mentions, actionLists, setShowMentions]);

  // useEffect(() => {
  //   if (ShowMentions && mentionSelectRef.current) {
  //     mentionSelectRef.current.focus();
  //      ("hello ref")
  //   }
  // }, [ShowMentions]);
  const handleShow = (image) => {
    const imgTagMatch = image.match(/<img[^>]*src=["']([^"']+)["']/);

    if (imgTagMatch) {
      // If an <img> tag is found, imgTagMatch[1] will contain the src value
      const imgSrc = imgTagMatch[1];
      setSelectedImage(imgSrc);
      setShow(true);
    } else {
      setSelectedImage(image);
      setShow(true);
    }
  };
  const handleShowPdf = (url) => {
    if (url.startsWith("data:application/pdf;base64,")) {
      // Handle base64 data
      const byteCharacters = atob(url.split(",")[1]);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      setPdfUrl(blobUrl);
    } else {
      // Handle direct URL
      setPdfUrl(url);
    }

    setShowPdf(true);
  };

  // useEffect(() => {
  // console.log("changed scroll");

  // }, [chatContainerRef])

  //   useEffect(() => {
  // if (summary) {
  //   setMessages(prevMessages => [
  //     ...prevMessages,
  //     {
  //       text: summary.text,
  //       isUser: summary.isUser,
  //       isHtml: true,
  //       default: true
  //     }
  //   ])
  // }
  // }, [summary])
  // get chat history
  useEffect(() => {
    let getChatHistoyFunction = async () => {
      if (chatSessionId) {
        const pageSize = 50; // Number of items per chunk
        const container = chatContainerRef.current;
        // container.style.overflow = 'hidden'; // Disable scrolling
        // container.scrollTop = scrollPosition; // Maintain the current scroll position
        var chatHistory = await getChatHistoy(chatSessionId, page, pageSize);
        // if (page > 1 && chatHistory.length > 0) {
        //   chatHistory = [chatHistory[chatHistory.length - 1]]; // Load only the last message from the page
        // }
        // console.log("chatHistory", chatHistory);
        let timeToUse = userTimeZone ? userTimeZone : "America/Toronto";
        if (chatHistory.length < pageSize || chatHistory.length === 0) {
          setHasMore(false);
        }
        var finalArray = [];

        chatHistory.forEach((element, index) => {
          // element.content = formatMessage(element.content)
          let messageDate = moment
            .utc(element.created_at)
            .tz(timeToUse)
            .format("MM-DD-YYYY hh:mm A");

          if (element.type === "text") {
            // Extract generated images from message content for AI responses
            const generatedImages = !element.is_query ? extractGeneratedImages(element.content) : [];
            
            setMessages((prevMessages) => [
              {
                text: element.content,
                isUser: element.is_query,
                isHtml: true,
                default: false,
                time: messageDate,
                isQueue: element.queue_id,
                // Add generated images if found
                ...(generatedImages.length > 0 && { generatedImages }),
              },
              ...prevMessages,
            ]);
          } else if (element.type === "image") {
            // console.log("Image path from DB:", element.path);
            setMessages((prevMessages) => [
              {
                text: element.content,
                isUser: element.is_query,
                fileName: "history",
                preview: element.path,
                fileType: element.type,
                default: false,
                time: messageDate,
              },
              ...prevMessages,
            ]);
          } else if (element.type === "pdf" || element.type === "document") {
            setMessages((prevMessages) => [
              {
                text: element.content,
                isUser: element.is_query,
                fileName: element.path ? element.path.split("/").pop() : null,
                preview: element.path,
                fileType: element.type,
                default: true,
                time: messageDate,
              },
              ...prevMessages,
            ]);
          } else if (element.type === "audio") {
            const audioMessage = {
              audioUrl: element.path,
              isUser: element.is_query,
              default: false,
              time: messageDate,
            };

            if (element.content) {
              setMessages((prevMessages) => [
                audioMessage,
                {
                  text: element.content,
                  isUser: element.is_query,
                  isHtml: !element.is_query,
                  default: true,
                  time: messageDate,
                },
                ...prevMessages,
              ]);
            } else {
              setMessages((prevMessages) => [
                audioMessage,
                ...prevMessages,
              ]);
            }
          }
        });
        setRendered(true);
        if (container) {
          if (previousScrollPosition) {
            container.scrollTop = previousScrollPosition;
          } else {
            container.scrollTop = previousScrollPositionNew;
          }
        }
        // ADD THIS:
        setIsLoading(false);
        setIsUserQueyLoading(false);
        setIsSending(false);

        setIsMessagesLoading(false);
      }
    };
    if (chatSessionId && userTimeZone) {
      getChatHistoyFunction();
      FirstLoad = false;
    }
    // Cookies.remove('is_call');
  }, [chatSessionId, userTimeZone, page]);

  // Handle fresh user case - when userId is available but no chatSessionId yet
  useEffect(() => {
    console.log("Fresh user check:", { userId, chatSessionId, userTimeZone });
    if (userId && !chatSessionId && userTimeZone) {
      // console.log("Fresh user detected - stopping loading states");
      // Fresh user with no sessions - stop loading and show welcome screen
      setIsMessagesLoading(false);
      setIsLoading(false);
      setIsUserQueyLoading(false);
      setIsSending(false);
    }
  }, [userId, chatSessionId, userTimeZone]);

  // Function to handle infinite scroll and pagination
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!isUserManuallyScroll) {
      if (chatContainerRef.current && (isUserQueyLoading || isLoading)) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }
  }, [isUserQueyLoading, isLoading]);

  // useEffect(() => {
  //   const autoScroll = () => {
  //     if (autoScrolling) {
  //       intervalIdRef.current = setInterval(() => {
  //         if (chatContainerRef.current) {
  //           chatContainerRef.current.scrollTop =
  //             chatContainerRef.current.scrollHeight;
  //         }
  //       }, 1000);
  //     } else {
  //       if (intervalIdRef.current) {
  //         clearInterval(intervalIdRef.current);
  //         intervalIdRef.current = null;
  //       }
  //     }
  //   };

  //   autoScroll();

  //   // Cleanup function to clear interval when component unmounts
  //   return () => {
  //     if (intervalIdRef.current) {
  //       clearInterval(intervalIdRef.current);
  //       intervalIdRef.current = null;
  //     }
  //   };
  // }, [autoScrolling]);

  useEffect(() => {
    let interval;
    const pollForNewMessages = async () => {
      if (!chatSessionId) return;

      try {
        // Get latest message to see if there are updates
        let { data: chat, error } = await supabase
          .from("chat")
          .select("*")
          .eq("chat_session_id", chatSessionId)
          .eq("user_id", userId)
          .eq("is_delete", false)
          .order("created_at", { ascending: false })
          .limit(1);

        if (error) {
          console.error("Error polling for new messages:", error);
          return;
        }

        // If there's a new message and it's not already in our messages
        if (chat && chat.length > 0) {
          if (chat[0].content === "" && chat[0].type === "text") {
            setResponseId(chat[0].id);
            setIsSending(true);
            setIsLoading(true);
            setIsUserQueyLoading(false);
          } else {
            setResponseId(null);
            setIsSending(false);
            setIsLoading(false);
            // ADD THIS:
            setIsUserQueyLoading(false);
          }
        } else {
          setResponseId(null);
          setIsSending(false);
          setIsLoading(false);
          // ADD THIS:
          setIsUserQueyLoading(false);
        }
      } catch (err) {
        console.error("Error in polling function:", err);
      }
    };
    pollForNewMessages();
  }, [chatSessionId]);

  const handleTypingDone = () => {
    setIsSending(false);
    setAutoScrolling(false);
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      if (ChatEventChannel && typeof ChatEventChannel === "object") {
        if (
          ChatEventChannel.is_delete === false &&
          ChatEventChannel.query_status === "start"
        ) {
          // if (container) {

          const isAtBottom =
            container.scrollHeight - container.scrollTop <
            container.clientHeight + 10;
          if (isAtBottom) {
            // setAutoScrolling(true)
            autoScrollingRef.current = true;
            setIsLoading(true);
          } else {
            // setAutoScrolling(false);
            autoScrollingRef.current = false;
            setIsLoading(false);
          }
          // }
          if (
            ChatEventChannel.content === "" &&
            ChatEventChannel.type === "text"
          ) {
            setResponseId(ChatEventChannel.id);
            setIsSending(true);
            // setIsLoading(true)
            setIsUserQueyLoading(false);
          } else {
            if (!ChatEventChannel.is_query) {
              setResponseId(null);
              setIsSending(false);
              // setIsLoading(false)
              // ADD THIS:
              setIsUserQueyLoading(false);
            } else {
              setIsUserQueyLoading(false);
            }
          }
          let timeToUse = userTimeZone ? userTimeZone : "America/Toronto";
          let messageDate = moment
            .utc(ChatEventChannel.created_at)
            .tz(timeToUse)
            .format("MM-DD-YYYY hh:mm A");
          if (ChatEventChannel.type === "text") {
            if (ChatEventChannel.content !== "") {
              setMessages((prevMessages) => [
                ...prevMessages,
                {
                  text: ChatEventChannel.content,
                  isUser: ChatEventChannel.is_query,
                  isHtml: true,
                  default: false,
                  time: messageDate,
                  isTyping: true,
                },
              ]);
            }
          } else if (ChatEventChannel.type === "image") {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                text: ChatEventChannel.content,
                isUser: ChatEventChannel.is_query,
                fileName: "history",
                preview: ChatEventChannel.path,
                fileType: ChatEventChannel.type,
                default: false,
                time: messageDate,
              },
            ]);
          } else if (ChatEventChannel.type === "pdf" || ChatEventChannel.type === "document") {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                text: ChatEventChannel.content,
                isUser: ChatEventChannel.is_query,
                fileName: ChatEventChannel.path
                  ? ChatEventChannel.path.split("/").pop()
                  : null,
                preview: ChatEventChannel.path,
                fileType: ChatEventChannel.type,
                default: true,
                time: messageDate,
              },
            ]);
          } else if (ChatEventChannel.type === "audio") {
            if (ChatEventChannel.content) {
              setMessages((prev) => [
                ...prev,
                {
                  audioUrl: ChatEventChannel.path,
                  isUser: false,
                  default: false,
                  time: messageDate,
                },
              ]);

              setMessages((prevMessages) => [
                ...prevMessages,
                {
                  text: ChatEventChannel.content,
                  isUser: ChatEventChannel.is_query,
                  isHtml: !ChatEventChannel.is_query,
                  default: true,
                  time: messageDate,
                },
              ]);
            } else {
              setMessages((prev) => [
                ...prev,
                {
                  audioUrl: ChatEventChannel.path,
                  isUser: true,
                  default: false,
                  time: messageDate,
                },
              ]);
            }
          }
          FirstLoad = false;
          setResponseText(false);
        }
      }
    }
  }, [ChatEventChannel]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;

    const currentScrollTop = container.scrollTop;

    // Skip if we're programmatically adjusting scroll
    if (isAdjustingScrollRef.current) {
      previousScrollTopRef.current = currentScrollTop;
      return;
    }

    // Only trigger if user manually scrolled from non-zero position to top
    // This prevents auto-triggering when scroll position is adjusted programmatically
    const userScrolledToTop =
      currentScrollTop <= 5 && previousScrollTopRef.current > 10;

    // Auto-load when user manually scrolls to the very top (one chunk at a time)
    if (userScrolledToTop && hasMore && !loadingMore && !isLoadingRef.current) {
      // Set ref immediately to block further triggers
      isLoadingRef.current = true;
      setLoadingMore(true);

      // Save the scroll height before loading new messages
      const scrollHeightBefore = container.scrollHeight;
      const scrollTopBefore = container.scrollTop;

      // Increment page to load older messages
      setPage((prevPage) => prevPage + 1);

      // Adjust scroll position immediately when messages load (after DOM update)
      // Use multiple checks to ensure DOM is fully updated
      const adjustScrollPosition = () => {
        setTimeout(() => {
          if (container) {
            // Set flag to prevent triggering during programmatic scroll
            isAdjustingScrollRef.current = true;

            // Calculate the difference in scroll height (new content height)
            const scrollHeightAfter = container.scrollHeight;
            const heightDifference = scrollHeightAfter - scrollHeightBefore;

            // Set scroll position to keep user at exact same visual location IMMEDIATELY
            // This calculation ensures the same message stays in view
            const newScrollPosition = scrollTopBefore + heightDifference;
            container.scrollTop = newScrollPosition;

            // Use requestAnimationFrame for smooth adjustment
            requestAnimationFrame(() => {
              container.scrollTop = newScrollPosition;

              // Update previous scroll position
              previousScrollTopRef.current = container.scrollTop;

              // Reset adjusting flag after a brief delay
              setTimeout(() => {
                isAdjustingScrollRef.current = false;
              }, 500);
            });
          }
        }, 500); // Wait longer for DOM to fully update with new messages
      };

      adjustScrollPosition();

      // Keep loader visible for minimum 1 second (independent of scroll)
      setTimeout(() => {
        setLoadingMore(false);
        // Reset ref after loading completes
        isLoadingRef.current = false;
      }, 1000); // Minimum 1 second loading time
    }

    // Update previous scroll position
    previousScrollTopRef.current = currentScrollTop;

    // Check if user has scrolled enough to show the up button
    setShowScrollUp(currentScrollTop > 0);

    const isAtBottom =
      container.scrollHeight - container.scrollTop <
      container.clientHeight + 10;

    if (!isAtBottom) {
      // User scrolled up. If a response is currently being streamed/sent,
      // treat this as a manual scroll and stop auto-scrolling so incoming
      // streamed content doesn't yank the view to bottom.
      if (isStreaming || isSending) {
        if (autoScrollingRef) autoScrollingRef.current = false;
        setAutoScrolling(false);
        setIsUserManuallyScroll(true);
      } else {
        setAutoScrolling(false);
      }
      setShowScrollBottomButton(true);
    } else {
      // User reached bottom — re-enable auto-scrolling
      if (autoScrollingRef) autoScrollingRef.current = true;
      setAutoScrolling(true);
      setIsUserManuallyScroll(false);
      setShowScrollBottomButton(false);
    }
  };

  useEffect(() => {
    // Only scroll to the bottom if the user hasn't manually scrolled
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      const currentScrollPosition = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if the user is already at the bottom or not
      if (isLoading && currentScrollPosition + clientHeight === scrollHeight) {
        // If the user is already at the bottom, scroll to the bottom
        container.scrollTop = scrollHeight;
      } else {
        // // Otherwise, maintain the current scroll position
        // container.scrollTop = currentScrollPosition;
      }
    }
  }, [isLoading]);

  useEffect(() => {
    if (FirstLoad && ResponseText) {
      const container = chatContainerRef.current;
      if (page === 1 && container) {
        // Initial load — scroll to bottom
        if (autoScrolling) {
          container.scrollTop = container.scrollHeight;
        }

        prevPageRef.current = page;
      } else if (page > prevPageRef.current) {
        // We loaded older messages, now restore scroll

        const topMessageElement = topMessageRef.current;

        if (container && topMessageElement) {
          const previousTopOffset =
            topMessageElement.getBoundingClientRect().top;

          // Wait for DOM update
          setTimeout(() => {
            const newTopOffset = topMessageElement.getBoundingClientRect().top;
            const scrollDifference = newTopOffset - previousTopOffset;
            container.scrollTop += scrollDifference + 10;
          }, 0);
        }

        prevPageRef.current = page;
      }
    } else {
      setIsLoading(false);
    }
  }, [messages]);

  useEffect(() => {
    let getTimeZoneOfUser = async () => {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) {
        console.error("Error fetching session:", sessionError);
        return;
      }
      if (!sessionData?.session?.user) {
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("time_zone")
        .eq("id", sessionData.session.user.id)
        .single();
      if (profile) {
        setUserTimeZone(profile.time_zone);
      }
    };
    getTimeZoneOfUser();
  }, []);
  useEffect(() => {
    const initializeChatSession = async () => {
      initializeChatSessionRun = false;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        // throw new Error("No session found. Please log in.");
        navigate("/login");
        return;
      }
      setApiToken(session.access_token);

      const session_id = await checkCurrentUserSession();
      if (session_id && session_id.id !== "" && session_id.id !== undefined) {
        await supabase
          .from("chat_session")
          .update({ is_call: false })
          .eq("id", session_id.id);
        setChatSessionId(session_id.id);
        setChatName(session_id.name === null ? "Main" : session_id.name);
        if (session_id.type === "chatbot") {
          setChatSessionType(session_id.type);
        }
        await getSessionType(session_id.id).then((data) => {
          if (data) {
            setChatSessionType(data.type);
            if (data.parent_session === null) {
              setChatSessionParentId(session_id.id);
            } else {
              setChatSessionParentId(data.parent_session);
            }
          }
        });
        onSetLoading(true);
      }
    };
    if (initializeChatSessionRun) {
      initializeChatSession();
    }
  }, []);

  // Add this state for temporary preview
  const [previewFile, setPreviewFile] = useState(null);

  // Update file selection handler
  // for singel file
  const handleFileSelect = (e) => {
    // Show warning if user tries to attach file while query is in progress
    if (isSending || isStreaming) {
      setShowQueryInProgressWarning(true);
      setTimeout(() => {
        setShowQueryInProgressWarning(false);
      }, 1500);
      e.target.value = "";
      return;
    }

    const file = e.target.files[0];
    if (file) {
      // Check file type
      const validTypes = [
        // Images
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/bmp",
        "image/tiff",
        "image/webp",
        // Documents
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
        "application/vnd.ms-excel", // xls
        "text/csv",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
        "application/msword", // doc
        "text/plain", // txt
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);

        // Auto-switch from Simple to Smart when file is selected (Simple lacks analyze_attachment)
        if (selectedMode === 'simple') {
          setSelectedMode('smart');
        }

        // Create preview if it's an image
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setPreviewFile({
              preview: reader.result,
              fileName: file.name,
              type: "image",
            });
          };
          reader.readAsDataURL(file);
        } else {
          // For documents (PDF, Excel, Word, etc.), use specific file type
          setPreviewFile({
            fileName: file.name,
            type: getFileTypeFromName(file.name),
          });
        }
      } else {
        alert("Please select valid file types: Images (JPG, PNG, GIF, BMP, TIFF, WEBP) or Documents (PDF, XLSX, XLS, CSV, DOCX, DOC, TXT)");
      }
    }
    // Reset file input value so the same file can be selected again
    e.target.value = "";
  };

  // for multple files
  // const handleFileSelect = (e) => {
  //   const uploadedFiles = Array.from(e.target.files); // Convert FileList to an array
  //   const validTypes = [
  //     'image/jpeg',
  //     'image/png',
  //     'image/gif',
  //     'application/pdf',
  //   ];

  //   uploadedFiles.forEach((uploadedFile) => {
  //     if (validTypes.includes(uploadedFile.type)) {
  //       // Generate a unique file name using timestamp
  //       const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
  //       const fileName = `${uploadedFile.name
  //         .split('.')
  //         .slice(0, -1)
  //         .join('.')}_${timestamp}.${uploadedFile.name.split('.').pop()}`;

  //       const file = new File([uploadedFile], fileName, { type: uploadedFile.type });

  //       // Add file to selected files state
  //       setSelectedFiles((prevFiles) => [...prevFiles, file]);

  //       // Create preview if it's an image
  //       if (file.type.startsWith('image/')) {
  //         const reader = new FileReader();
  //         reader.onloadend = () => {
  //           setFilePreviews((prevPreviews) => [
  //             ...prevPreviews,
  //             {
  //               preview: reader.result,
  //               fileName: uploadedFile.name,
  //               type: 'image',
  //             },
  //           ]);
  //         };
  //         reader.readAsDataURL(file);
  //       } else if (file.type === 'application/pdf') {
  //         // For PDF, just store the file name
  //         setFilePreviews((prevPreviews) => [
  //           ...prevPreviews,
  //           {
  //             fileName: uploadedFile.name,
  //             type: 'pdf',
  //             preview: null,
  //           },
  //         ]);
  //       }
  //     } else {
  //       alert('Please select only image or PDF files');
  //     }
  //   });

  //   // Reset the file input value to allow re-selection of the same file
  //   e.target.value = '';
  // };

  // Remove pending file for singel
  const handleRemovePendingFile = () => {
    setSelectedFile(null);
    setPreviewFile(null);
  };
  // deepgram audio
  const playAudio = async (index, textToPlay, action) => {
    // const url = 'https://api.deepgram.com/v1/speak?model=aura-luna-en';
    // const apiKey = process.env.REACT_APP_DEEPGRAM_API_KEY;
    const MAX_CHARS = 1000; // Deepgram's character limit

    // Handle stop action
    if (action === "stop") {
      if (window.currentAudio) {
        window.currentAudio.pause();
        window.currentAudio.currentTime = 0;

        // If there's a queue of audio chunks, clear it
        if (window.audioQueue) {
          window.audioQueue = [];
        }

        setPlayingId(null);
        setPlaying(false);
      }
      return;
    }

    // Don't start if already playing
    if (isPlaying) {
      return;
    }

    setPlayingId(index);
    setPlaying(true);

    try {
      // Split text into chunks if needed
      const textChunks = chunkText(textToPlay, MAX_CHARS);

      // Create a queue to track audio chunks
      window.audioQueue = [...textChunks];

      // Process the first chunk immediately
      await processNextChunk();
    } catch (error) {
      console.error("Error:", error);
      setPlayingId(null);
      setPlaying(false);
    }
  };

  // Helper function to chunk text intelligently
  const chunkText = (text, maxLength) => {
    // If text is already under the limit, return it as a single chunk
    if (text.length <= maxLength) {
      return [text];
    }

    const chunks = [];

    // Try to split by sentences for more natural breaks
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [];

    if (sentences.length === 0) {
      // If no sentence breaks, fall back to word splitting
      return splitByWords(text, maxLength);
    }

    let currentChunk = "";

    for (const sentence of sentences) {
      // If this sentence alone exceeds the limit, we need to split it
      if (sentence.length > maxLength) {
        // First push any accumulated chunk
        if (currentChunk) {
          chunks.push(currentChunk.trim());
          currentChunk = "";
        }

        // Split this long sentence by words
        const sentenceChunks = splitByWords(sentence, maxLength);
        chunks.push(...sentenceChunks);
        continue;
      }

      // Check if adding this sentence would exceed the limit
      if ((currentChunk + sentence).length <= maxLength) {
        currentChunk += sentence;
      } else {
        // Push current chunk and start a new one
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      }
    }

    // Add any remaining text
    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  };

  // Split text by words when sentence splitting isn't possible
  const splitByWords = (text, maxLength) => {
    const chunks = [];
    const words = text.split(/\s+/);
    let currentChunk = "";

    for (const word of words) {
      if ((currentChunk + " " + word).length <= maxLength) {
        currentChunk += (currentChunk ? " " : "") + word;
      } else {
        if (currentChunk) {
          chunks.push(currentChunk);
        }
        // Handle the case where a single word is longer than maxLength
        if (word.length > maxLength) {
          // Split the word into parts
          for (let i = 0; i < word.length; i += maxLength) {
            chunks.push(word.substr(i, maxLength));
          }
          currentChunk = "";
        } else {
          currentChunk = word;
        }
      }
    }

    if (currentChunk) {
      chunks.push(currentChunk);
    }

    return chunks;
  };

  // Process the next chunk in the queue
  const processNextChunk = async () => {
    if (!window.audioQueue || window.audioQueue.length === 0) {
      // No more chunks to process
      setPlayingId(null);
      setPlaying(false);
      return;
    }

    const nextChunk = window.audioQueue.shift();

    const url = "https://api.deepgram.com/v1/speak?model=aura-luna-en";
    const apiKey = process.env.REACT_APP_DEEPGRAM_API_KEY;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Token ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: nextChunk }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.statusText}`);
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);

      window.currentAudio = audio;

      // When this chunk ends, play the next one
      audio.onended = () => {
        // Free memory
        URL.revokeObjectURL(audioUrl);

        if (window.audioQueue.length > 0) {
          // Play next chunk
          processNextChunk();
        } else {
          // All done
          setPlayingId(null);
          setPlaying(false);
        }
      };

      // Play the audio
      await audio.play();
    } catch (error) {
      console.error("Error processing audio chunk:", error);
      setPlayingId(null);
      setPlaying(false);
    }
  };

  const audioToText = async (audioInput) => {
    try {
      // Get fresh session token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("No valid session found. Please log in again.");
      }

      const formData = new FormData();

      // Append the audio file
      formData.append("file", audioInput, "recording.mp3");

      const response = await fetch(
        zoomeyApiUrl + "/speech-to-text",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            // Don't include Content-Type - browser sets it automatically for FormData
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Upload failed: ${response.statusText} - ${
            errorData.error?.message || ""
          }`
        );
      }

      const data = await response.json();
      return data.text;
    } catch (error) {
      console.error("Error transcribing audio:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (previewFile === null) {
      setIsUserQueyLoading(false);
    }
  }, [previewFile]);

  // A2UI Action Handler - handles user interactions with A2UI components
  const handleA2UIAction = async (action) => {
    console.log("A2UI Action received:", action);
    
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session?.data?.session?.access_token;
      
      if (!accessToken) {
        console.error("No access token for A2UI action");
        return;
      }

      // Send action to backend
      const response = await fetch(`${zoomeyApiUrl}/run_sse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          appName: "main_agent",
          userId: userId,
          sessionId: ApiSessionId || "main_session",
          newMessage: {
            role: "user",
            parts: [{ text: `[A2UI_ACTION] ${action.name}: ${JSON.stringify(action.context)}` }]
          },
          streaming: true,
          userAction: action,
          ui_context: uiContext, // Pass uiContext
        }),
      });

      if (!response.ok) {
        console.error("A2UI action failed:", response.status);
        return;
      }

      // Handle streaming response similar to regular messages
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = "";
      let responseContent = "";
      let a2uiMessages = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        accumulatedText += chunk;
        
        // Parse SSE data lines
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'content' && data.text) {
                responseContent += data.text;
              } else if (data.type === 'a2ui' && data.content) {
                // Collect A2UI messages for processing
                a2uiMessages.push(data.content);
              }
            } catch (e) {
              // Ignore parse errors for partial chunks
            }
          }
        }
      }

      // Process the response - update the current message
      if (responseContent) {
        console.log("A2UI action response content:", responseContent);
        
        // Update the last message in the chat to show the response
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          const lastBotIndex = newMessages.length - 1;
          if (lastBotIndex >= 0 && !newMessages[lastBotIndex].isUser) {
            // Clear A2UI content and update with response
            newMessages[lastBotIndex] = {
              ...newMessages[lastBotIndex],
              text: responseContent,
              isHtml: true,
              a2uiContent: null,
              isA2UI: false
            };
          }
          return newMessages;
        });
      }

      // Process A2UI messages (like deleteSurface)
      for (const a2uiMsg of a2uiMessages) {
        try {
          const parsed = typeof a2uiMsg === 'string' ? JSON.parse(a2uiMsg) : a2uiMsg;
          if (parsed.deleteSurface) {
            console.log("Deleting A2UI surface:", parsed.deleteSurface.surfaceId);
            // The A2UIRenderer will automatically handle this through the message update above
            // which clears a2uiContent
          }
        } catch (e) {
          console.error("Error parsing A2UI message:", e);
        }
      }

      console.log("A2UI action response:", accumulatedText);
    } catch (error) {
      console.error("Error handling A2UI action:", error);
    }
  };

  // Update handleSendMessage
  // setChatSessionType
  const handleSendMessage = async () => {
    let supabaseUploadFileUrl = null;
    // Handle queue mode routing (both connected and unconnected)
    if (queueMode !== 'none') {
      // Use chatSessionId for connected queue, null for unconnected (standalone) queue
      const sessionIdForQueue = queueMode === 'connected' ? chatSessionId : null;
      const queueTitleForStore = queueMode === 'connected'
        ? (chatSessionType == "main" ? "Main Topic" : chatName)
        : inputText.trim().split(/\s+/).slice(0, 5).join(' '); // First 5 words for standalone

      const { dataQueue, chatQuery } = await storeQueueHistory(
        sessionIdForQueue,
        queueTitleForStore,
        inputText.trim(),
        null,
        userId
      );

      // Add the new queue request to sidebar list with queue_chat data
      if (dataQueue && dataQueue[0]) {
        const queueRequestWithChat = {
          ...dataQueue[0],
          queue_chat: chatQuery[0] || null
        };
        setQueueRequests((prev) => [...prev, queueRequestWithChat]);
        setQueueRefreshKey((prev) => prev + 1);
      }

      var UserQuery = selectedMention
        ? `${selectedMention.label}: ${inputText.trim()}`
        : inputText.trim();
      UserQuery = formatMentionSpacing(UserQuery);
      setSelectedMention(null);

      const payload = {
        user_id: userId,
        session_id: dataQueue[0].id.toString(),
        store_id: chatQuery[0].id.toString(),
        user_query: inputText.trim(),
        mode: selectedMode,
        customContextLimits: fullContextEnabled && customContextLimits ? customContextLimits : undefined,
        fullContext: fullContextEnabled || undefined,
      };

      // Store file info before clearing
      let queueFileInfo = null;

      // File upload logic
      if (selectedFile) {
        // Store file info before clearing
        queueFileInfo = {
          name: selectedFile.name,
          type: selectedFile.type,
          isImage: selectedFile.type.startsWith("image/"),
        };

        const fileUrl = await uploadFileAndGetUrl(
          dataQueue[0].id,
          userId,
          selectedFile
        );
        const { data, error } = await supabase
          .from("queue_chat")
          .update({ path: fileUrl })
          .eq("id", chatQuery[0].id)
          .select("*")
          .single();

        supabaseUploadFileUrl = await uploadFileAndGetUrl(
          chatSessionId,
          userId,
          selectedFile
        );

        // Add Attachment_URL to user_query same as normal query
        if (supabaseUploadFileUrl) {
          payload.user_query = inputText.trim() + `\n Attachment_URL: [${supabaseUploadFileUrl}]`;
        }

        setPreviewFile(null);
        setSelectedFile(null);
      }

      // Only add to chat session UI/history for CONNECTED queue, not standalone
      if (queueMode === 'connected') {
        let messageDate = moment
          .utc()
          .tz(userTimeZone ? userTimeZone : "America/Toronto")
          .format("MM-DD-YYYY hh:mm A");

        // Show message in UI with file info if file was uploaded
        if (queueFileInfo && supabaseUploadFileUrl) {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: inputText.trim(),
              isUser: true,
              isHtml: true,
              fileName: queueFileInfo.name,
              preview: supabaseUploadFileUrl,
              fileType: queueFileInfo.isImage ? "image" : getFileTypeFromName(queueFileInfo.name),
              default: false,
              time: messageDate,
              isTyping: true,
              isQueue: dataQueue[0].id,
            },
          ]);
        } else {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: inputText.trim(),
              isUser: true,
              isHtml: true,
              default: false,
              time: messageDate,
              isTyping: true,
              isQueue: dataQueue[0].id,
            },
          ]);
        }

        // Store chat history with file info if file was uploaded
        if (queueFileInfo && supabaseUploadFileUrl) {
          const fileType = queueFileInfo.isImage ? "image" : getFileTypeFromName(queueFileInfo.name);
          await storeChatHistory(
            chatSessionId,
            inputText.trim(),
            fileType,
            true,
            supabaseUploadFileUrl,
            dataQueue[0].id,
            userId
          );
        } else {
          await storeChatHistory(
            chatSessionId,
            inputText.trim(),
            "text",
            true,
            null,
            dataQueue[0].id,
            userId
          );
        }
      }

      setInputText("");

      // Delete session draft so it doesn't leak to other devices
      if (chatSessionId && userId) {
        const key = `session_${chatSessionId}_${userId}`;
        await deleteDraft(key);
      }

      // Auto-deactivate queue mode after sending
      setQueueMode('none');

      // Ensure proper URL construction
      const baseUrl = (zoomeyApiUrl || '').replace(/\/$/, ''); // Remove trailing slash if any
      await fetch(`${baseUrl}/single_query`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(payload),
      }).then(async (response) => {
        if (response.status === 200) {
          // setShowQueueModal(false)
        } else {
          // alert("Something went wrong with your server.");
        }
      });
      return "";
    } // For stopping the API call

    // After sending message, delete draft
    // need to change
    if (chatSessionId && userId) {
      const key = `session_${chatSessionId}_${userId}`;
      await deleteDraft(key);
    }

    if (isSending || isStreaming) {
      // If user presses send while a response is coming, ensure we reset streaming state
      // and enable auto-scrolling so new query and subsequent response will scroll.
      autoScrollingRef.current = true;
      setIsSending(false);
      setIsLoading(false);
      setIsStreaming(false); // Also set streaming to false

      // CANCEL THE ACTUAL API REQUEST
      if (controllerRef.current) {
        controllerRef.current.abort(); // This will cancel the fetch request
        controllerRef.current = null; // Clear the reference
      }

      // Stopped request message to display and store
      const stoppedMessage = '<span style="color: #888; font-style: italic;">⏹️ You stopped this request</span>';

      // Get the current streaming content from ref (this is updated during streaming)
      const currentStreamingContent = currentStreamingContentRef.current || "";

      // Check if we have meaningful content
      const hasRealContent =
        currentStreamingContent &&
        !currentStreamingContent.includes("Thinking") &&
        !currentStreamingContent.includes("loading-dots") &&
        currentStreamingContent.trim() !== "" &&
        currentStreamingContent.trim() !== "<p> </p>";

      // Determine what content to store in database
      const contentToStore = hasRealContent
        ? currentStreamingContent + '<br><br>' + stoppedMessage
        : stoppedMessage;

      // HANDLE THE RESPONSE MESSAGE IN UI - Always show stopped message
      if (messageIndexRef.current !== null) {
        console.log(
          "Stop button: Handling stopped message at index:",
          messageIndexRef.current
        );
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];

          // Check if the index is valid and the message exists
          if (
            messageIndexRef.current < updatedMessages.length &&
            updatedMessages[messageIndexRef.current] &&
            !updatedMessages[messageIndexRef.current].isUser
          ) {
            const currentMessage = updatedMessages[messageIndexRef.current];

            if (hasRealContent) {
              // ADD STOPPED MESSAGE TO PARTIAL RESPONSE
              updatedMessages[messageIndexRef.current] = {
                ...currentMessage,
                text: contentToStore,
                isTyping: false,
              };
              console.log(
                "Stop button: Added stop message to partial response"
              );
            } else {
              // SHOW "You stopped this request" MESSAGE INSTEAD OF REMOVING
              updatedMessages[messageIndexRef.current] = {
                ...currentMessage,
                text: stoppedMessage,
                isTyping: false,
              };
              console.log("Stop button: Replaced thinking with stopped message");
            }
          } else {
            // Fallback: Handle the last bot message
            console.log("Stop button: Index invalid, trying fallback");
            for (let i = updatedMessages.length - 1; i >= 0; i--) {
              if (updatedMessages[i] && !updatedMessages[i].isUser) {
                if (hasRealContent) {
                  // ADD STOPPED MESSAGE TO PARTIAL RESPONSE
                  updatedMessages[i] = {
                    ...updatedMessages[i],
                    text: contentToStore,
                    isTyping: false,
                  };
                  console.log(
                    "Stop button: Added stop message to last bot response"
                  );
                } else {
                  // SHOW "You stopped this request" MESSAGE INSTEAD OF REMOVING
                  updatedMessages[i] = {
                    ...updatedMessages[i],
                    text: stoppedMessage,
                    isTyping: false,
                  };
                  console.log(
                    "Stop button: Replaced thinking with stopped message via fallback"
                  );
                }
                break;
              }
            }
          }
          return updatedMessages;
        });
        messageIndexRef.current = null;
      } else {
        console.log(
          "Stop button: messageIndexRef.current is null - no message to handle"
        );
      }

      // Update database with stopped status and the actual content (partial response + stopped message)
      const responseIdToUpdate = currentResponseIdRef.current || responseId;
      if (responseIdToUpdate != null) {
        await supabase
          .from("chat")
          .update({
            query_status: "stop",
            content: contentToStore
          })
          .eq("id", responseIdToUpdate);
        console.log("Stop button: Updated database with content, id:", responseIdToUpdate, "content length:", contentToStore.length);
      }

      // Clear the refs
      currentResponseIdRef.current = null;
      currentStreamingContentRef.current = "";

      return; // Don't continue execution after stopping
    }

    if ((inputText.trim() || selectedFile || audioInput) && !isLoading) {
      // Ensure we enable auto-scroll when user explicitly sends a message
      if (autoScrollingRef) autoScrollingRef.current = true;

      // Set "Thinking" immediately
      setResponseInformMessage("Thinking");

      setAutoScrolling(true);
      setIsUserManuallyScroll(false);
      scrollToBottom();

      let insertedRecords = null;
      let queueInsertedRecords = null;

      try {
        const fileData = previewFile;
        var IfUserQueryStored = true;
        // If a mention chip is active, prepend it to the query
        var UserQuery = selectedMention
          ? `${selectedMention.label}: ${inputText.trim()}`
          : inputText.trim();
        setSelectedMention(null);
        setInputText("");

        // 1. Check if audio input is available
        if (audioInput != null) {
          setIsVoiceProcessing(true); // Start loader
          UserQuery += " " + (await audioToText(audioInput));
          UserQuery = UserQuery.trim();
          setIsVoiceProcessing(false); // Hide loader after transcript

          // copyToTextArea
          if (copyToTextArea) {
            setInputText(UserQuery);
            setCopyToTextArea(false);
            return;
          }

          let supabaseAudioUrl = await uploadAudioAndGetUrl(
            chatSessionId,
            userId,
            audioInput
          );

          if (supabaseAudioUrl) {
            try {
              await storeChatHistory(
                chatSessionId,
                "",
                "audio",
                true,
                supabaseAudioUrl,
                null,
                userId
              );
            } catch (audioStoreError) {
              console.error("Error storing audio message:", audioStoreError);
            }
          }

          const messageDate = moment
            .utc()
            .tz(userTimeZone ? userTimeZone : "America/Toronto")
            .format("MM-DD-YYYY hh:mm A");

          setMessages((prevMessages) => [
            ...prevMessages,
            ...(supabaseAudioUrl
              ? [
                  {
                    audioUrl: supabaseAudioUrl,
                    isUser: true,
                    default: false,
                    time: messageDate,
                    isTyping: false,
                  },
                ]
              : []),
            {
              text: UserQuery,
              isUser: true,
              isHtml: true,
              default: false,
              time: messageDate,
              isTyping: true,
            },
          ]);
          // NOTE: User message is saved by backend (run_sse/route.ts line 77)
          // Audio message is saved above, text query is saved by backend
          IfUserQueryStored = false; // Mark as stored to prevent duplicate
          setAudioInput(null);
          setInputText(""); // <-- ADD THIS LINE
          // return;
        }

        if (fileData) {
          supabaseUploadFileUrl = await uploadFileAndGetUrl(
            chatSessionId,
            userId,
            selectedFile
          );

          let messageDate = moment
            .utc()
            .tz(userTimeZone ? userTimeZone : "America/Toronto")
            .format("MM-DD-YYYY hh:mm A");
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: UserQuery,
              isUser: true,
              fileName: fileData.fileName,
              preview: supabaseUploadFileUrl,
              fileType: fileData.type,
              default: false,
              time: messageDate,
              isTyping: true,
            },
          ]);

          // console.log("supabaseUploadFileUrl", supabaseUploadFileUrl);
          // PATCH: Handle file upload failure
          if (!supabaseUploadFileUrl) {
            alert("File upload failed. Please try again.");
            setPreviewFile(null);
            setSelectedFile(null);
            return;
          }
          // NOTE: User message is saved by backend (run_sse/route.ts line 77)
          // File URL is included in message text via Attachment_URL format
          IfUserQueryStored = false;
          setPreviewFile(null);
          setSelectedFile(null);
          // return;
        }

        // 2. Check if UserQuery is empty after processing audio and file
        if (!fileData && !audioInput) {
          setTimeout(() => {
            setIsUserQueyLoading(true);
            setIsSending(true);
          }, 0);
        }

        if (IfUserQueryStored) {
          let messageDate = moment
            .utc()
            .tz(userTimeZone ? userTimeZone : "America/Toronto")
            .format("MM-DD-YYYY hh:mm A");

          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: UserQuery,
              isUser: true,
              isHtml: true,
              default: false,
              time: messageDate,
              isTyping: true,
            },
          ]);
          // NOTE: User message is saved by backend (run_sse/route.ts line 77)
          // to avoid duplicate messages when switching sessions
          setIsLoading(false);
          setIsSending(false);
          setIsUserQueyLoading(false);
        }

        const formData = new FormData();
        formData.append("session_id", chatSessionId);
        formData.append("session_name", chatName ? chatName : "main");
        formData.append("search_mode", webSearch);
        if (selectedItem) formData.append("session_model_name", selectedItem);

        if (selectedFile) {
          formData.append("file", selectedFile);
        }

        setTimeout(async () => {
          // CREATE NEW ABORT CONTROLLER FOR EACH REQUEST
          controllerRef.current = new AbortController();

          insertedRecords = await storeChatHistory(
            chatSessionId,
            "",
            "text",
            false,
            null,
            null,
            userId
          );

          // Store the response ID in ref for stop functionality
          if (insertedRecords && insertedRecords[0]) {
            currentResponseIdRef.current = insertedRecords[0].id;
          }

          var mention = null;
          if (UserQuery.includes("@")) {
            mention = UserQuery.match(
              /@[A-Za-z0-9&_?]+(?:[\s_]+[A-Za-z0-9&_?]+)*/
            )?.[0];
            const atIndex = UserQuery.indexOf("@");
            const colonIndex = UserQuery.indexOf(":", atIndex);
            if (colonIndex !== -1) {
              // UserQuery = UserQuery.slice(colonIndex + 1).trim();
            }
          }
          if (mention) {
            formData.append("isDirect", actionLists[mention]);
          } else {
            formData.append("isDirect", "");
          }
          formData.append("chatInput", UserQuery);
          formData.append("request_id", insertedRecords[0].id);
          if (chatSessionType === "chatbot") {
            formData.append("assistant_id", ChatAsistantId);
          }

          if (queue) {
            queueInsertedRecords = await storeQueueHistory(
              chatSessionId,
              chatName ? chatName : "main",
              UserQuery,
              null,
              userId
            );
          }
          if (queue && queueInsertedRecords && queueInsertedRecords.dataQueue && queueInsertedRecords.dataQueue[0]) {
            const queueRequestWithChat = {
              ...queueInsertedRecords.dataQueue[0],
              queue_chat: queueInsertedRecords.chatQuery?.[0] || null
            };
            setQueueRequests((prev) => [...prev, queueRequestWithChat]);
            setQueueRefreshKey((prev) => prev + 1);
          }

          let userText = UserQuery;
          if (selectedFile && supabaseUploadFileUrl) {
            userText += `\n Attachment_URL: [${supabaseUploadFileUrl}]`;
          }

          // Validate session ID before sending
          if (!chatSessionId) {
            console.error("[ChatBot] chatSessionId is null - cannot send message without valid session");
            setIsSending(false);
            return;
          }

          console.log("[ChatBot] Sending message with sessionId:", chatSessionId);

          let payload = {
            appName: "main_agent",
            userId: userId,
            sessionId: chatSessionId,
            newMessage: {
              role: "user",
              parts: [{ text: userText }],
            },
            streaming: !queue,
            request_id: insertedRecords[0].id,
            queue_request_id: queue ? queueInsertedRecords.dataQueue[0].id : "",
            ui_context: uiContext, // Pass uiContext
            mode: selectedMode, // Mode system: "simple" or "smart"
            customModel: selectedMode === "genius" && hasExplicitCustomModel ? selectedItem : undefined, // Direct provider/model override
            customModelEnabled: selectedMode === "genius" && hasExplicitCustomModel,
            customContextLimits: fullContextEnabled && customContextLimits ? customContextLimits : undefined,
            fullContext: fullContextEnabled || undefined,
          };

          // setIsLoading(true);
          setIsSending(true);

          messageIndexRef.current = null;
          // Reset tools tracking for new response (used for DEBUG_UI)
          toolsUsedRef.current = [];
          
          let messageDate = moment
            .utc()
            .tz(userTimeZone ? userTimeZone : "America/Toronto")
            .format("MM-DD-YYYY hh:mm A");

          setMessages((prevMessages) => {
            const newMessages = [
              ...prevMessages,
              {
                text: "",
                isUser: false,
                isHtml: true,
                default: false,
                time: messageDate,
                isTyping: true,
                toolsUsed: [], // Will be populated during streaming for DEBUG_UI
              },
            ];
            messageIndexRef.current = newMessages.length - 1;
            console.log(
              "Created new response message at index:",
              messageIndexRef.current
            );
            console.log("New message:", newMessages[messageIndexRef.current]);
            return newMessages;
          });

          let send_url = "";
          let headers_data = {};
          if (chatSessionType === "chatbot") {
            payload = {
              query: formData.get("chatInput"),
              assistant_id: ChatAsistantId,
              request_id: insertedRecords[0].id,
            };
            send_url = zoomeyApiUrl + "/chatbot";
            headers_data = {
              "Content-Type": "application/json",
            };
          } else {
            send_url = zoomeyApiUrl + "/run_sse";
            // Refresh token before API call
            const {
              data: { session },
            } = await supabase.auth.getSession();
            if (session?.access_token) {
              setApiToken(session.access_token);
            }
            headers_data = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            };
          }

          setIsStreaming(true);
          await fetch(send_url, {
            method: "POST",
            headers: headers_data,
            body: JSON.stringify(payload),
            signal: controllerRef.current.signal, // ADD THIS LINE
          })
            .then(async (response) => {
              console.log("response received", response);
              if (!response.ok) {
                // Try to read the error response body
                let errorDetail = "";
                try {
                  const errorData = await response.json();
                  errorDetail = errorData.detail || errorData.error || "";
                } catch (e) {
                  errorDetail = "";
                }

                // Check for 401 Authentication error - use status code directly
                const is401Error = response.status === 401 ||
                  (errorDetail.toLowerCase().includes("401") && errorDetail.toLowerCase().includes("authentication"));

                if (is401Error) {
                  // Save the user's query to localStorage for restoration after refresh
                  const userQuery = formData.get("chatInput");
                  if (userQuery) {
                    localStorage.setItem("auth_error_draft_query", userQuery);
                  }

                  // Delete the last 2 chat entries (user query + empty response) for better UX
                  try {
                    // Get the last 2 records for this session and user
                    const { data: lastRecords } = await supabase
                      .from("chat")
                      .select("id")
                      .eq("chat_session_id", chatSessionId)
                      .eq("user_id", userId)
                      .order("created_at", { ascending: false })
                      .limit(2);

                    if (lastRecords && lastRecords.length > 0) {
                      const idsToDelete = lastRecords.map(record => record.id);
                      await supabase
                        .from("chat")
                        .delete()
                        .in("id", idsToDelete);
                      console.log("Deleted last 2 chat entries due to auth error:", idsToDelete);
                    }
                  } catch (deleteError) {
                    console.error("Error deleting chat entries:", deleteError);
                  }

                  // Reset states
                  setIsSending(false);
                  setIsLoading(false);
                  setIsStreaming(false);
                  setResponseInformMessage("Thinking");

                  // Show Swal error popup and refresh page
                  Swal.fire({
                    icon: "error",
                    title: "Session Expired",
                    text: "Your session has expired. Please log in again.",
                    confirmButtonText: "OK",
                    background: "var(--background)",
                    color: "var(--text)",
                    confirmButtonColor: "var(--teal-bg)",
                  }).then(() => {
                    window.location.reload();
                  });

                  return; // Stop further processing
                }

                // Check for 402 Insufficient Credits
                if (response.status === 402 || errorDetail.includes("INSUFFICIENT_CREDITS")) {
                  setShowRechargeModal(true);
                  setIsSending(false);
                  setIsLoading(false);
                  setIsStreaming(false);
                  setResponseInformMessage("Thinking");

                  // Remove the stuck "Thinking" assistant bubble
                  if (messageIndexRef.current !== null) {
                    setMessages((prev) => prev.filter((_, i) => i !== messageIndexRef.current));
                    messageIndexRef.current = null;
                  }

                  // Delete empty assistant placeholder from DB
                  const responseIdToUpdate = currentResponseIdRef.current;
                  if (responseIdToUpdate != null) {
                    supabase.from("chat").delete().eq("id", responseIdToUpdate);
                    currentResponseIdRef.current = null;
                  }

                  return;
                }

                throw new Error(errorDetail || `HTTP error! status: ${response.status}`);
              }

              if (!queue) {
                if (!response.body) {
                  throw new Error(
                    "ReadableStream not supported in this browser/environment."
                  );
                }
                setIsStreaming(true);
                // Reset streaming content ref when starting new stream
                currentStreamingContentRef.current = "";
                const reader = response.body.getReader();
                const decoder = new TextDecoder("utf-8");
                let partial = "";
                let message = "";
                let isFirstChunk = true;
                let responseThinkingTimeout = null;

                while (true) {
                  try {
                    const { done, value } = await reader.read();
                    if (done) break;
                    const chunk = decoder.decode(value, { stream: false });
                    partial += chunk;
                    let lines = partial.split("\n");
                    partial = lines.pop();

                    for (let line of lines) {
                      // Process data: prefixed lines (SSE format)
                      if (line.trim().startsWith("data:")) {
                        try {
                          const jsonStr = line.trim().substring(5).trim();
                          if (jsonStr) {
                            const data = JSON.parse(jsonStr);

                            if (data.error != undefined || data.detail != undefined) {
                              const errorText = data.error || data.detail || 'Something went wrong with the server. Please try again.';

                              // Check for 401 Authentication error - case insensitive
                              const is401Error = errorText.toLowerCase().includes("401") && errorText.toLowerCase().includes("authentication");

                              if (is401Error) {
                                // Save the user's query to localStorage for restoration after refresh
                                const userQuery = formData.get("chatInput");
                                if (userQuery) {
                                  localStorage.setItem("auth_error_draft_query", userQuery);
                                }

                                // Show Swal error popup and refresh page
                                Swal.fire({
                                  icon: "error",
                                  title: "Session Expired",
                                  text: "Your session has expired. The page will refresh to restore your session.",
                                  confirmButtonText: "OK",
                                  background: "var(--background)",
                                  color: "var(--text)",
                                  confirmButtonColor: "var(--teal-bg)",
                                }).then(() => {
                                  window.location.reload();
                                });

                                // Reset states
                                setIsSending(false);
                                setIsLoading(false);
                                setIsStreaming(false);
                                setResponseInformMessage("Thinking");
                                return;
                              }

                              // Check for litellm/model errors and show user-friendly message
                              const isModelError = errorText.toLowerCase().includes("litellm") ||
                                                   errorText.toLowerCase().includes("notfounderror") ||
                                                   errorText.toLowerCase().includes("anthropicexception") ||
                                                   errorText.toLowerCase().includes("openaiexception") ||
                                                   errorText.toLowerCase().includes("model:");

                              const userFriendlyError = isModelError
                                ? "Something went wrong."
                                : errorText;

                              // Format error message from server for non-401 errors
                              const serverErrorMessage = `<span style="color: #dc3545;">⚠️ ${userFriendlyError}</span>`;

                              setMessages((prevMessages) => {
                                if (
                                  messageIndexRef.current !== null &&
                                  prevMessages[messageIndexRef.current]
                                ) {
                                  const updatedMessages = [...prevMessages];
                                  updatedMessages[messageIndexRef.current] = {
                                    ...updatedMessages[messageIndexRef.current],
                                    text: serverErrorMessage,
                                    isTyping: false,
                                    isHtml: true,
                                  };
                                  return updatedMessages;
                                }
                                return prevMessages;
                              });

                              // Update database with error
                              const responseIdToUpdate = currentResponseIdRef.current;
                              if (responseIdToUpdate != null) {
                                supabase
                                  .from("chat")
                                  .update({
                                    query_status: "error",
                                    content: serverErrorMessage
                                  })
                                  .eq("id", responseIdToUpdate)
                                  .then(() => {
                                    console.log("(SSE Error) Updated database with error message");
                                    currentResponseIdRef.current = null;
                                  });
                              }

                              // Reset states
                              setIsSending(false);
                              setIsLoading(false);
                              setIsStreaming(false);
                              setResponseInformMessage("Thinking");
                            }

                            // === NEW UI MESSAGE STREAM FORMAT HANDLERS ===

                            // Capture generated images from backend for special rendering
                            if (data.type === "data-generated-images" && data.data) {
                              const imageUrls = data.data.urls || [];
                              const imagePrompt = data.data.prompt;
                              if (imageUrls.length > 0) {
                                console.log('[ChatBot] Received generated images:', imageUrls);
                                // Store generated images for this message
                                setMessages((prevMessages) => {
                                  if (messageIndexRef.current !== null && prevMessages[messageIndexRef.current]) {
                                    const updatedMessages = [...prevMessages];
                                    updatedMessages[messageIndexRef.current] = {
                                      ...updatedMessages[messageIndexRef.current],
                                      generatedImages: imageUrls,
                                      imagePrompt: imagePrompt,
                                    };
                                    return updatedMessages;
                                  }
                                  return prevMessages;
                                });
                              }
                              continue;
                            }

                            // EXPLICITLY IGNORE tool-related events that should not be rendered
                            // These contain tool input arguments and results, not user-facing text
                            if (data.type && (
                              data.type === "tool-input-start" ||
                              data.type === "tool-input-delta" ||
                              data.type === "tool-input-end" ||
                              data.type === "tool-input-available" ||
                              data.type === "tool-output-available" ||
                              data.type === "tool-call" ||
                              data.type === "tool-result" ||
                              data.type === "start" ||
                              data.type === "start-step" ||
                              data.type === "finish-step" ||
                              data.type === "finish"
                            )) {
                              // Skip these events - they're for internal processing only
                              continue;
                            }

                            // Handle text-delta (new format from Vercel AI SDK UI Message Stream)
                            if (data.type === "text-delta" && data.delta) {
                              message += data.delta;
                              currentStreamingContentRef.current = formatMessage(message);

                              if (isFirstChunk) {
                                setIsLoading(false);
                                setIsSending(false);
                                isFirstChunk = false;
                              }
                              setResponseInformMessage("Writing");
                              if (responseThinkingTimeout) {
                                clearTimeout(responseThinkingTimeout);
                                responseThinkingTimeout = null;
                              }

                              responseThinkingTimeout = setTimeout(() => {
                                setResponseInformMessage("Thinking");
                              }, 4000);

                              setMessages((prevMessages) => {
                                if (
                                  messageIndexRef.current !== null &&
                                  prevMessages[messageIndexRef.current]
                                ) {
                                  const updatedMessages = [...prevMessages];
                                  updatedMessages[messageIndexRef.current] = {
                                    ...updatedMessages[messageIndexRef.current],
                                    text: message,
                                  };
                                  return updatedMessages;
                                }
                                return prevMessages;
                              });
                              if (autoScrollingRef.current && chatContainerRef.current) {
                                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                              }
                            }

                            // Handle data parts (tool status updates) - new format
                            if (data.type === "data-tool-status" && data.data) {
                              const toolName = data.data.tool;
                              const statusMessage = data.data.status || getToolStatusMessage(toolName);
                              
                              // Track tool usage for DEBUG_UI
                              if (toolName && !toolsUsedRef.current.includes(toolName)) {
                                toolsUsedRef.current.push(toolName);
                              }
                              
                              if (responseThinkingTimeout) {
                                clearTimeout(responseThinkingTimeout);
                                responseThinkingTimeout = null;
                              }
                              setResponseInformMessage(statusMessage);
                              
                              responseThinkingTimeout = setTimeout(() => {
                                setResponseInformMessage("Thinking");
                              }, 6000);
                            }

                            // Handle error type - new format
                            if (data.type === "error" && data.errorText) {
                              const serverErrorMessage = `<span style="color: #dc3545;">⚠️ ${data.errorText}</span>`;
                              setMessages((prevMessages) => {
                                if (
                                  messageIndexRef.current !== null &&
                                  prevMessages[messageIndexRef.current]
                                ) {
                                  const updatedMessages = [...prevMessages];
                                  updatedMessages[messageIndexRef.current] = {
                                    ...updatedMessages[messageIndexRef.current],
                                    text: serverErrorMessage,
                                    isTyping: false,
                                    isHtml: true,
                                  };
                                  return updatedMessages;
                                }
                                return prevMessages;
                              });
                              setIsSending(false);
                              setIsLoading(false);
                              setIsStreaming(false);
                              setResponseInformMessage("Thinking");
                            }

                            // Handle text-start and text-end (markers, no action needed)
                            if (data.type === "text-start" || data.type === "text-end") {
                              // These are just markers, no action needed
                            }

                            // === LEGACY FORMAT HANDLERS (for backward compatibility) ===
                            if (data.type === "content") {
                              const responseMessageData = data.text;

                              if (
                                responseMessageData.length > message.length &&
                                responseMessageData.startsWith(message)
                              ) {
                                message = responseMessageData;
                              } else {
                                message += responseMessageData;
                              }
                              // Update the ref with current streaming content for stop functionality
                              currentStreamingContentRef.current = formatMessage(message);

                              if (isFirstChunk) {
                                setIsLoading(false);
                                setIsSending(false);
                                isFirstChunk = false;
                              }
                              setResponseInformMessage("Writing");
                              if (responseThinkingTimeout) {
                                clearTimeout(responseThinkingTimeout);
                                responseThinkingTimeout = null;
                              }

                              responseThinkingTimeout = setTimeout(() => {
                                setResponseInformMessage("Thinking");
                              }, 4000);

                              setMessages((prevMessages) => {
                                if (
                                  messageIndexRef.current !== null &&
                                  prevMessages[messageIndexRef.current]
                                ) {
                                  const updatedMessages = [...prevMessages];
                                  updatedMessages[messageIndexRef.current] = {
                                    ...updatedMessages[messageIndexRef.current],
                                    text: message,
                                  };
                                  return updatedMessages;
                                }
                              });
                              // setTimeout(() => {
                              if (autoScrollingRef.current) {
                                if (chatContainerRef.current) {
                                  chatContainerRef.current.scrollTop =
                                    chatContainerRef.current.scrollHeight;
                                }
                              }
                            }

                            if (
                              data.content &&
                              data.content.parts &&
                              data.content.parts[0] &&
                              data.content.parts[0].text &&
                              typeof data.partial !== "undefined" &&
                              data.partial === true
                            ) {
                              const currentText = data.content.parts[0].text;
                              if (
                                currentText.length > message.length &&
                                currentText.startsWith(message)
                              ) {
                                message = currentText;
                              } else {
                                message += currentText;
                              }
                              // Update the ref with current streaming content for stop functionality
                              currentStreamingContentRef.current = formatMessage(message);

                              if (isFirstChunk) {
                                setIsLoading(false);
                                setIsSending(false);
                                isFirstChunk = false;
                              }
                              setResponseInformMessage("Writing");
                              if (responseThinkingTimeout) {
                                clearTimeout(responseThinkingTimeout);
                                responseThinkingTimeout = null;
                              }

                              responseThinkingTimeout = setTimeout(() => {
                                setResponseInformMessage("Thinking");
                              }, 4000);

                              setMessages((prevMessages) => {
                                if (
                                  messageIndexRef.current !== null &&
                                  prevMessages[messageIndexRef.current]
                                ) {
                                  const updatedMessages = [...prevMessages];
                                  updatedMessages[messageIndexRef.current] = {
                                    ...updatedMessages[messageIndexRef.current],
                                    text: message,
                                  };
                                  return updatedMessages;
                                }
                              });
                              // setTimeout(() => {
                              if (autoScrollingRef.current) {
                                if (chatContainerRef.current) {
                                  chatContainerRef.current.scrollTop =
                                    chatContainerRef.current.scrollHeight;
                                }
                              }
                              // }, 50);
                            }

                            if (
                              data.content &&
                              data.content.parts &&
                              data.content.parts[0] &&
                              data.content.parts[0].functionCall
                            ) {
                              const toolName = data.content.parts[0].functionCall.name;
                              console.log("Function call detected:", toolName);
                              
                              // Track tool usage for DEBUG_UI
                              if (toolName && !toolsUsedRef.current.includes(toolName)) {
                                toolsUsedRef.current.push(toolName);
                              }
                              
                              if (responseThinkingTimeout) {
                                clearTimeout(responseThinkingTimeout);
                                responseThinkingTimeout = null;
                              }
                              setResponseInformMessage(
                                zoomeyToolShortMessages[toolName] || getToolStatusMessage(toolName)
                              );
                            }
                            
                            // Handle A2UI content from backend
                            if (data.type === 'a2ui' && data.content) {
                              console.log("A2UI content received:", data.content.substring(0, 100));
                              console.log("messageIndexRef.current:", messageIndexRef.current);
                              
                              // Update the current message with A2UI content
                              if (messageIndexRef.current !== null) {
                                const targetIndex = messageIndexRef.current;
                                setMessages((prevMessages) => {
                                  console.log("Updating message at index", targetIndex, "total messages:", prevMessages.length);
                                  // Create a brand new array with brand new object to force React re-render
                                  const newMessages = prevMessages.map((msg, idx) => {
                                    if (idx === targetIndex) {
                                      console.log("Message before update:", JSON.stringify(msg).substring(0, 200));
                                      const updated = {
                                        ...msg,
                                        a2uiContent: data.content,
                                        isA2UI: true,
                                        isTyping: false // Ensure isTyping is false so we render A2UI
                                      };
                                      console.log("Message after update - isA2UI:", updated.isA2UI, "has a2uiContent:", !!updated.a2uiContent);
                                      return updated;
                                    }
                                    return msg;
                                  });
                                  return newMessages;
                                });
                              } else {
                                console.warn("A2UI content received but messageIndexRef.current is null");
                              }
                            }
                            
                            // Handle usage/token metadata from backend
                            if (data.type === 'usage' && data.model) {
                              console.log("Usage data received:", data);
                              
                              // Update the current message with usage info
                              if (messageIndexRef.current !== null) {
                                const targetIndex = messageIndexRef.current;
                                setMessages((prevMessages) => {
                                  const newMessages = prevMessages.map((msg, idx) => {
                                    if (idx === targetIndex) {
                                      return {
                                        ...msg,
                                        usageInfo: {
                                          model: data.model,
                                          tier: data.tier,
                                          tokens: data.tokens
                                        }
                                      };
                                    }
                                    return msg;
                                  });
                                  return newMessages;
                                });
                              }
                            }
                          }
                        } catch (e) {
                          console.warn("Failed to parse SSE data:", e);
                        }
                      }
                    }
                  } catch (readerError) {
                    // Handle reader abort error
                    if (readerError.name === "AbortError") {
                      console.log("Stream reading was cancelled by user");
                      setIsSending(false);
                      setIsLoading(false);
                      setIsStreaming(false);
                      setResponseInformMessage("Thinking");

                      // Stopped request message to display and store
                      const stoppedMessage = '<span style="color: #888; font-style: italic;">⏹️ You stopped this request</span>';

                      // Get the current streaming content from ref
                      const currentStreamingContent = currentStreamingContentRef.current || "";

                      // Check if we have meaningful content
                      const hasRealContent =
                        currentStreamingContent &&
                        !currentStreamingContent.includes("Thinking") &&
                        !currentStreamingContent.includes("loading-dots") &&
                        currentStreamingContent.trim() !== "" &&
                        currentStreamingContent.trim() !== "<p> </p>";

                      // Determine what content to store in database
                      const contentToStore = hasRealContent
                        ? currentStreamingContent + '<br><br>' + stoppedMessage
                        : stoppedMessage;

                      // CHECK IF WE HAVE PARTIAL RESPONSE OR EMPTY RESPONSE
                      if (messageIndexRef.current !== null) {
                        console.log(
                          "(Reader) Handling stopped message at index:",
                          messageIndexRef.current
                        );
                        setMessages((prevMessages) => {
                          const updatedMessages = [...prevMessages];

                          if (
                            messageIndexRef.current < updatedMessages.length &&
                            updatedMessages[messageIndexRef.current] &&
                            !updatedMessages[messageIndexRef.current].isUser
                          ) {
                            const currentMessage =
                              updatedMessages[messageIndexRef.current];

                            if (hasRealContent) {
                              // ADD STOPPED MESSAGE TO PARTIAL RESPONSE
                              updatedMessages[messageIndexRef.current] = {
                                ...currentMessage,
                                text: contentToStore,
                                isTyping: false,
                              };
                              console.log(
                                "(Reader) Added stop message to partial response"
                              );
                            } else {
                              // SHOW "You stopped this request" MESSAGE INSTEAD OF REMOVING
                              updatedMessages[messageIndexRef.current] = {
                                ...currentMessage,
                                text: stoppedMessage,
                                isTyping: false,
                              };
                              console.log(
                                "(Reader) Replaced thinking with stopped message"
                              );
                            }
                          }
                          return updatedMessages;
                        });
                        messageIndexRef.current = null;
                      }

                      // Update database with stopped status and the actual content
                      const responseIdToUpdate = currentResponseIdRef.current;
                      if (responseIdToUpdate != null) {
                        await supabase
                          .from("chat")
                          .update({
                            query_status: "stop",
                            content: contentToStore
                          })
                          .eq("id", responseIdToUpdate);
                        console.log("(Reader) Updated database with content, id:", responseIdToUpdate, "content length:", contentToStore.length);
                        currentResponseIdRef.current = null;
                        currentStreamingContentRef.current = "";
                      }

                      break; // Exit the while loop cleanly
                    }
                    // Re-throw other errors to be caught by main catch block
                    throw readerError;
                  }
                }
                setIsStreaming(false);
                setIsUserQueyLoading(false); // Reset loading state after successful completion
                setIsSending(false);
                setResponseInformMessage("Thinking");
                // Clear response ID ref and streaming content ref on successful completion
                currentResponseIdRef.current = null;
                currentStreamingContentRef.current = "";

                // Check for session navigation in the response
                if (message) {
                  checkAndNavigateSession(message);
                }

                // Update message with tools used for DEBUG_UI
                if (messageIndexRef.current !== null && toolsUsedRef.current.length > 0) {
                  const finalToolsUsed = [...toolsUsedRef.current];
                  setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    if (updatedMessages[messageIndexRef.current]) {
                      updatedMessages[messageIndexRef.current] = {
                        ...updatedMessages[messageIndexRef.current],
                        toolsUsed: finalToolsUsed,
                        isTyping: false,
                      };
                    }
                    return updatedMessages;
                  });
                }
                
                if (responseThinkingTimeout) {
                  clearTimeout(responseThinkingTimeout);
                  responseThinkingTimeout = null;
                }
              } else {
                const data = await response.json();
                setIsLoading(false);
                setIsSending(false);
                setIsUserQueyLoading(false); // Reset loading state for queue responses
                let messageDate = moment
                  .utc()
                  .tz(userTimeZone ? userTimeZone : "America/Toronto")
                  .format("MM-DD-YYYY hh:mm A");
                if (
                  data.content &&
                  data.content.parts &&
                  data.content.parts[0] &&
                  data.content.parts[0].text
                ) {
                  const responseText = data.content.parts[0].text;
                  setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                      text: responseText,
                      isUser: false,
                      isHtml: true,
                      default: false,
                      time: messageDate,
                      isTyping: false,
                    },
                  ]);
                }
              }
            })
            .catch(async (fetchError) => {
              console.error("Fetch error:", fetchError);

              // Handle fetch abort error FIRST (user stopped the request)
              if (fetchError.name === "AbortError") {
                console.log("Fetch request was cancelled by user");
                setIsSending(false);
                setIsLoading(false);
                setIsStreaming(false);
                setResponseInformMessage("Thinking");

                // Stopped request message to display and store
                const stoppedMessage = '<span style="color: #888; font-style: italic;">⏹️ You stopped this request</span>';

                // Get the current streaming content from ref
                const currentStreamingContent = currentStreamingContentRef.current || "";

                // Check if we have meaningful content
                const hasRealContent =
                  currentStreamingContent &&
                  !currentStreamingContent.includes("Thinking") &&
                  !currentStreamingContent.includes("loading-dots") &&
                  currentStreamingContent.trim() !== "" &&
                  currentStreamingContent.trim() !== "<p> </p>";

                // Determine what content to store in database
                const contentToStore = hasRealContent
                  ? currentStreamingContent + '<br><br>' + stoppedMessage
                  : stoppedMessage;

                // CHECK IF WE HAVE PARTIAL RESPONSE OR EMPTY RESPONSE
                if (messageIndexRef.current !== null) {
                  console.log(
                    "(Fetch) Handling stopped message at index:",
                    messageIndexRef.current
                  );
                  setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];

                    if (
                      messageIndexRef.current < updatedMessages.length &&
                      updatedMessages[messageIndexRef.current] &&
                      !updatedMessages[messageIndexRef.current].isUser
                    ) {
                      const currentMessage =
                        updatedMessages[messageIndexRef.current];

                      if (hasRealContent) {
                        // ADD STOPPED MESSAGE TO PARTIAL RESPONSE
                        updatedMessages[messageIndexRef.current] = {
                          ...currentMessage,
                          text: contentToStore,
                          isTyping: false,
                        };
                        console.log(
                          "(Fetch) Added stop message to partial response"
                        );
                      } else {
                        // SHOW "You stopped this request" MESSAGE INSTEAD OF REMOVING
                        updatedMessages[messageIndexRef.current] = {
                          ...currentMessage,
                          text: stoppedMessage,
                          isTyping: false,
                        };
                        console.log("(Fetch) Replaced thinking with stopped message");
                      }
                    }
                    return updatedMessages;
                  });
                  messageIndexRef.current = null;
                }

                // Update database with stopped status and the actual content
                const responseIdToUpdate = currentResponseIdRef.current;
                if (responseIdToUpdate != null) {
                  await supabase
                    .from("chat")
                    .update({
                      query_status: "stop",
                      content: contentToStore
                    })
                    .eq("id", responseIdToUpdate);
                  console.log("(Fetch) Updated database with content, id:", responseIdToUpdate, "content length:", contentToStore.length);
                  currentResponseIdRef.current = null;
                  currentStreamingContentRef.current = "";
                }

                return; // Don't re-throw the error for abort
              }

              // Handle SERVER ERRORS - show error message instead of blank bubble
              setIsSending(false);
              setIsLoading(false);
              setIsStreaming(false);
              setResponseInformMessage("Thinking");

              // Error message to display and store
              const errorMessage = '<span style="color: #dc3545;">⚠️ Something went wrong with the server. Please try again.</span>';

              // Update the response bubble with error message instead of removing it
              if (messageIndexRef.current !== null) {
                console.log(
                  "(Fetch Error) Showing error message at index:",
                  messageIndexRef.current
                );
                setMessages((prevMessages) => {
                  const updatedMessages = [...prevMessages];

                  if (
                    messageIndexRef.current < updatedMessages.length &&
                    updatedMessages[messageIndexRef.current] &&
                    !updatedMessages[messageIndexRef.current].isUser
                  ) {
                    // Replace thinking/empty message with error message
                    updatedMessages[messageIndexRef.current] = {
                      ...updatedMessages[messageIndexRef.current],
                      text: errorMessage,
                      isTyping: false,
                      isHtml: true,
                    };
                    console.log("(Fetch Error) Replaced message with error");
                  }
                  return updatedMessages;
                });
              } else {
                // Fallback: Add error message if no message index
                let messageDate = moment
                  .utc()
                  .tz(userTimeZone ? userTimeZone : "America/Toronto")
                  .format("MM-DD-YYYY hh:mm A");
                setMessages((prevMessages) => [
                  ...prevMessages,
                  {
                    text: errorMessage,
                    isUser: false,
                    isHtml: true,
                    default: false,
                    time: messageDate,
                    isTyping: false,
                  },
                ]);
              }

              // Update database with error message
              const responseIdToUpdate = currentResponseIdRef.current;
              if (responseIdToUpdate != null) {
                await supabase
                  .from("chat")
                  .update({
                    query_status: "error",
                    content: errorMessage
                  })
                  .eq("id", responseIdToUpdate);
                console.log("(Fetch Error) Updated database with error message, id:", responseIdToUpdate);
                currentResponseIdRef.current = null;
              }

              messageIndexRef.current = null;
            });
        }, 100);
      } catch (error) {
        // HANDLE ABORT ERROR
        if (error.name === "AbortError") {
          console.log("Request was cancelled by user");
          setIsSending(false);
          setIsLoading(false);
          setIsStreaming(false);
          setResponseInformMessage("Thinking");

          // Stopped request message to display and store
          const stoppedMessage = '<span style="color: #888; font-style: italic;">⏹️ You stopped this request</span>';

          // Get the current streaming content from ref
          const currentStreamingContent = currentStreamingContentRef.current || "";

          // Check if we have meaningful content
          const hasRealContent =
            currentStreamingContent &&
            !currentStreamingContent.includes("Thinking") &&
            !currentStreamingContent.includes("loading-dots") &&
            currentStreamingContent.trim() !== "" &&
            currentStreamingContent.trim() !== "<p> </p>";

          // Determine what content to store in database
          const contentToStore = hasRealContent
            ? currentStreamingContent + '<br><br>' + stoppedMessage
            : stoppedMessage;

          // CHECK IF WE HAVE PARTIAL RESPONSE OR EMPTY RESPONSE
          if (messageIndexRef.current !== null) {
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];

              if (
                messageIndexRef.current < updatedMessages.length &&
                updatedMessages[messageIndexRef.current] &&
                !updatedMessages[messageIndexRef.current].isUser
              ) {
                const currentMessage = updatedMessages[messageIndexRef.current];

                if (hasRealContent) {
                  // ADD STOPPED MESSAGE TO PARTIAL RESPONSE
                  updatedMessages[messageIndexRef.current] = {
                    ...currentMessage,
                    text: contentToStore,
                    isTyping: false,
                  };
                  console.log("(Catch) Added stop message to partial response");
                } else {
                  // SHOW "You stopped this request" MESSAGE INSTEAD OF REMOVING
                  updatedMessages[messageIndexRef.current] = {
                    ...currentMessage,
                    text: stoppedMessage,
                    isTyping: false,
                  };
                  console.log("(Catch) Replaced thinking with stopped message");
                }
              }
              return updatedMessages;
            });
            messageIndexRef.current = null;
          }

          // Update database with stopped status and the actual content
          const responseIdToUpdate = currentResponseIdRef.current;
          if (responseIdToUpdate != null) {
            await supabase
              .from("chat")
              .update({
                query_status: "stop",
                content: contentToStore
              })
              .eq("id", responseIdToUpdate);
            console.log("(Catch) Updated database with content, id:", responseIdToUpdate, "content length:", contentToStore.length);
            currentResponseIdRef.current = null;
            currentStreamingContentRef.current = "";
          }

          return;
        }

        // Handle other errors - show error message instead of blank bubble
        setIsStreaming(false);
        setIsSending(false);
        setIsLoading(false);
        setResponseInformMessage("Thinking");

        // Error message to display and store
        const errorMessage = '<span style="color: #dc3545;">⚠️ Something went wrong with the server. Please try again.</span>';

        // Update the response bubble with error message instead of leaving it blank
        if (messageIndexRef.current !== null) {
          console.log(
            "(Main Catch) Showing error message at index:",
            messageIndexRef.current
          );
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];

            if (
              messageIndexRef.current < updatedMessages.length &&
              updatedMessages[messageIndexRef.current] &&
              !updatedMessages[messageIndexRef.current].isUser
            ) {
              // Replace thinking/empty message with error message
              updatedMessages[messageIndexRef.current] = {
                ...updatedMessages[messageIndexRef.current],
                text: errorMessage,
                isTyping: false,
                isHtml: true,
              };
              console.log("(Main Catch) Replaced message with error");
            }
            return updatedMessages;
          });
        } else {
          // Fallback: Add error message if no message index
          let messageDate = moment
            .utc()
            .tz(userTimeZone ? userTimeZone : "America/Toronto")
            .format("MM-DD-YYYY hh:mm A");
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: errorMessage,
              isUser: false,
              isHtml: true,
              default: false,
              time: messageDate,
              isTyping: false,
            },
          ]);
        }

        // Update database with error message
        const responseIdToUpdate = currentResponseIdRef.current;
        if (responseIdToUpdate != null) {
          await supabase
            .from("chat")
            .update({
              query_status: "error",
              content: errorMessage
            })
            .eq("id", responseIdToUpdate);
          console.log("(Main Catch) Updated database with error message, id:", responseIdToUpdate);
          currentResponseIdRef.current = null;
        } else if (insertedRecords && insertedRecords[0]) {
          // Fallback to insertedRecords if ref is not set
          await supabase
            .from("chat")
            .update({
              query_status: "error",
              content: errorMessage
            })
            .eq("id", insertedRecords[0].id)
            .eq("user_id", userId);
          console.log("(Main Catch) Updated database with error message via insertedRecords");
        }

        messageIndexRef.current = null;
        console.error("Error Sending Message:", error);
      }
    }
  };

  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Update handleKeyPress to handle async function
  const handleKeyPress = (e) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !e.altKey &&
      !e.ctrlKey &&
      !ShowMentions
    ) {
      e.preventDefault();

      // Show warning if user tries to send while query is in progress
      if (isSending || isStreaming) {
        setShowQueryInProgressWarning(true);
        setTimeout(() => {
          setShowQueryInProgressWarning(false);
        }, 3000);
        return;
      }

      setInputText("");
      handleSendMessage();
    }
  };

  const handlePaste = (e) => {
    const clipboardItems = e.clipboardData.items;
    let hasFile = false;

    for (let item of clipboardItems) {
      if (item.kind === "file") {
        e.preventDefault(); // Only prevent default when pasting a file
        hasFile = true;
        const file = item.getAsFile();
        const validTypes = [
          // Images
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/bmp",
          "image/tiff",
          "image/webp",
          // Documents
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
          "application/vnd.ms-excel", // xls
          "text/csv",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
          "application/msword", // doc
          "text/plain", // txt
        ];

        if (validTypes.includes(file.type)) {
          const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
          const fileName = `${file.name
            .split(".")
            .slice(0, -1)
            .join(".")}_${timestamp}.${file.name.split(".").pop()}`;

          setSelectedFile(file);

          if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => {
              setPreviewFile({
                preview: reader.result,
                fileName: fileName,
                type: "image",
              });
            };
            reader.readAsDataURL(file);
          } else {
            const reader = new FileReader();
            reader.onloadend = () => {
              setPreviewFile({
                fileName: fileName,
                type: "document",
                preview: reader.result,
              });
            };
            reader.readAsDataURL(file);
          }
          return;
        }
      }
    }

    if (!hasFile) {
      // If no file was pasted, allow text to be pasted normally
      return;
    }
  };

  // Add clearChat function near your other state handlers
  const clearChat = async () => {
    // setMessages([
    //   { text: 'Hi! 👋 How can I help you today?', isUser: false, default: true }
    // ])
    setMessages([]);
    await clearChatHistory(chatSessionId).then(() => window.location.reload());
    // await storeChatHistory(chatSessionId, 'Hi! 👋 How can I help you today?', 'text', false)

    // setMessages([
    //   { text: "Hi! 👋 How can I help you today?", isUser: false,default: true }
    // ]);
  };

  useEffect(() => {
    if (setClearChat === true) {
      if (chatSessionId) {
        clearChat();
      }
    }
  }, [setClearChat]);

  // Add the formatting function
  // const formatMessage = text => {
  //   let cleanedText = text
  //   .replace("```html", '') // Remove starting ```html
  //   .replace("```", '') // Remove starting ```html
  //   .replace(/^"/, '') // Remove leading quotation mark
  //   .replace(/"$/, '') // Remove trailing quotation mark
  //   .replace(/\\n/g, '') // Remove all escaped newlines
  //   return cleanedText
  //     .replace(/###\s*(.*?)(?:\n|$)/g, '<h5>$1</h5>') // H3 headings
  //     .replace(/##\s*(.*?)(?:\n|$)/g, '<h4>$1</h4>') // H2 headings
  //     .replace(/#\s*(.*?)(?:\n|$)/g, '<h3>$1</h3>') // H1 headings
  //     .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
  //     .replace(/(\d+)\.\s+(.*?)(?:\n|$)/g, '<ol start="$1"><li>$2</li></ol>') // Numbered lists
  //     // .replace(
  //     //   /`(.*?)`/g,
  //     //   '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>'
  //     // ) // Inline code
  //     .replace(
  //       /\[(.*?)\]\((https?:\/\/[^\s]+)\)/g,
  //       '<a href="$2" target="_blank">$1</a>'
  //     ) // Markdown links
  //     // .replace(/\n/g, '<br />') // New lines
  //     .replace(/\{(.*?)\}/g, '$1') // Remove curly braces
  // }
  // Helper: Parse markdown tables
  const parseMarkdownTables = (text) => {
    const tableRegex = /^\|(.+)\|\n\|[-:\s|]+\|\n((?:\|.+\|\n?)+)/gm;

    return text.replace(tableRegex, (match, headerRow, bodyRows) => {
      const headers = headerRow.split('|').map(h => h.trim()).filter(Boolean);
      const rows = bodyRows.trim().split('\n').map(row =>
        row.split('|').map(cell => cell.trim()).filter(Boolean)
      );

      let table = '<table class="chat-table"><thead><tr>';
      headers.forEach(h => { table += `<th>${h}</th>`; });
      table += '</tr></thead><tbody>';
      rows.forEach(row => {
        table += '<tr>';
        row.forEach(cell => { table += `<td>${cell}</td>`; });
        table += '</tr>';
      });
      table += '</tbody></table>';

      return table;
    });
  };

  // Helper: Parse unordered lists
  const parseUnorderedLists = (text) => {
    const lines = text.split('\n');
    let result = [];
    let inList = false;

    for (const line of lines) {
      const listMatch = line.match(/^[-*]\s+(.+)$/);
      if (listMatch) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push(`<li>${listMatch[1]}</li>`);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(line);
      }
    }
    if (inList) result.push('</ul>');

    return result.join('\n');
  };

  // Helper: Parse ordered lists
  // Handles blank lines between list items (keeps them in same <ol>)
  const parseOrderedLists = (text) => {
    const lines = text.split('\n');
    let result = [];
    let inList = false;
    let pendingBlanks = []; // Track blank lines that might be between list items

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const listMatch = line.match(/^(\d+)\.\s+(.+)$/);
      
      if (listMatch) {
        if (!inList) {
          result.push('<ol>');
          inList = true;
        } else if (pendingBlanks.length > 0) {
          // Don't add blank lines between list items, just continue the list
          pendingBlanks = [];
        }
        result.push(`<li>${listMatch[2]}</li>`);
      } else if (line.trim() === '' && inList) {
        // Blank line while in list - check if next non-blank line is a list item
        pendingBlanks.push(line);
        // Look ahead to see if there's another list item coming
        let hasMoreListItems = false;
        for (let j = i + 1; j < lines.length; j++) {
          if (lines[j].trim() === '') continue;
          if (lines[j].match(/^(\d+)\.\s+(.+)$/)) {
            hasMoreListItems = true;
          }
          break;
        }
        if (!hasMoreListItems) {
          // No more list items, close the list
          result.push('</ol>');
          inList = false;
          result.push(...pendingBlanks);
          pendingBlanks = [];
        }
      } else {
        if (inList) {
          result.push('</ol>');
          inList = false;
          result.push(...pendingBlanks);
          pendingBlanks = [];
        }
        result.push(line);
      }
    }
    if (inList) result.push('</ol>');

    return result.join('\n');
  };

  // Helper to extract generated image URLs from message content
  // Detects Supabase Storage URLs with 'generated-' pattern
  const extractGeneratedImages = (content) => {
    if (!content) return [];
    
    // Match Supabase Storage URLs for generated images
    // Pattern: https://xxx.supabase.co/storage/v1/object/public/Storage/.../images/generated-...
    const supabasePattern = /https:\/\/[^\/]+\.supabase\.co\/storage\/v1\/object\/public\/Storage\/[^\/]+\/[^\/]+\/images\/generated-[^\s"'<>]+/gi;
    const matches = content.match(supabasePattern);
    return matches || [];
  };

  // Enhanced formatMessage with full markdown support
  const formatMessage = (text) => {
    if (!text) return '';

    let html = text
      // Clean up code block wrappers and quotes
      .replace(/```html\n?/g, '')
      .replace(/^"/, '')
      .replace(/"$/, '');

    // Code blocks (must be before inline code) - preserve content
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
      const escapedCode = code.replace(/</g, '&lt;').replace(/>/g, '&gt;').trim();
      return `<pre class="code-block${lang ? ` language-${lang}` : ''}"><code>${escapedCode}</code></pre>`;
    });

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

    // Headers (order matters: ###### before ##### ... before #)
    html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

    // Protect URLs from underscore processing by temporarily replacing them
    // This prevents URLs like https://example.com/file_name from becoming file<em>name</em>
    // IMPORTANT: Placeholder must NOT contain underscores to avoid being processed!
    const urlPlaceholders = [];
    // First protect URLs in markdown link syntax [text](url)
    html = html.replace(/\]\((https?:\/\/[^)]+)\)/g, (match, url) => {
      urlPlaceholders.push(url);
      return `](URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL)`;
    });
    // Then protect standalone URLs (not inside parentheses)
    html = html.replace(/(https?:\/\/[^\s<>\[\]()]+)/g, (match) => {
      urlPlaceholders.push(match);
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });

    // Bold and italic (order matters)
    html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Restore URLs
    html = html.replace(/URLPLACEHOLDER(\d+)ENDURL/g, (match, index) => {
      return urlPlaceholders[parseInt(index)];
    });

    // Blockquotes
    html = html.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>');
    // Merge consecutive blockquotes
    html = html.replace(/<\/blockquote>\n<blockquote>/g, '<br />');

    // Horizontal rules
    html = html.replace(/^---$/gm, '<hr />');
    html = html.replace(/^\*\*\*$/gm, '<hr />');

    // Tables
    html = parseMarkdownTables(html);

    // Unordered lists
    html = parseUnorderedLists(html);

    // Ordered lists
    html = parseOrderedLists(html);

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Clean up curly braces
    html = html.replace(/\{(.*?)\}/g, '$1');


    // Normalize excessive newlines (3+ newlines → 2 newlines)
    html = html.replace(/\n{3,}/g, '\n\n');

    // Remove newlines immediately after block elements (they already have spacing)
    html = html.replace(/(<\/(ul|ol|li|h[1-6]|blockquote|pre|table|hr)>)\n+/g, '$1\n');

    // Remove newlines immediately before block elements
    html = html.replace(/\n+(<(ul|ol|h[1-6]|blockquote|pre|table|hr))/g, '\n$1');

    // Convert double newlines to paragraph breaks (for actual paragraph separation)
    html = html.replace(/\n\n/g, '</p><p>');

    // Convert remaining single newlines to <br /> only for inline content
    // Don't add <br /> right before or after block elements
    html = html.replace(/\n(?!<)/g, '<br />');
    html = html.replace(/\n/g, '');

    // Clean up empty paragraphs and extra spacing
    html = html.replace(/<p>\s*<\/p>/g, '');
    html = html.replace(/<br \/>\s*<br \/>/g, '<br />');
    html = html.replace(/<p>\s*<br \/>/g, '<p>');
    html = html.replace(/<br \/>\s*<\/p>/g, '</p>');
    // Wrap in paragraph if not starting with block element
    if (!/^<(h[1-6]|ul|ol|pre|blockquote|table|hr|p)/i.test(html.trim())) {
      html = `<p>${html}</p>`;
    }

    return html;
  };
  const initializeTheme = () => {
    const savedTheme = Cookies.get("isDarkMode");
    // Check if there's a saved theme preference
    if (savedTheme !== undefined) {
      return savedTheme === "true";
    }
    // If no saved preference, check system preference
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  };

  // Update the theme state to use the prop value
  const [isDarkModeInternal, setIsDarkModeInternal] = useState(
    initializeTheme()
  );

  useEffect(() => {
    const savedTheme = Cookies.get("isDarkMode");
    if (savedTheme !== undefined) {
      setIsDarkModeInternal(savedTheme === "true");
    }
  }, []);

  // Sync internal state with prop value
  useEffect(() => {
    setIsDarkModeInternal(isDarkMode);
  }, [isDarkMode]);

  // Add these theme objects
  const theme = {
    light: {
      header: "#1a73e8",
      background: "#ffffff",
      messageBackground: "#fff",
      // messageBackground: "#F4F4F5",
      text: "#333333",
      // userMessage: "#F4F4F5",
      userMessage: "#18181B",
      // userText: "#333333",
      userText: "white",
      inputBackground: "white",
      inputText: "#333333",
      inputBorder: "#e1e1e1",
      copyButtonColor: "#666666",
      codeBackground: "#f0f0f0",
      codeText: "#333333",
      buttonHover: "rgba(0,0,0,0.1)",
      boxShadow: "none",
      // boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
      divider: "#e1e1e1",
    },
    dark: {
      header: "#1a1a1a",
      background: "#2d2d2d",
      messageBackground: "#3d3d3d",
      text: "#ffffff",
      userMessage: "#1a73e8",
      userText: "white",
      inputBackground: "#2d2d2d",
      inputText: "#ffffff",
      inputBorder: "#4d4d4d",
      copyButtonColor: "#999999",
      codeBackground: "#2b2b2b",
      codeText: "#ffffff",
      buttonHover: "rgba(255,255,255,0.1)",
      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
      divider: "#4d4d4d",
    },
  };

  const handleAudioRecorded = (audioUrl) => {
    // setMessages(prev => [
    //   ...prev,
    //   {
    //     text: '',
    //     isUser: true,
    //     audioUrl: audioUrl
    //   }
    // ])
  };

  // Fetch session draft when component mounts or chatSessionId/userId changes
  useEffect(() => {
    const fetchSessionDraft = async () => {
      if (chatSessionId && userId) {
        const key = `session_${chatSessionId}_${userId}`;
        const draft = await getDraft(key);
        if (draft) setInputText(draft);
      }
    };
    fetchSessionDraft();
  }, [chatSessionId, userId]);

  // Restore user query from localStorage after 401 auth error refresh
  useEffect(() => {
    const authErrorDraftQuery = localStorage.getItem("auth_error_draft_query");
    if (authErrorDraftQuery) {
      setInputText(authErrorDraftQuery);
      localStorage.removeItem("auth_error_draft_query");
    }
  }, []);

  useEffect(() => {
    if (!cancelAudio) {
      handleSendMessage();
    }
    setCancelAudio(false);
  }, [audioInput]);
  useEffect(() => {
    let setAsistantID = async () => {
      if (chatSessionType === "chatbot") {
        const { data, error } = await supabase
          .from("custom_chat_bot")
          .select("assistant_id")
          .eq("session_id", chatSessionId)
          .single();

        if (error) {
          console.error("Error fetching assistant ID:", error);
        } else {
          setChatAsistantId(data.assistant_id);
        }
      }
    };
    if (chatSessionId) {
      setAsistantID();
    }
  }, [chatSessionType]);
  const handleEditMessage = (messageText) => {
    // Convert HTML to plain text while preserving sensible newlines
    const stripHtml = (html) => {
      const div = document.createElement("div");
      div.innerHTML = html || "";

      // Add newlines after block elements to preserve formatting
      ["br", "p", "div", "li", "h1", "h2", "h3", "h4", "h5", "h6"].forEach(
        (tag) => {
          Array.from(div.getElementsByTagName(tag)).forEach((el) => {
            el.appendChild(document.createTextNode("\n"));
          });
        }
      );

      let text = div.textContent || div.innerText || "";
      // Normalize spaces/newlines
      text = text.replace(/\u00A0/g, " ").replace(/\r/g, "");
      text = text.replace(/\n\s*\n+/g, "\n\n").trim();
      return text;
    };

    let plain = stripHtml(messageText || "");

    // Remove @Action: prefix if exists (e.g., "@Notion: what is notion" -> " what is notion")
    // Pattern matches @ActionName: at the start of the text, adds space in front
    plain = plain.replace(/^@[A-Za-z]+:\s*/, " ");

    const textarea = textareaRef?.current;

    if (!textarea) {
      // fallback: replace entire input
      setInputText(plain);
      return;
    }

    // Determine selection positions (use current textarea selection)
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;

    setInputText((prev) => {
      const before = prev.slice(0, start);
      const after = prev.slice(end);
      const newValue = before + plain + after;

      // Focus and set caret after insertion on next tick
      setTimeout(() => {
        if (textarea) {
          const caretPos = before.length + plain.length;
          textarea.focus();
          try {
            textarea.setSelectionRange(caretPos, caretPos);
          } catch (err) {
            // ignore if element not focusable
          }
        }
      }, 0);

      return newValue;
    });
  };

  const handleNewSession = (parentId = null) => {
    setIsNewSession(true);
    setSessionToRename(parentId);
    setShowFormPopup(true);
  };

  const confirmRename = async (newName, setError) => {
    if (!newName.trim()) {
      setError("Topic name cannot be blank.");
      return;
    }
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (session) {
      let createdSession;
      try {
        // Use correct type: "topic" for new topics, "sub_topic" for subtopics
        const sessionType = sessionToRename ? "sub_topic" : "topic";
        createdSession = await createChatSession(
          sessionType,
          newName,
          session.user.id,
          null,
          sessionToRename,
          false
        );
        setChatSessionId(createdSession[0].id);
        setChatName(createdSession[0].name);
        setChatSessionType(sessionType);
        setChatSessionParentId(sessionToRename);

        // Get the initial message from the user input
        await firstMessage(
          sessionCurrentMessage,
          null,
          createdSession[0].id,
          createdSession[0].name
        );
        await storeChatHistory(
          createdSession[0].id,
          sessionCurrentMessage,
          "text",
          false,
          null,
          null,
          userId
        );

        try {
          await fetch(zoomeyApiUrl + "/data_store_memory", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiToken}`,
            },
            body: JSON.stringify({
              content: sessionCurrentMessage,
              session_id: createdSession[0].id,
              user_id: userId,
            }),
          });

          // Call restore_context API and wait for response
          try {
            await fetch(zoomeyApiUrl + "/restore_context", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiToken}`,
              },
              body: JSON.stringify({
                session_id: createdSession[0].id,
                user_id: userId,
              }),
            });
          } catch (err) {
            console.error("Error restoring context:", err);
          }

          // Reload page after restore_context completes
          if (!Cookies.get("is_call")) {
            console.log("Creating new session:", createdSession[0]);
            Cookies.set("chat_session_id", createdSession[0].id);
            window.location.reload();
          }
        } catch (error) {
          console.error("Error storing memory:", error);
          setShowFormPopup(false);
          setSessionToRename(null);
          setIsNewSession(false);
        }
      } catch (err) {
        console.error("Error creating new session:", err);
        setError("An error occurred while creating the topic.");
        setShowFormPopup(false);
        setSessionToRename(null);
        setIsNewSession(false);
      }
    }
  };

  const handleDropdownClick = (type, message) => {
    setSessionCurrentMessage(message);
    if (type === "side" || type === "topic") {
      handleNewSession();
    } else if (type === "child" || type === "sub_topic") {
      handleNewSession(chatSessionParentId);
    }
  };

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // Page reload hone par update query chalegi
  useEffect(() => {
    const updateWebColumn = async () => {
      if (chatSessionId) {
        console.log("Page reloaded - web column set to false", chatSessionId);
        await supabase
          .from("chat_session")
          .update({ web: false })
          .eq("id", chatSessionId)
          .eq("user_auth", userId);
      }
    };
    updateWebColumn();
  }, [chatSessionId]);

  useEffect(() => {
    const fetchWebColumn = async () => {
      if (chatSessionId) {
        const { data, error } = await supabase
          .from("chat_session")
          .select("web")
          .eq("id", chatSessionId)
          .eq("user_auth", userId)
          .single();
        if (data && data.web === true) {
          setShowRefreshButton(true);
          setAnimateSyncBtn(true);
          setTimeout(() => setAnimateSyncBtn(false), 1000); // Slide-up duration
        } else {
          setShowRefreshButton(false);
          setAnimateSyncBtn(false);
        }
      }
    };
    fetchWebColumn();
    // console.log("onResetSessionBtn changed:", onResetSessionBtn);
  }, [onResetSessionBtn, chatSessionId]);

  const handleRefreshClick = async () => {
    if (chatSessionId) {
      // console.log(
      //   "Refresh button clicked - web column set to false",
      //   chatSessionId
      // );
      window.location.reload(); // Chat area reload
    }
  };

  const handleQuickIdeaSaved = () => {
    setQuickIdeaToast("Ideas saved successfully.");

    if (quickIdeaToastTimerRef.current) {
      window.clearTimeout(quickIdeaToastTimerRef.current);
    }

    quickIdeaToastTimerRef.current = window.setTimeout(() => {
      setQuickIdeaToast(null);
      quickIdeaToastTimerRef.current = null;
    }, 2800);
  };

  const handleChange = async (e) => {
    const value = e.target.value;
    setInputText(value);
    // Remove auto-resize logic - user will manually resize

    if (value.includes("@")) {
      // Check if the input contains '@' anywhere
      const caretPosition = e.target.selectionStart;
      const textBeforeCaret = value.slice(0, caretPosition);
      const lastAtIndex = textBeforeCaret.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        const query = textBeforeCaret.slice(lastAtIndex + 1); // Get the text after @

        // Filter action list based on query
        const filteredActions = Object.keys(actionLists).filter((action) =>
          action.toLowerCase().includes(query.toLowerCase())
        );
        console.log("filteredActions", filteredActions);
        // Show suggestions if any matching action found
        if (filteredActions.length > 0) {
          setMentions(filteredActions);
          setShowMentions(true);
          // Set the position of the mention popover near the caret
          setMentionPosition({
            top: e.target.offsetTop + e.target.scrollTop + 30, // Position below the textarea
            left: e.target.offsetLeft + caretPosition * 8, // Adjust horizontally
          });
          setTimeout(() => {
            if (e.target) {
              e.target.focus(); // Focus the textarea when ShowMentions is true
            }
          }, 0); // Delay to ensure the textarea is rendered before focusing
        } else {
          setShowMentions(false);
        }
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }

    // Session draft save
    if (chatSessionId && userId) {
      const key = `session_${chatSessionId}_${userId}`;
      await saveDraft(key, value);
    }
  };
  const handleClosePopup = () => {
    setShowFormPopup(false);
  };
  const scrollToTop = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = 10;
    }
  };
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
      // When explicitly scrolling to bottom, re-enable auto-scroll
      if (autoScrollingRef) autoScrollingRef.current = true;
      setAutoScrolling(true);
      setIsUserManuallyScroll(false);
    }
  };

  // ...existing code...
  if (!sessionLoaded) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          // center only inside the existing chat area (not full page)
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "calc(100vh - 11.5vh)", // match main chat area height used elsewhere
          padding: 24,
          background: "transparent",
        }}
      >
        <div
          style={{
            textAlign: "center",
            maxWidth: 520,
            padding: "24px 28px",
            borderRadius: 12,
            display: "inline-block",
          }}
        >
          <div
            style={{
              textAlign: "center",
              marginTop: "20px",
              color: "var(--teal-bg)",
            }}
          >
            <div className="loading-wave-container">
              <div className="loading-wave"></div>
              <div className="loading-wave"></div>
              <div className="loading-wave"></div>
              <div className="loading-wave"></div>
              <div className="loading-wave"></div>
            </div>
          </div>

          {/* <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--text, #111)",
              marginBottom: 8,
            }}
          >
            Preparing your chat…
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--muted, #666)",
              lineHeight: 1.5,
            }}
          >
            Preparing your session and loading recent messages. This usually
            takes a few seconds — thank you for your patience.
          </div> */}
        </div>
      </div>
    );
  }
  // ...existing code...

  return (
    <>
      <Modal show={showPdf} onHide={() => setShowPdf(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>PDF Viewer</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: 0 }}>
          <iframe
            src={pdfUrl}
            style={{ width: "100%", height: "80vh", border: "none" }}
          ></iframe>
        </Modal.Body>
      </Modal>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        style={{
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        }}
      >
        <Modal.Body className="p-0 position-relative">
          {/* Close Button in the Top Right of the Screen */}
          <i
            className="bi bi-x"
            onClick={() => setShow(false)}
            style={{
              position: "fixed", // Makes it fixed on the screen
              top: "10px",
              right: "10px",
              color: "white",
              fontSize: "54px",
              zIndex: 1050, // Ensures it's above the modal
              cursor: "pointer",
            }}
          ></i>

          {/* Image */}
          <img
            src={selectedImage}
            alt="Preview"
            style={{ width: "100%", height: "auto", borderRadius: "8px" }}
          />
        </Modal.Body>
      </Modal>
      {/* <div style={{ display: "flex", height: "calc(100vh - 11.3vh)" }}> */}
      {/* <div style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}> */}
      <div
        style={{
          width: "100%",
          height: "calc(100vh - 11.5vh)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          // position: "relative",
        }}
      >
        {/* Chat Area */}
        {(() => {
          console.log("Render check:", {
            isMessagesLoading,
            messagesLength: messages.length,
          });
          return isMessagesLoading ? (
            // Loader show karo
            <div className="chat-loader">
              <div className="loading-dots">
                <div>Loading...</div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <WelcomeChatbot
              inputText={inputText}
              setInputText={setInputText}
              handleSendMessage={handleSendMessage}
              userId={userId}
              userName={userName}
            />
          ) : (
            <>
              <div
                ref={chatContainerRef}
                style={{
                  background: "var(--chatbot-bg)",
                  position: "relative",
                }}
                onScroll={() => handleScroll()}
                className="chat-container"
                // IsSidebardNotificationVisible
              >
              {/* {
              ScrollToBottomVar ? (
                <div style={{
                  width: '40px',
                  height: '40px',
                  left:'95%',
                  borderRadius: '50%',
                  border: 'none',
                  background: isDarkModeInternal?'#1A73E8':'#18181B',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  opacity: connecting || isLoading ? 0.5 : 1,
                  disabled: (isLoading || previewFile) !== null
                }}
                onClick={() => scrollToBottom()}
                >
                <i className="bi bi-chevron-down"></i>

            </div>
              ) : null
            } */}
              {/* loadingMore */}
              {/* Loading Indicator - Shows only when loading */}
              {loadingMore && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  <div
                    style={{
                      background: "var(--teal-bg)",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      padding: "10px 15px",
                      gap: "8px",
                      borderRadius: "0.375rem",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--textwhite, #666)",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Loading chats...
                    </span>
                  </div>
                </div>
              )}

              {messages.map((message, index) => {
                const isLastZoomey =
                  !message.isUser &&
                  index === messages.length - 1 &&
                  isStreaming;

                return (
                  <div
                    key={index}
                    ref={index === 0 ? topMessageRef : null} // Track the first message
                    style={{
                      display: "flex",
                      justifyContent: message.isUser
                        ? "flex-end"
                        : "flex-start",
                      marginBottom: "12px",
                      width: "100%",
                      zIndex: message.isUser ? "none" : "1",
                      cursor:
                        message.isUser && message.isQueue
                          ? "pointer"
                          : "default",
                    }}
                    className={`chat-container-map${message.isUser ? ' chat-user-msg' : ''}`}
                    onClick={() => {
                      if (message.isUser && message.isQueue) {
                        onOpenQueueModal(message.isQueue, chatName);
                      }
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        // background: message.audioUrl
                        //   ? "transparent"
                        //   : message.isUser
                        //   ? theme[isDarkModeInternal ? "dark" : "light"].userMessage
                        //   : theme[isDarkModeInternal ? "dark" : "light"]
                        //       .messageBackground,
                        background: message.audioUrl
                          ? "transparent"
                          : message.isUser
                          ? "var(--teal-bg)"
                          : "",
                        color: message.isUser
                          ? "var(--textwhite)"
                          : "var(--text)",
                        // color: message.isUser
                        //   ? theme[isDarkModeInternal ? "dark" : "light"].userText
                        //   : theme[isDarkModeInternal ? "dark" : "light"].text,
                        padding: message.audioUrl ? "0" : "12px 16px",
                        borderRadius: "12px",
                        width: message.isUser ? "auto" : "100%",
                        maxWidth: message.isUser ? "70%" : "100%",
                        marginTop: message.isUser ? "10px" : "45px",
                        boxShadow: message.audioUrl
                          ? "none"
                          : theme[isDarkModeInternal ? "dark" : "light"]
                              .boxShadow,
                        border: "none",
                        outline: "none",
                        wordBreak: "break-word",
                      }}
                    >
                      {message.audioUrl ? (
                        <audio controls src={message.audioUrl}></audio>
                      ) : (
                        <>
                          {message.isUser ? (
                            ""
                          ) : (
                            <>
                              <span className="chatbot_z_logo">Z</span>
                            </>
                          )}
                          <span
                            className="zoomey-title"
                            style={{ fontWeight: "bold" }}
                          >
                            {message.isUser ? "" : "Zoomey"}
                          </span>

                          {message.preview && message.fileType == "image" && (
                            <div
                              style={{
                                marginBottom: "8px",
                                position: "relative",
                              }}
                            >
                              <img
                                src={message.preview}
                                alt={message.fileName}
                                style={{
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                  maxHeight: "200px",
                                  objectFit: "contain",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleShow(message.preview)}
                              />
                              {message.isPending && (
                                <button
                                  onClick={handleRemovePendingFile}
                                  style={{
                                    position: "absolute",
                                    top: "4px",
                                    right: "4px",
                                    background: "rgba(0,0,0,0.5)",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "24px",
                                    height: "24px",
                                    color: "white",
                                    cursor: "pointer",
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          )}
                          {message.fileType && message.fileType !== "image" && (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: message.text ? "8px" : "0",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleShowPdf(message.preview)}
                              >
                                {getFileIcon(message.fileName, 24)}
                                {message.fileName}
                              </div>
                            </>
                          )}

                          {!message.isUser && !message.isPending && (
                            <>
                              <button
                                onClick={() => {
                                  let textToCopy = message.text;
                                  const selectedText = window
                                    .getSelection()
                                    .toString();

                                  if (selectedText) {
                                    // If text is selected, copy only the selected text
                                    textToCopy = selectedText;
                                  }
                                  textToCopy = textToCopy
                                    // Extract headings
                                    .replace(
                                      /<h3>(.*?)<\/h3>/gi,
                                      (match, content) =>
                                        `\n${content
                                          .replace(/<[^>]+>/g, "")
                                          .trim()}\n`
                                    )
                                    .replace(
                                      /<h4>(.*?)<\/h4>/gi,
                                      (match, content) =>
                                        `\n${content
                                          .replace(/<[^>]+>/g, "")
                                          .trim()}\n`
                                    )
                                    .replace(
                                      /<h5>(.*?)<\/h5>/gi,
                                      (match, content) =>
                                        `\n${content
                                          .replace(/<[^>]+>/g, "")
                                          .trim()}\n`
                                    )
                                    // Paragraphs and line breaks
                                    .replace(/<p>(.*?)<\/p>/gi, "$1\n")
                                    .replace(/<br\s*\/?>/gi, "\n")
                                    // Extract bold text
                                    .replace(/<strong>(.*?)<\/strong>/gi, "$1")
                                    // Handle code blocks
                                    .replace(/<code[^>]*>(.*?)<\/code>/gi, "$1")

                                    // Handle ordered lists with "start" attribute
                                    .replace(
                                      /<ol[^>]*start="(\d+)"[^>]*>(.*?)<\/ol>/gis,
                                      (match, start, content) => {
                                        const items =
                                          content.match(/<li>(.*?)<\/li>/gi) ||
                                          [];
                                        return (
                                          items
                                            .map(
                                              (item, index) =>
                                                `${+start + index}. ${item
                                                  .replace(/<\/?li>/gi, "")
                                                  .trim()}`
                                            )
                                            .join("\n") + "\n"
                                        );
                                      }
                                    )

                                    // Handle nested ordered lists inside <li>
                                    .replace(
                                      /<li>(.*?)<ol[^>]*start="(\d+)"[^>]*>(.*?)<\/ol>(.*?)<\/li>/gis,
                                      (
                                        match,
                                        beforeOl,
                                        nestedStart,
                                        nestedOlContent,
                                        afterOl
                                      ) => {
                                        const nestedItems =
                                          nestedOlContent.match(
                                            /<li>(.*?)<\/li>/gi
                                          ) || [];
                                        const nestedList = nestedItems
                                          .map(
                                            (item, index) =>
                                              `    ${
                                                +nestedStart + index
                                              }. ${item
                                                .replace(/<\/?li>/gi, "")
                                                .trim()}`
                                          )
                                          .join("\n");
                                        return `${beforeOl.trim()}\n${nestedList}\n${afterOl.trim()}`;
                                      }
                                    )

                                    // Handle nested ordered lists without "start" attribute inside <li>
                                    .replace(
                                      /<li>(.*?)<ol[^>]*>(.*?)<\/ol>(.*?)<\/li>/gis,
                                      (
                                        match,
                                        beforeOl,
                                        nestedOlContent,
                                        afterOl
                                      ) => {
                                        const nestedItems =
                                          nestedOlContent.match(
                                            /<li>(.*?)<\/li>/gi
                                          ) || [];
                                        const nestedList = nestedItems
                                          .map(
                                            (item, index) =>
                                              `    ${index + 1}. ${item
                                                .replace(/<\/?li>/gi, "")
                                                .trim()}`
                                          )
                                          .join("\n");
                                        return `${beforeOl.trim()}\n${nestedList}\n${afterOl.trim()}`;
                                      }
                                    )

                                    // Handle ordered lists without "start" attribute
                                    .replace(
                                      /<ol[^>]*>(.*?)<\/ol>/gis,
                                      (match, content) => {
                                        const items =
                                          content.match(/<li>(.*?)<\/li>/gi) ||
                                          [];
                                        return (
                                          items
                                            .map(
                                              (item, index) =>
                                                `${index + 1}. ${item
                                                  .replace(/<\/?li>/gi, "")
                                                  .trim()}`
                                            )
                                            .join("\n") + "\n"
                                        );
                                      }
                                    )

                                    // Handle unordered lists
                                    .replace(
                                      /<ul[^>]*>(.*?)<\/ul>/gis,
                                      (match, content) => {
                                        const items =
                                          content.match(/<li>(.*?)<\/li>/gi) ||
                                          [];
                                        return (
                                          items
                                            .map(
                                              (item) =>
                                                `- ${item
                                                  .replace(/<\/?li>/gi, "")
                                                  .trim()}`
                                            )
                                            .join("\n") + "\n"
                                        );
                                      }
                                    )

                                    // Handle list items <li>
                                    .replace(/<li>(.*?)<\/li>/gi, "- $1")

                                    // Remove any remaining HTML tags
                                    .replace(/<[^>]+>/g, "")
                                    // Normalize newlines
                                    .replace(/\n\s*\n/g, "\n\n")
                                    .replace(/^\s+|\s+$/g, "")
                                    .trim();

                                  // Remove any non-breaking spaces and replace with regular spaces
                                  textToCopy = textToCopy.replace(
                                    /\u00A0/g,
                                    " "
                                  );

                                  navigator.clipboard.writeText(textToCopy);
                                }}
                                style={{
                                  position: "sticky",
                                  top: "12px",
                                  right: "12px",
                                  float: "right",
                                  border: "none",
                                  background: "none",
                                  width: "30px",
                                  height: "30px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color:
                                    theme[isDarkModeInternal ? "dark" : "light"]
                                      .copyButtonColor,
                                  opacity: 0.7,
                                  transition: "opacity 0.2s",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.opacity = 1)
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.opacity = 0.7)
                                }
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {" "}
                                  {isPlayingId == index && isPlaying ? (
                                    <i
                                      className="bi bi-stop-fill"
                                      onClick={async () =>
                                        playAudio(index, message.text, "stop")
                                      }
                                    ></i>
                                  ) : (
                                    <i
                                      className="bi bi-play-fill"
                                      onClick={async () =>
                                        playAudio(index, message.text, "play")
                                      }
                                      disabled={
                                        isPlayingId !== null &&
                                        isPlayingId != index
                                      }
                                    ></i>
                                  )}
                                  <i
                                    className="bi bi-pencil-fill"
                                    style={{
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      marginLeft: "8px",
                                      marginRight: "8px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditMessage(message.text);
                                    }}
                                  ></i>
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                  </svg>
                                  <i
                                    className="dropdownClass bi bi-three-dots-vertical nav-link dropdown-toggle"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    style={{ cursor: "pointer" }}
                                  ></i>
                                  <ul
                                    className="dropdown-menu"
                                    aria-labelledby="navbarDropdown"
                                    style={{ opacity: "1 !important" }}
                                  >
                                    {chatSessionType === "side" ? (
                                      <>
                                        <li
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleDropdownClick(
                                              "side",
                                              message.text
                                            )
                                          }
                                        >
                                          <i className="bi bi-plus-square-dotted"></i>{" "}
                                          New Topic
                                        </li>
                                        <li>
                                          <hr className="dropdown-divider" />
                                        </li>
                                        <li
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleDropdownClick(
                                              "child",
                                              message.text
                                            )
                                          }
                                        >
                                          <i className="bi bi-plus-square-dotted"></i>{" "}
                                          New Subtopic
                                        </li>
                                        <li>
                                          <hr className="dropdown-divider" />
                                        </li>
                                        <li
                                          className="dropdown-item"
                                          onClick={() => {
                                            setShowAddToSessionModal(true);
                                            setAddToChat(message.text);
                                          }}
                                        >
                                          {" "}
                                          <i className="bi bi-plus-square-dotted"></i>{" "}
                                          Add to
                                        </li>
                                      </>
                                    ) : (
                                      <>
                                        <li
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleDropdownClick(
                                              "side",
                                              message.text
                                            )
                                          }
                                        >
                                          <i className="bi bi-plus-square-dotted"></i>{" "}
                                          New Topic
                                        </li>
                                        <li>
                                          <hr className="dropdown-divider" />
                                        </li>
                                        <li
                                          className="dropdown-item"
                                          onClick={() => {
                                            setShowAddToSessionModal(true);
                                            setAddToChat(message.text);
                                          }}
                                        >
                                          {" "}
                                          <i className="bi bi-plus-square-dotted"></i>{" "}
                                          Add to
                                        </li>
                                      </>
                                    )}
                                  </ul>
                                </div>
                              </button>
                            </>
                          )}
                          {message.isUser && (
                            <>
                              <button
                                style={{
                                  position:
                                    message.fileType == "image"
                                      ? "absolute"
                                      : "sticky",
                                  float: "right",
                                  top: "15px",
                                  marginTop: "5px",
                                  right: "10px",
                                  gap: "8px",
                                  display: "flex",
                                  padding: 0,
                                  background: "transparent",
                                  color: "white",
                                  border: "none",
                                  // /* cursor: pointer;
                                }}
                              >
                                {message.preview &&
                                message.text.trim() == "" ? null : (
                                  <>
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      onClick={() => {
                                        let textToCopy = message.text;
                                        const selectedText = window
                                          .getSelection()
                                          .toString();

                                        if (selectedText) {
                                          // If text is selected, copy only the selected text
                                          textToCopy = selectedText;
                                        }
                                        textToCopy = textToCopy
                                          // Extract headings
                                          .replace(
                                            /<h3>(.*?)<\/h3>/gi,
                                            (match, content) =>
                                              `\n${content
                                                .replace(/<[^>]+>/g, "")
                                                .trim()}\n`
                                          )
                                          .replace(
                                            /<h4>(.*?)<\/h4>/gi,
                                            (match, content) =>
                                              `\n${content
                                                .replace(/<[^>]+>/g, "")
                                                .trim()}\n`
                                          )
                                          .replace(
                                            /<h5>(.*?)<\/h5>/gi,
                                            (match, content) =>
                                              `\n${content
                                                .replace(/<[^>]+>/g, "")
                                                .trim()}\n`
                                          )
                                          // Paragraphs and line breaks
                                          .replace(/<p>(.*?)<\/p>/gi, "$1\n")
                                          .replace(/<br\s*\/?>/gi, "\n")
                                          // Extract bold text
                                          .replace(
                                            /<strong>(.*?)<\/strong>/gi,
                                            "$1"
                                          )
                                          // Handle code blocks
                                          .replace(
                                            /<code[^>]*>(.*?)<\/code>/gi,
                                            "$1"
                                          )

                                          // Handle ordered lists with "start" attribute
                                          .replace(
                                            /<ol[^>]*start="(\d+)"[^>]*>(.*?)<\/ol>/gis,
                                            (match, start, content) => {
                                              const items =
                                                content.match(
                                                  /<li>(.*?)<\/li>/gi
                                                ) || [];
                                              return (
                                                items
                                                  .map(
                                                    (item, index) =>
                                                      `${+start + index}. ${item
                                                        .replace(
                                                          /<\/?li>/gi,
                                                          ""
                                                        )
                                                        .trim()}`
                                                  )
                                                  .join("\n") + "\n"
                                              );
                                            }
                                          )

                                          // Handle nested ordered lists inside <li>
                                          .replace(
                                            /<li>(.*?)<ol[^>]*start="(\d+)"[^>]*>(.*?)<\/ol>(.*?)<\/li>/gis,
                                            (
                                              match,
                                              beforeOl,
                                              nestedStart,
                                              nestedOlContent,
                                              afterOl
                                            ) => {
                                              const nestedItems =
                                                nestedOlContent.match(
                                                  /<li>(.*?)<\/li>/gi
                                                ) || [];
                                              const nestedList = nestedItems
                                                .map(
                                                  (item, index) =>
                                                    `    ${
                                                      +nestedStart + index
                                                    }. ${item
                                                      .replace(/<\/?li>/gi, "")
                                                      .trim()}`
                                                )
                                                .join("\n");
                                              return `${beforeOl.trim()}\n${nestedList}\n${afterOl.trim()}`;
                                            }
                                          )

                                          // Handle nested ordered lists without "start" attribute inside <li>
                                          .replace(
                                            /<li>(.*?)<ol[^>]*>(.*?)<\/ol>(.*?)<\/li>/gis,
                                            (
                                              match,
                                              beforeOl,
                                              nestedOlContent,
                                              afterOl
                                            ) => {
                                              const nestedItems =
                                                nestedOlContent.match(
                                                  /<li>(.*?)<\/li>/gi
                                                ) || [];
                                              const nestedList = nestedItems
                                                .map(
                                                  (item, index) =>
                                                    `    ${index + 1}. ${item
                                                      .replace(/<\/?li>/gi, "")
                                                      .trim()}`
                                                )
                                                .join("\n");
                                              return `${beforeOl.trim()}\n${nestedList}\n${afterOl.trim()}`;
                                            }
                                          )

                                          // Handle ordered lists without "start" attribute
                                          .replace(
                                            /<ol[^>]*>(.*?)<\/ol>/gis,
                                            (match, content) => {
                                              const items =
                                                content.match(
                                                  /<li>(.*?)<\/li>/gi
                                                ) || [];
                                              return (
                                                items
                                                  .map(
                                                    (item, index) =>
                                                      `${index + 1}. ${item
                                                        .replace(
                                                          /<\/?li>/gi,
                                                          ""
                                                        )
                                                        .trim()}`
                                                  )
                                                  .join("\n") + "\n"
                                              );
                                            }
                                          )

                                          // Handle unordered lists
                                          .replace(
                                            /<ul[^>]*>(.*?)<\/ul>/gis,
                                            (match, content) => {
                                              const items =
                                                content.match(
                                                  /<li>(.*?)<\/li>/gi
                                                ) || [];
                                              return (
                                                items
                                                  .map(
                                                    (item) =>
                                                      `- ${item
                                                        .replace(
                                                          /<\/?li>/gi,
                                                          ""
                                                        )
                                                        .trim()}`
                                                  )
                                                  .join("\n") + "\n"
                                              );
                                            }
                                          )

                                          // Handle list items <li>
                                          .replace(/<li>(.*?)<\/li>/gi, "- $1")

                                          // Remove any remaining HTML tags
                                          .replace(/<[^>]+>/g, "")
                                          // Normalize newlines
                                          .replace(/\n\s*\n/g, "\n\n")
                                          .replace(/^\s+|\s+$/g, "")
                                          .trim();

                                        // Remove any non-breaking spaces and replace with regular spaces
                                        textToCopy = textToCopy.replace(
                                          /\u00A0/g,
                                          " "
                                        );

                                        navigator.clipboard.writeText(
                                          textToCopy
                                        );
                                      }}
                                      style={{
                                        cursor: "pointer",
                                      }}
                                    >
                                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                    </svg>
                                    <i
                                      className="bi bi-pencil-fill"
                                      style={{
                                        fontSize: "12px",
                                        cursor: "pointer",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevents triggering the copy action
                                        handleEditMessage(message.text);
                                      }}
                                    ></i>
                                  </>
                                )}
                              </button>
                            </>
                          )}
                          {message.isHtml ? (
                            <>
                              {/* <div
                          style={{ paddingRight: '40px' }}
                          dangerouslySetInnerHTML={{ __html: message.text }}
                          /> */}
                              {message.isUser ? (
                                <>
                                  {/* Check for embedded Attachment_URL in text and render image/file */}
                                  {(() => {
                                    const attachment = extractAttachmentFromText(message.text);
                                    if (attachment && attachment.fileType === 'image') {
                                      return (
                                        <>
                                          <div
                                            style={{
                                              marginBottom: "8px",
                                              position: "relative",
                                            }}
                                          >
                                            <img
                                              src={attachment.url}
                                              alt="Attachment"
                                              style={{
                                                maxWidth: "100%",
                                                borderRadius: "8px",
                                                maxHeight: "200px",
                                                objectFit: "contain",
                                                cursor: "pointer",
                                              }}
                                              onClick={() => handleShow(attachment.url)}
                                            />
                                          </div>
                                          {attachment.cleanText && (
                                            <div style={{ paddingRight: "50px" }}>
                                              {message.isQueue && (
                                                <>
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    className="bi bi-leaf"
                                                    viewBox="0 0 16 16"
                                                  >
                                                    <path d="M1.4 1.7c.216.289.65.84 1.725 1.274 1.093.44 2.884.774 5.834.528l.37-.023c1.823-.06 3.117.598 3.956 1.579C14.16 6.082 14.5 7.41 14.5 8.5c0 .58-.032 1.285-.229 1.997q.198.248.382.54c.756 1.2 1.19 2.563 1.348 3.966a1 1 0 0 1-1.98.198c-.13-.97-.397-1.913-.868-2.77C12.173 13.386 10.565 14 8 14c-1.854 0-3.32-.544-4.45-1.435-1.125-.887-1.89-2.095-2.391-3.383C.16 6.62.16 3.646.509 1.902L.73.806zm-.05 1.39c-.146 1.609-.008 3.809.74 5.728.457 1.17 1.13 2.213 2.079 2.961.942.744 2.185 1.22 3.83 1.221 2.588 0 3.91-.66 4.609-1.445-1.789-2.46-4.121-1.213-6.342-2.68-.74-.488-1.735-1.323-1.844-2.308-.023-.214.237-.274.38-.112 1.4 1.6 3.573 1.757 5.59 2.045 1.227.215 2.21.526 3.033 1.158.058-.39.075-.782.075-1.158 0-.91-.288-1.988-.975-2.792-.626-.732-1.622-1.281-3.167-1.229l-.316.02c-3.05.253-5.01-.08-6.291-.598a5.3 5.3 0 0 1-1.4-.811" />
                                                  </svg>
                                                  &nbsp;&nbsp;
                                                </>
                                              )}
                                              <span
                                                dangerouslySetInnerHTML={{
                                                  __html: sanitizeHTML(linkifyAndPreserveHTML(
                                                    formatMentionSpacing(attachment.cleanText)
                                                  )),
                                                }}
                                              />
                                            </div>
                                          )}
                                        </>
                                      );
                                    } else if (attachment && attachment.fileType !== 'image') {
                                      // Non-image attachment (PDF, etc.)
                                      return (
                                        <>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              marginBottom: attachment.cleanText ? "8px" : "0",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => handleShowPdf(attachment.url)}
                                          >
                                            {getFileIcon(attachment.url.split('/').pop(), 24)}
                                            <span style={{ fontSize: "14px", opacity: 0.9 }}>
                                              {attachment.url.split('/').pop()?.split('?')[0] || 'Document'}
                                            </span>
                                          </div>
                                          {attachment.cleanText && (
                                            <div style={{ paddingRight: "50px" }}>
                                              {message.isQueue && (
                                                <>
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    className="bi bi-leaf"
                                                    viewBox="0 0 16 16"
                                                  >
                                                    <path d="M1.4 1.7c.216.289.65.84 1.725 1.274 1.093.44 2.884.774 5.834.528l.37-.023c1.823-.06 3.117.598 3.956 1.579C14.16 6.082 14.5 7.41 14.5 8.5c0 .58-.032 1.285-.229 1.997q.198.248.382.54c.756 1.2 1.19 2.563 1.348 3.966a1 1 0 0 1-1.98.198c-.13-.97-.397-1.913-.868-2.77C12.173 13.386 10.565 14 8 14c-1.854 0-3.32-.544-4.45-1.435-1.125-.887-1.89-2.095-2.391-3.383C.16 6.62.16 3.646.509 1.902L.73.806zm-.05 1.39c-.146 1.609-.008 3.809.74 5.728.457 1.17 1.13 2.213 2.079 2.961.942.744 2.185 1.22 3.83 1.221 2.588 0 3.91-.66 4.609-1.445-1.789-2.46-4.121-1.213-6.342-2.68-.74-.488-1.735-1.323-1.844-2.308-.023-.214.237-.274.38-.112 1.4 1.6 3.573 1.757 5.59 2.045 1.227.215 2.21.526 3.033 1.158.058-.39.075-.782.075-1.158 0-.91-.288-1.988-.975-2.792-.626-.732-1.622-1.281-3.167-1.229l-.316.02c-3.05.253-5.01-.08-6.291-.598a5.3 5.3 0 0 1-1.4-.811" />
                                                  </svg>
                                                  &nbsp;&nbsp;
                                                </>
                                              )}
                                              <span
                                                dangerouslySetInnerHTML={{
                                                  __html: sanitizeHTML(linkifyAndPreserveHTML(
                                                    formatMentionSpacing(attachment.cleanText)
                                                  )),
                                                }}
                                              />
                                            </div>
                                          )}
                                        </>
                                      );
                                    }
                                    // No embedded attachment, render text normally
                                    return (
                                      <div style={{ paddingRight: "50px" }}>
                                        {message.isQueue && (
                                          <>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="16"
                                              fill="currentColor"
                                              className="bi bi-leaf"
                                              viewBox="0 0 16 16"
                                            >
                                              <path d="M1.4 1.7c.216.289.65.84 1.725 1.274 1.093.44 2.884.774 5.834.528l.37-.023c1.823-.06 3.117.598 3.956 1.579C14.16 6.082 14.5 7.41 14.5 8.5c0 .58-.032 1.285-.229 1.997q.198.248.382.54c.756 1.2 1.19 2.563 1.348 3.966a1 1 0 0 1-1.98.198c-.13-.97-.397-1.913-.868-2.77C12.173 13.386 10.565 14 8 14c-1.854 0-3.32-.544-4.45-1.435-1.125-.887-1.89-2.095-2.391-3.383C.16 6.62.16 3.646.509 1.902L.73.806zm-.05 1.39c-.146 1.609-.008 3.809.74 5.728.457 1.17 1.13 2.213 2.079 2.961.942.744 2.185 1.22 3.83 1.221 2.588 0 3.91-.66 4.609-1.445-1.789-2.46-4.121-1.213-6.342-2.68-.74-.488-1.735-1.323-1.844-2.308-.023-.214.237-.274.38-.112 1.4 1.6 3.573 1.757 5.59 2.045 1.227.215 2.21.526 3.033 1.158.058-.39.075-.782.075-1.158 0-.91-.288-1.988-.975-2.792-.626-.732-1.622-1.281-3.167-1.229l-.316.02c-3.05.253-5.01-.08-6.291-.598a5.3 5.3 0 0 1-1.4-.811" />
                                            </svg>
                                            &nbsp;&nbsp;
                                          </>
                                        )}
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeHTML(linkifyAndPreserveHTML(
                                              formatMentionSpacing(message.text)
                                            )),
                                          }}
                                        />
                                      </div>
                                    );
                                  })()}
                                </>
                              ) : message.isTyping ? (
                                //   <div style={{ paddingRight: '40px', wordBreak: 'break-word' }}>
                                //   {message.text.split(/(\s+)/).map((part, idx) => {
                                //     // If it's just whitespace (like space, tab, newline), keep it as-is
                                //     if (/^\s+$/.test(part)) {
                                //       return part;
                                //     }

                                //     return (
                                //       <span
                                //         key={idx}
                                //         className="word-fade-in"
                                //         style={{
                                //           animationDelay: `${idx * 0.01}s`,
                                //         }}
                                //         dangerouslySetInnerHTML={{ __html: part }}
                                //       ></span>
                                //     );
                                //   })}
                                // </div>
                                // <div style={{ paddingRight: '40px'  ,animationDelay:' 5s'}}
                                // className="word-fade-in"
                                // >
                                //       {parse(message.text)}
                                //       </div>
                                // <AnimatedText
                                //   messageText={message.text}
                                //   containerRef={chatContainerRef}
                                // />

                                // <Typist
                                //   onTypingDone={handleTypingDone}
                                //   typingDelay={1}
                                //   hideCursorWhenDone={true}
                                //   cursor={<span className={'Cursor--blinking'}>|</span>}
                                // >
                                //   <div style={{ paddingRight: '40px' }}>
                                //     {parse(message.text)}
                                //   </div>
                                // </Typist>
                                <div
                                  style={{
                                    paddingRight: "40px",
                                    marginLeft: message.isUser ? "" : "42px",
                                    paddingTop: message.isUser ? "" : "12px",
                                    marginBottom: message.isUser ? "" : "12px",
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHTML(linkifyAndPreserveHTML(
                                      message.isUser ? message.text : formatMessage(message.text)
                                    )),
                                  }}
                                />
                              ) : message.isA2UI && message.a2uiContent ? (
                                // A2UI Rich Interactive UI Rendering (from SSE event)
                                <>
                                  {/* Show the text response above the form */}
                                  {message.text && (
                                    <div
                                      style={{
                                        paddingRight: "40px",
                                        marginLeft: message.isUser ? "" : "42px",
                                        paddingTop: message.isUser ? "" : "12px",
                                        marginBottom: "12px",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: sanitizeHTML(linkifyAndPreserveHTML(
                                          formatMessage(message.text)
                                        )),
                                      }}
                                    />
                                  )}
                                  {/* Render A2UI form */}
                                  <div
                                    style={{
                                      paddingRight: "40px",
                                      marginLeft: message.isUser ? "" : "42px",
                                      paddingTop: "12px",
                                      marginBottom: message.isUser ? "" : "12px",
                                      width: "100%",
                                    }}
                                  >
                                    <A2UIRenderer
                                      content={message.a2uiContent}
                                      onAction={handleA2UIAction}
                                    />
                                  </div>
                                </>
                              ) : isA2UIContent(message.text) ? (
                                // A2UI Rich Interactive UI Rendering (legacy inline detection)
                                <div
                                  style={{
                                    paddingRight: "40px",
                                    marginLeft: message.isUser ? "" : "42px",
                                    paddingTop: message.isUser ? "" : "12px",
                                    marginBottom: message.isUser ? "" : "12px",
                                    width: "100%",
                                  }}
                                >
                                  <A2UIRenderer
                                    content={message.text}
                                    onAction={handleA2UIAction}
                                  />
                                </div>
                              ) : (
                                // Regular HTML text rendering
                                <>
                                  <div
                                    style={{
                                      paddingRight: "40px",
                                      marginLeft: message.isUser ? "" : "42px",
                                      paddingTop: message.isUser ? "" : "12px",
                                      marginBottom: message.isUser ? "" : "12px",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeHTML(linkifyAndPreserveHTML(
                                        message.isUser ? message.text : formatMessage(message.text)
                                      )),
                                    }}
                                    onClick={() => {
                                      if (
                                        message.text.match(
                                          /<img[^>]*src=["']([^"']+)["']/
                                        )
                                      ) {
                                        handleShow(message.text);
                                      }
                                    }}
                                  />
                                  {/* Generated Image Card - Show when AI generates images */}
                                  {message.generatedImages && message.generatedImages.length > 0 && (
                                    <div style={{ marginLeft: "42px" }}>
                                      <GeneratedImageCard 
                                        images={message.generatedImages}
                                        prompt={message.imagePrompt}
                                      />
                                    </div>
                                  )}
                                </>
                              )}

                              {/* <br /> */}
                              <span
                                className="mt-2"
                                style={{
                                  marginLeft: message.isUser ? "" : "42px",
                                }}
                              >
                                {message.isUser ? "" : message.time}
                              </span>
                            </>
                          ) : (
                            <>
                              <div
                                style={{
                                  paddingRight: "40px",
                                  marginLeft: message.isUser ? "" : "42px",
                                  paddingTop: message.isUser ? "" : "12px",
                                  marginBottom: message.isUser ? "" : "12px",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHTML(linkifyAndPreserveHTML(
                                    message.isUser ? message.text : formatMessage(message.text)
                                  )),
                                }}
                              />

                              {message.time != false ? (
                                <>
                                  <span>{message.time}</span>
                                </>
                              ) : null}
                            </>
                          )}
                          {!message.isUser && (
                            <span className="think_loader">
                              {/* think */}
                              {isLastZoomey && responseInformMessage && (
                                <span
                                  style={{
                                    color: "#888",
                                    fontStyle: "italic",
                                    marginLeft: 8,
                                  }}
                                >
                                  <div
                                    className="loading-dots"
                                    style={{ marginLeft: "6px" }}
                                  >
                                    <div
                                      className="dot"
                                      style={{ right: "-18px" }}
                                    ></div>
                                    <div
                                      className="dot"
                                      style={{ right: "-6px" }}
                                    ></div>
                                    <div
                                      className="dot"
                                      style={{ right: "-12px" }}
                                    ></div>
                                    <div>{responseInformMessage}</div>
                                  </div>
                                </span>
                              )}
                            </span>
                          )}
                          
                          {/* Usage/Token Info Display */}
                          {!message.isUser && message.usageInfo && !isLastZoomey && (
                            <div className="usage-info-badge">
                              <span className="usage-icon">⚡</span>
                              <span className="usage-model">{message.usageInfo.model}</span>
                              <span className="usage-separator">·</span>
                              <span className="usage-tier">{message.usageInfo.tier}</span>
                              {message.usageInfo.tokens && message.usageInfo.tokens.total > 0 && (
                                <>
                                  <span className="usage-separator">·</span>
                                  <span className="usage-tokens">
                                    {message.usageInfo.tokens.input} in · {message.usageInfo.tokens.output} out
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          
                          {/* DEBUG_UI: Collapsible debug details panel */}
                          {!message.isUser && !isLastZoomey && (
                            <div style={{ marginLeft: "42px" }}>
                              <DebugDetailsPanel
                                toolsUsed={message.toolsUsed || []}
                                usageInfo={message.usageInfo}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* Code blocks styling */}
                      <style>
                        {`
                        code {
                          background-color: ${
                            theme[isDarkModeInternal ? "dark" : "light"]
                              .codeBackground
                          } !important;
                          color: ${
                            theme[isDarkModeInternal ? "dark" : "light"]
                              .codeText
                          } !important;
                          padding: 2px 4px;
                          border-radius: 4px;
                          font-family: monospace;
                        }
                      `}
                      </style>
                    </div>
                  </div>
                );
              })}

              {/* {
    Rendered &&  messages.length > 2? (
     <div style={{
       width: '40px',
       height: '40px',
       left:'95%',
       borderRadius: '50%',
       border: 'none',
       background: isDarkModeInternal?'#1A73E8':'#18181B',
       color: 'white',
       cursor: 'pointer',
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       position: 'relative',
       opacity: connecting || isLoading ? 0.5 : 1,
       disabled: (isLoading || previewFile) !== null
     }}
     onClick={() => scrollToTop()}
     >
     <i className="bi bi-chevron-up"></i>

 </div>
    )   :null

     } */}
              {/* Loading indicator */}
              {/* {isLoading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      background:
                        theme[isDarkModeInternal ? "dark" : "light"]
                          .messageBackground,
                      padding: "12px 16px",
                      borderRadius: "12px",
                      boxShadow:
                        theme[isDarkModeInternal ? "dark" : "light"].boxShadow,
                    }}
                  >
                    <div className="loading-dots">
                      <div>Zoomey</div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                </div>
              )} */}

{/* Processing loader removed - redundant with Thinking indicator in message bubble */}
            </div>
            </>
          );
        })()}

        {/* Speaking Indicators */}
        {connected && (
          <div
            style={{
              padding: "12px 16px",
              display: "flex",
              justifyContent: "center",
              background:
                theme[isDarkModeInternal ? "dark" : "light"].background,
            }}
          >
            {assistantIsSpeaking ? (
              // Assistant speaking wave animation
              <div className="audio-wave">
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
              </div>
            ) : (
              // User microphone indicator
              <div className="mic-indicator">
                <div className="mic-dot"></div>
              </div>
            )}
          </div>
        )}

        {/* Add this style block for both animations */}
        <style>
          {`
                .audio-wave {
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  height: 24px;
                  padding: 4px 12px;
                  background: white;
                  border-radius: 12px;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .wave-bar {
                  width: 4px;
                  height: 100%;
                  background: #1a73e8;
                  border-radius: 2px;
                  animation: wave 1.2s ease-in-out infinite;
                }

                .wave-bar:nth-child(1) { animation-delay: 0s; }
                .wave-bar:nth-child(2) { animation-delay: 0.1s; }
                .wave-bar:nth-child(3) { animation-delay: 0.2s; }
                .wave-bar:nth-child(4) { animation-delay: 0.3s; }
                .wave-bar:nth-child(5) { animation-delay: 0.4s; }
                .wave-bar:nth-child(6) { animation-delay: 0.5s; }
                .wave-bar:nth-child(7) { animation-delay: 0.6s; }

                @keyframes wave {
                  0%, 100% {
                    transform: scaleY(0.3);
                  }
                  50% {
                    transform: scaleY(1);
                  }
                }

                .mic-indicator {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  height: 24px;
                  padding: 4px 16px;
                  background: white;
                  border-radius: 12px;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .mic-dot {
                  width: 8px;
                  height: 8px;
                  background: #dc3545;
                  border-radius: 50%;
                  animation: pulse 1.5s ease-in-out infinite;
                }

                @keyframes pulse {
                  0%, 100% {
                    transform: scale(0.8);
                    opacity: 0.5;
                  }
                  50% {
                    transform: scale(1.2);
                    opacity: 1;
                  }
                }

                .loading-dots {
                  display: flex;
                  gap: 4px;
                }

                .dot {
                  width: 4px;
                  height: 4px;
                  background: ${
                    theme[isDarkModeInternal ? "dark" : "light"].text
                  };
                  border-radius: 50%;
                  margin-top:15px;
                  animation: bounce 1.4s infinite ease-in-out;
                }

                .dot:nth-child(2) { animation-delay: -0.32s; }
                .dot:nth-child(3) { animation-delay: -0.16s; }
                .dot:nth-child(4) { animation-delay: -0.16s; }

                @keyframes bounce {
                  0%, 80%, 100% { 
                    transform: scale(0);
                    opacity: 0.5;
                  }
                  40% { 
                    transform: scale(1);
                    opacity: 1;
                  }
                }
              `}
        </style>

        {/* Update Input Area */}
        <div
          style={{
            padding: "10px 16px 16px 10px",
            borderTop: "1px solid var(--border-color)",
            backgroundColor: "var(--background)",
            // opacity: !chatSessionId ? 0.5 : 1, // 👈 ADD THIS
            // pointerEvents: !chatSessionId ? "none" : "auto", // 👈 ADD THIS
          }}
        >
          {/* <-- ADD THIS HERE */}
          {isVoiceProcessing && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "8px 12px",
                // background: "var(--background)",
                borderRadius: "8px",
                marginBottom: "8px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {/* Spinner */}
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTop: "3px solid var(--teal-bg)",
                    borderRadius: "50%",
                    animation: "spin .5s linear infinite",
                  }}
                ></div>
                {/* Mic Icon */}
                <i
                  className="bi bi-mic-fill"
                  style={{
                    fontSize: "14px",
                    opacity: 0.7,
                    color: "var(--text)",
                  }}
                ></i>
                <span
                  style={{
                    fontSize: "12px",
                    opacity: 0.7,
                    color: "var(--text)",
                  }}
                >
                  Processing your voice...
                </span>
              </div>
              {/* <div
        className="audio-processing-spinner"
        style={{
          width: "16px",
          height: "16px",
          border: "2px solid rgba(255,255,255,0.3)",
          borderTop: "2px solid white",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}
      ></div> */}
              <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg);}
            100% { transform: rotate(360deg);}
          }
        `}
              </style>
            </div>
          )}
          {/* <-- END ADD */}
          <div
            style={{
              position: "fixed",
              right: IsSidebardNotificationVisible ? "25.5%" : "0.5%",
              // right: '6px',
              top: "16%",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              zIndex: 0,
              opacity: showScrollUp ? 1 : 0,
              visibility: showScrollUp ? "visible" : "hidden",
              transition: "opacity 0.3s, visibility 0.3s",
            }}
          >
            <button
              title="Scroll Up"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                background: isDarkModeInternal
                  ? "rgba(26, 115, 232, 0.4)"
                  : "rgba(24, 24, 27, 0.5)",
                color: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: showScrollUp ? "scale(1)" : "scale(0.8)",
                transition: "transform 0.3s",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
              }}
              onClick={() => {
                if (chatContainerRef.current) {
                  isManualScrollTop.current = false;

                  chatContainerRef.current.scrollTo({
                    top: isFirstScrollUp.current ? 10 : 0,
                    behavior: "smooth",
                  });

                  isFirstScrollUp.current = false; // After first click, always go to top: 0
                }
              }}
            >
              <i style={{ marginTop: "-4px" }} className="bi bi-chevron-up"></i>
            </button>
          </div>
          <div
            style={{
              position: "fixed",
              right: IsSidebardNotificationVisible ? "25.5%" : "0.5%",
              // right: '6px',
              bottom: "11.7em",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              zIndex: 0,
              visibility: showScrollBottomButton ? "visible" : "hidden",
              opacity: showScrollDown ? 1 : 0,
              transition: "opacity 0.3s, visibility 0.3s",
            }}
          >
            <button
              title="Scroll Down"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                background: isDarkModeInternal
                  ? "rgba(26, 115, 232, 0.4)"
                  : "rgba(24, 24, 27, 0.5)",
                color: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: showScrollDown ? "scale(1)" : "scale(0.8)",
                transition: "transform 0.3s",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
              }}
              onClick={() => {
                if (chatContainerRef.current) {
                  chatContainerRef.current.scrollTo({
                    top: chatContainerRef.current.scrollHeight,
                    behavior: "smooth",
                  });
                }
                setShowScrollBottomButton(false);
              }}
            >
              <i
                style={{ marginLeft: "0px" }}
                className="bi bi-chevron-down"
              ></i>
            </button>
          </div>

          {/* Preview Area */}
          {/* for singel files  */}
          {previewFile && (
            <div
              style={{
                marginBottom: "8px",
                padding: "8px",
                backgroundColor: "var(--background)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid var(--border-color)",
              }}
            >
              {previewFile.type === "image" ? (
                <img
                  src={previewFile.preview}
                  alt={previewFile.fileName}
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
              ) : (
                getFileIcon(previewFile.fileName, 28, "var(--text)")
              )}
              <span
                style={{
                  flex: 1,
                  color: "var(--text)",
                }}
              >
                {previewFile.fileName}
              </span>
              <button
                onClick={handleRemovePendingFile}
                style={{
                  border: "none",
                  background: "none",
                  padding: "4px",
                  cursor: "pointer",
                  color: "var(--text)",
                  opacity: 0.7,
                  transition: "opacity 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = 1)}
                onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
          )}

          {/* for multple files */}
          {/* {filePreviews.map((file, index) => (
  <div
    key={index}
    style={{
      marginBottom: '8px',
      padding: '8px',
      background: theme[isDarkModeInternal ? 'dark' : 'light'].messageBackground,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      border: `1px solid ${theme[isDarkModeInternal ? 'dark' : 'light'].divider}`,
    }}
  >
    {file.type === 'image' ? (
      <img
        src={file.preview}
        alt={file.fileName}
        style={{
          width: '40px',
          height: '40px',
          objectFit: 'cover',
          borderRadius: '4px',
        }}
      />
    ) : (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill={theme[isDarkModeInternal ? 'dark' : 'light'].text}
      >
        <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z" />
      </svg>
    )}
    <span
      style={{
        flex: 1,
        color: theme[isDarkModeInternal ? 'dark' : 'light'].text,
      }}
    >
      {file.fileName}
    </span>
    <button
      onClick={() => handleRemovePendingFile(index)} // You need to modify the handleRemovePendingFile to work with index
      style={{
        border: 'none',
        background: 'none',
        padding: '4px',
        cursor: 'pointer',
        color: theme[isDarkModeInternal ? 'dark' : 'light'].text,
        opacity: 0.7,
        transition: 'opacity 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={(e) => (e.target.style.opacity = 1)}
      onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
      </svg>
    </button>
  </div>
          ))} */}

          {/* Query In-Progress Indicator - shows only when user tries to send 2nd query */}
          {showQueryInProgressWarning && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 12px",
                marginBottom: "8px",
                backgroundColor: isDarkModeInternal
                  ? "rgba(255, 193, 7, 0.15)"
                  : "rgba(255, 193, 7, 0.2)",
                border: `1px solid ${isDarkModeInternal ? "rgba(255, 193, 7, 0.3)" : "rgba(255, 193, 7, 0.5)"}`,
                borderRadius: "8px",
                fontSize: "13px",
                color: isDarkModeInternal ? "#ffc107" : "#856404",
                animation: "fadeIn 0.2s ease-in",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="bi bi-exclamation-circle"></i>
                <span>A query is in progress. Please wait or stop to Continue</span>
              </div>
            </div>
          )}

          {/* Input and Buttons Area */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              position: "relative",
            }}
          >
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              // multiple
              onChange={handleFileSelect}
              accept="image/*,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt"
              style={{ display: "none" }}
              // disabled={selectedFiles.length >= 5} // Disable input if 5 files are already selected
            />
            {/* {ShowMentions && (
  <Form.Control
    ref={mentionSelectRef}
    as='select'
    className='me-2'
    defaultValue=''
    onChange={e => {
      const selectedMention = e.target.value;
      setInputText(selectedMention + ': ');
      console.log(actionLists[selectedMention]);
      setShowMentions(false);
    }}
    style={{
      position: 'absolute',
      left: mentionPosition.left,
      background: isDarkModeInternal ? '#18181B' : '#fff',
      color: isDarkModeInternal ? '#fff' : '#18181B',
      border: '1px solid #ccc',
      borderRadius: '5px',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      width: '200px'
    }}
  >
    {Mentions.map((mention, index) => (
      <option key={index} value={mention}>
        {mention}
      </option>
    ))}
  </Form.Control>
)} */}
            {ShowMentions && (
              <div
                ref={dropdownRef}
                tabIndex={0}
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: "0",
                  marginBottom: "8px",
                  background: "var(--background)",
                  color: "var(--text)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                  boxShadow: isDarkModeInternal
                    ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                    : "0 8px 32px rgba(0, 0, 0, 0.15)",
                  zIndex: 1000,
                  width: "220px",
                  overflow: "hidden",
                }}
                // Hide dropdown when it loses focus
                onBlur={() => {
                  setTimeout(() => setShowMentions(false));
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "10px 14px",
                    borderBottom: "1px solid var(--border-color)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "var(--background)",
                    borderRadius: "12px 12px 0 0",
                  }}
                >
                  <span style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "var(--text)",
                    opacity: 0.7,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}>
                    Actions
                  </span>
                  <span style={{
                    fontSize: "10px",
                    color: isDarkModeInternal ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                    display: "flex",
                    gap: "4px",
                    alignItems: "center"
                  }}>
                    <kbd style={{
                      padding: "2px 5px",
                      borderRadius: "4px",
                      background: isDarkModeInternal ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
                      color: isDarkModeInternal ? "#fff" : "#333",
                      fontSize: "9px",
                      fontWeight: "500",
                    }}>Tab</kbd>
                    <span>to select</span>
                  </span>
                </div>
                {/* Mention Items Container */}
                <div style={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: isDarkModeInternal ? "#555 transparent" : "#ccc transparent",
                }}>
                {Mentions.map((mention, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "10px 14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      backgroundColor:
                        highlightedIndex === index
                          ? "var(--teal-bg)"
                          : "transparent",
                      color:
                        highlightedIndex === index
                          ? "#fff"
                          : "var(--text)",
                      transition: "all 0.15s ease",
                      borderLeft: highlightedIndex === index
                        ? "3px solid var(--orange-bg)"
                        : "3px solid transparent",
                    }}
                    onClick={() => {
                      // Remove any existing @ mentions and get clean text
                      let cleanText = inputText.replace(/@[^:]*:/g, "").trim();

                      // Find the current @ search text to remove
                      const textarea = textareaRef.current;
                      const value = inputText;
                      const selectionStart = textarea
                        ? textarea.selectionStart
                        : value.length;
                      const atIndex = value.lastIndexOf("@", selectionStart - 1);

                      if (atIndex !== -1) {
                        let endIndex = atIndex + 1;
                        while (endIndex < value.length && /[\w]/.test(value[endIndex])) {
                          endIndex++;
                        }
                        const before = value.slice(0, atIndex);
                        const after = value.slice(endIndex);
                        cleanText = (before + after).replace(/@[^:]*:/g, "").trim();
                      }

                      // Set as chip; keep only clean text in textarea
                      setSelectedMention({ label: mention, action: actionLists[mention] });
                      setInputText(cleanText);

                      setTimeout(() => {
                        if (textareaRef.current) {
                          textareaRef.current.setSelectionRange(cleanText.length, cleanText.length);
                          textareaRef.current.focus();
                        }
                      }, 0);

                      setShowMentions(false);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)} // Highlight on hover
                  >
                    <i
                      className={`bi ${
                        mention.includes("Task") ? "bi-check2-square" :
                        mention.includes("Notes") ? "bi-journal-text" :
                        mention.includes("Lists") ? "bi-list-ul" :
                        mention.includes("Gmail") ? "bi-envelope" :
                        mention.includes("Calendar") ? "bi-calendar3" :
                        mention.includes("Contacts") ? "bi-person-lines-fill" :
                        mention.includes("Notion") ? "bi-box" :
                        mention.includes("DeepResearch") ? "bi-search" :
                        mention.includes("WebSearch") ? "bi-globe" :
                        mention.includes("Youtube") ? "bi-youtube" :
                        mention.includes("Instagram") ? "bi-instagram" :
                        mention.includes("TikTok") ? "bi-tiktok" :
                        mention.includes("ScrapeUrl") ? "bi-link-45deg" :
                        mention.includes("Reminders") ? "bi-bell" :
                        mention.includes("SecondBrain") ? "bi-lightbulb" :
                        mention.includes("Topics") ? "bi-chat-dots" :
                        mention.includes("Meetings") ? "bi-camera-video" :
                        mention.includes("SMS") ? "bi-chat-left-text" :
                        mention.includes("KnowledgeBase") ? "bi-book" :
                        "bi-at"
                      }`}
                      style={{
                        fontSize: "14px",
                        opacity: highlightedIndex === index ? 1 : 0.7,
                      }}
                    ></i>
                    <span style={{
                      fontSize: "13px",
                      fontWeight: highlightedIndex === index ? "500" : "400",
                    }}>
                      {mention.replace("@", "")}
                    </span>
                  </div>
                ))}
                </div>
              </div>
            )}
            {/* <textarea
              value={inputText}
              onPaste={handlePaste}
              onChange={e => handleChange(e)}
              onKeyPress={handleKeyPress}
              placeholder={
                selectedFiles.length >= 5
                  ? 'Maximum 5 files allowed at a time.'
                  : 'Type a message...'
              }
              disabled={selectedFiles.length >= 5} // Disable textarea if 5 files are selected
              style={{
                flex: 1,
                border: isDarkModeInternal
                  ? '1px solid #3D3D3D'
                  : '1px solid #E5E7EB',
                borderRadius: '5px',
                outline: 'none',
                fontSize: '16px',
                background:
                  theme[isDarkModeInternal ? 'dark' : 'light'].inputBackground,
                color: theme[isDarkModeInternal ? 'dark' : 'light'].inputText,
                opacity: 1,
                padding: '1em',
                maxHeight: '200px',
                overflow: 'auto'
              }}
              rows={2}
              cols={50}
            /> */}
            <div>
              <Fab
                mainButtonStyles={{
                  backgroundColor: "var(--orange-bg)",
                  width: "45px",
                  height: "45px",
                  borderRadius: "8px", // square look like your Z
                }}
                icon={
                  <span
                    className="text-white fw-bold"
                    style={{ fontSize: "1rem" }}
                    onClick={handleMainSessionClick}
                  >
                    Z
                  </span>
                }
                alwaysShowTitle={false}
                position={{ bottom: 0, left: 0 }}
                style={{
                  position: "relative",
                  margin: "0px 0px 0px 8px",
                  zIndex: 0,
                }}
              >
                {/* Audio Button - Triggers Voice Assistant via OpenAI Realtime */}
                <Action
                  text={isVoiceActive ? "End Call" : "Voice"}
                  style={{
                    backgroundColor: isVoiceActive ? "#ef4444" : "var(--orange-bg)",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handleAudioClick}
                >
                  <i className={`bi ${isVoiceActive ? 'bi-telephone-x-fill' : 'bi-mic'}`}></i>
                </Action>
                {/* Webcam Button - Hidden when screen share is active */}
                <Action
                  text={isVoiceVideoEnabled && !isVoiceScreenSharing ? "Stop Video" : "Camera"}
                  style={{
                    backgroundColor: isVoiceVideoEnabled && !isVoiceScreenSharing ? "#ef4444" : "var(--orange-bg)",
                    width: "40px",
                    height: "40px",
                    display: isVoiceScreenSharing ? "none" : "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handleCameraToggle}
                >
                  <i className={`bi ${isVoiceVideoEnabled && !isVoiceScreenSharing ? 'bi-camera-video-off-fill' : 'bi-camera-video'}`}></i>
                </Action>
                {/* Screen Share Button - Hidden when camera is active */}
                <Action
                  text={isVoiceVideoEnabled && isVoiceScreenSharing ? "Stop Share" : "Screen"}
                  style={{
                    backgroundColor: isVoiceVideoEnabled && isVoiceScreenSharing ? "#ef4444" : "var(--orange-bg)",
                    width: "40px",
                    height: "40px",
                    display: isScreenSharingSupported() && (!isVoiceVideoEnabled || isVoiceScreenSharing) ? "flex" : "none",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handleScreenToggle}
                >
                  <i className={`bi ${isVoiceVideoEnabled && isVoiceScreenSharing ? 'bi-display' : 'bi-display'}`}></i>
                </Action>
              </Fab>
            </div>
            
            {/* Mode Selector - Right side of Z button */}
            <ModeSelector onModeChange={setSelectedMode} />
            
            {(startVoiceRecording || isVideoActive) && (
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {/* Voice Active Status Indicator */}
                {(startVoiceRecording || isVideoActive) && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 10px",
                      borderRadius: "5px",
                      background: "rgba(16, 185, 129, 0.1)",
                      border: "1px solid rgba(16, 185, 129, 0.3)",
                      fontSize: "12px",
                      color: "#10B981",
                    }}
                  >
                    <span style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "#10B981",
                      animation: "pulse 2s infinite",
                    }} />
                    Live
                  </div>
                )}
                {/* Screenshot Button (during screen share) */}
                {isVideoActive && activeVideoMode === "screen" && (
                  <button
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "5px",
                      border: "1px solid var(--border-color)",
                      background: "var(--teal-bg)",
                      color: "var(--textwhite)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={handleCaptureScreenshot}
                    title="Save screenshot to Drive"
                  >
                    <i className="bi bi-camera fs-5"></i>
                  </button>
                )}
                {/* Stop Button */}
                <button
                  style={{
                    width: "45px",
                    height: "45px",
                    borderRadius: "5px",
                    border: "1px solid var(--border-color)",
                    background: "var(--danger)",
                    color: "var(--textwhite)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    opacity: 1,
                  }}
                  onClick={stopVoiceAssistant}
                >
                  <i className="bi bi-stop-fill fs-5"></i>
                </button>
              </div>
            )}
            {/* Textarea with custom resize handle */}
            <div style={{ position: "relative", flex: 1 }}>
              {/* @mention chip — absolutely positioned inside the input border */}
              {selectedMention && (
                <span
                  ref={mentionChipRef}
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "10px",
                    zIndex: 2,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "2px 6px 2px 10px",
                    borderRadius: "20px",
                    background: "rgba(13, 148, 136, 0.15)",
                    border: "1px solid var(--teal-bg, #0d9488)",
                    color: "var(--teal-bg, #0d9488)",
                    fontSize: "12px",
                    fontWeight: "600",
                    whiteSpace: "nowrap",
                    pointerEvents: "auto",
                  }}
                >
                  {selectedMention.label}
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setSelectedMention(null); }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      cursor: "pointer",
                      padding: "0 2px",
                      fontSize: "14px",
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Remove"
                  >×</button>
                </span>
              )}
              <textarea
                className="search-input-box chatbot-main-input"
                ref={textareaRef}
                value={inputText}
                onPaste={handlePaste}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && selectedMention && e.target.value === "") {
                    backspaceOnEmptyCount.current += 1;
                    if (backspaceOnEmptyCount.current >= 2) {
                      backspaceOnEmptyCount.current = 0;
                      setSelectedMention(null);
                    }
                  } else {
                    backspaceOnEmptyCount.current = 0;
                  }
                }}
                placeholder={
                  selectedFiles.length >= 5
                    ? "Maximum 5 files allowed at a time."
                    : queueMode === 'connected'
                    ? "Add this to your connected queue..."
                    : queueMode === 'unconnected'
                    ? "Add this to your standalone queue..."
                    : "What can I take off your mind right now?"
                }
                disabled={selectedFiles.length >= 5}
                style={{
                  width: "100%",
                  border: queueMode === 'connected'
                    ? "2px solid var(--teal-bg)"
                    : queueMode === 'unconnected'
                    ? "2px solid var(--orange-bg)"
                    : isDarkModeInternal
                    ? "1px solid #3D3D3D"
                    : "1px solid #E5E7EB",
                  borderRadius: "5px",
                  outline: "none",
                  fontSize: "16px",
                  background:
                    theme[isDarkModeInternal ? "dark" : "light"]
                      .inputBackground,
                  color: theme[isDarkModeInternal ? "dark" : "light"].inputText,
                  opacity: 1,
                  padding: "10px 12px",
                  minHeight: "44px",
                  maxHeight: "400px",
                  height: inputTextareaHeight,
                  overflowY: "auto",
                  resize: "none",
                  lineHeight: "1.4",
                }}
                rows={1}
                cols={50}
              />
              {/* Custom resize handle */}
              <i
                className="bi bi-grip-horizontal"
                style={{
                  position: "absolute",
                  top: "-15px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: "18px",
                  cursor: "ns-resize",
                  color: isDarkModeInternal ? "#666" : "#999",
                  opacity: 0.6,
                  zIndex: 10,
                  transition: "opacity 0.2s",
                  padding: "2px 8px",
                  borderRadius: "3px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = 1;
                  e.target.style.background = isDarkModeInternal
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = 0.6;
                  e.target.style.background = "transparent";
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsManualInputResize(true); // User started manual resize
                  const startY = e.clientY;
                  const startHeight = textareaRef.current.offsetHeight;

                  const handleMouseMove = (e) => {
                    const deltaY = startY - e.clientY;
                    const newHeight = Math.max(
                      44,
                      Math.min(400, startHeight + deltaY)
                    );
                    textareaRef.current.style.height = `${newHeight}px`;
                    setInputTextareaHeight(newHeight);
                  };

                  const handleMouseUp = () => {
                    document.removeEventListener("mousemove", handleMouseMove);
                    document.removeEventListener("mouseup", handleMouseUp);
                    // Optionally: setIsManualInputResize(false); // If you want to re-enable auto-resize after mouse up
                  };

                  document.addEventListener("mousemove", handleMouseMove);
                  document.addEventListener("mouseup", handleMouseUp);
                }}
              />
            </div>
            {/* Send button */}
            {!audioRecording && (
              <button
                onClick={handleSendMessage}
                disabled={
                  isLoading ||
                  (!isSending &&
                    !isStreaming &&
                    !inputText.trim() &&
                    !selectedMention &&
                    !selectedFile)
                }
                style={{
                  border: "none",
                  background: isSending || isStreaming ? "#dc3545" : "var(--teal-bg)",
                  padding: "8px",
                  borderRadius: "8px",
                  width: "40px",
                  height: "40px",
                }}
                title={isSending || isStreaming ? "Stop generating" : "Send message"}
              >
                {isSending || isStreaming ? (
                  <i className="bi bi-stop-fill"></i>
                ) : (
                  <i className="bi bi-send"></i>
                )}
              </button>
            )}
            {/* Attachment button */}
            {!audioRecording && (
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "transparent",
                  color: "var(--text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  // cursor: !chatSessionId ? "not-allowed" : "pointer", // 👈 ADD THIS
                  opacity:
                    // !chatSessionId || // 👈 ADD THIS
                    connecting ||
                    isLoading ||
                    selectedFiles.length > 5 ||
                    isSending ||
                    isStreaming
                      ? 0.5
                      : 1,
                  disabled: (isLoading || previewFile) !== null,
                }}
                disabled={
                  // !chatSessionId || // 👈 ADD THIS
                  isLoading ||
                  previewFile !== null ||
                  selectedFiles.length > 5 ||
                  isSending ||
                  isStreaming
                }
              >
                <i className="bi bi-paperclip"></i>
              </button>
            )}
            {!queue && (
              <Record
                setAudioRecording={setAudioRecording}
                onStop={handleAudioRecorded} // Pass the handler to Record component
                setAudioInput={setAudioInput} // Pass the setAudioInput function to Record component
                isLoading={
                  // !chatSessionId ||
                  isLoading || isSending || isStreaming
                } // Pass the isLoading state to Record component
                setIsUserQueyLoading={setIsUserQueyLoading} // Pass the setIsLoading function to Record component
                isDarkModeInternal={isDarkModeInternal} // Pass the isDarkModeInternal state to Record component
                setCopyToTextArea={setCopyToTextArea} // Pass the handleAddToTextArea function to Record component
                setCancelAudio={setCancelAudio}
                setIsVoiceProcessing={setIsVoiceProcessing}
              />
            )}
            <UploadFile
              userId={userId}
              isLoading={isLoading || isSending || isStreaming}
            />{" "}
            {/* Add the UploadFile component */}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "4px",
            }}
          >
            {/* Connected Queue Button Group (LEFT) */}
            <div
              className="queue-button-group"
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "5px",
                borderRadius: "5px",
                overflow: "hidden",
                border: queueMode === 'connected'
                  ? "none"
                  : "1px solid var(--border-color)",
              }}
            >
              {/* Connected Queue Toggle Button */}
              <button
                title={queueMode === 'connected' ? "Connected queue ON - click to disable" : "Enable connected queue (links to current session)"}
                style={{
                  height: "38px",
                  padding: "0 12px",
                  border: "none",
                  borderRadius: "5px 0 0 5px",
                  background: queueMode === 'connected'
                    ? "var(--teal-bg)"
                    : "transparent",
                  color: queueMode === 'connected'
                    ? "var(--textwhite)"
                    : "var(--text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  transition: "all 0.2s ease",
                  opacity: connecting || isLoading || isSending || isStreaming ? 0.5 : 1,
                }}
                onClick={handleConnectedQueueToggle}
                onTouchStart={handleConnectedTouchStart}
                onTouchEnd={handleConnectedTouchEnd}
                disabled={connecting || isLoading || isSending || isStreaming}
              >
                <i className="bi bi-link-45deg" style={{ fontSize: "14px" }}></i>
                <span style={{ fontSize: "13px", fontWeight: queueMode === 'connected' ? "500" : "400" }}>Connected</span>
              </button>

              {/* Divider */}
              <div style={{
                width: "1px",
                height: "24px",
                background: queueMode === 'connected'
                  ? "rgba(255,255,255,0.3)"
                  : "var(--border-color)",
              }}></div>

              {/* Up-Arrow Button to Open Connected Queue Popup */}
              <button
                title="Open connected queue popup (preserves current input)"
                style={{
                  height: "38px",
                  padding: "0 10px",
                  border: "none",
                  borderRadius: "0 5px 5px 0",
                  background: queueMode === 'connected'
                    ? "var(--teal-bg)"
                    : "transparent",
                  color: queueMode === 'connected'
                    ? "var(--textwhite)"
                    : "var(--text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  opacity: connecting || isLoading || isSending || isStreaming ? 0.5 : 1,
                }}
                onClick={handleConnectedQueuePopup}
                disabled={connecting || isLoading || isSending || isStreaming}
              >
                <i className="bi bi-arrow-up-square" style={{ fontSize: "16px" }}></i>
              </button>
            </div>

            {/* Main Topic + Sync Now + Catch Up Buttons */}
            <div
              className="quick-idea-toolbar"
              style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "5px" }}
            >
              {/* Full Context Toggle + Settings */}
              <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                <button
                  title={fullContextEnabled ? "Full Context ON - using custom limits" : "Enable Full Context for custom history limits"}
                  onClick={() => {
                    const next = !fullContextEnabled;
                    setFullContextEnabled(next);
                    // Persist toggle state to localStorage + DB
                    saveCustomContextLimits({ enabled: next, ...(customContextLimits || {}) });
                    const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
                    fetch(`${NEXT_API_URL}/api/user/profile`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        user_id: userId,
                        custom_context_limits: {
                          enabled: next,
                          ...(customContextLimits || {}),
                        },
                      }),
                    }).catch(() => {});
                  }}
                  style={{
                    height: "38px",
                    padding: "0 10px",
                    borderRadius: "8px 0 0 8px",
                    background: fullContextEnabled
                      ? "linear-gradient(135deg, #0d9488, #14b8a6)"
                      : isDarkMode ? "var(--card-bg, #1a1a2e)" : "#e5e7eb",
                    color: fullContextEnabled ? "#fff" : isDarkMode ? "#aaa" : "#666",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "12px",
                    fontWeight: fullContextEnabled ? "600" : "400",
                    transition: "all 0.2s ease",
                    border: fullContextEnabled ? "none" : `1px solid ${isDarkMode ? "var(--border-color)" : "#d1d5db"}`,
                  }}
                >
                  <i className={`bi ${fullContextEnabled ? "bi-chat-square-text-fill" : "bi-chat-square-text"}`} style={{ fontSize: "13px" }}></i>
                  Full Context
                </button>
                <button
                  title="Context Limits Settings"
                  onClick={() => setShowContextSettings(true)}
                  style={{
                    height: "38px",
                    width: "34px",
                    border: "none",
                    borderRadius: "0 8px 8px 0",
                    background: fullContextEnabled
                      ? "linear-gradient(135deg, #0d9488, #0f766e)"
                      : isDarkMode ? "var(--card-bg, #1a1a2e)" : "#d1d5db",
                    color: fullContextEnabled ? "#fff" : isDarkMode ? "#aaa" : "#666",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    borderLeft: fullContextEnabled
                      ? "1px solid rgba(255,255,255,0.2)"
                      : `1px solid ${isDarkMode ? "var(--border-color)" : "#bbb"}`,
                  }}
                >
                  <i className="bi bi-gear-fill" style={{ fontSize: "13px" }}></i>
                </button>
              </div>

              <div className="quick-idea-anchor">
                {quickIdeaToast ? (
                  <div className="quick-idea-toast">
                    <i className="bi bi-check2-circle"></i>
                    {quickIdeaToast}
                  </div>
                ) : null}
                <button
                  title="Save quick ideas"
                  className="quick-idea-btn"
                  type="button"
                  onClick={() => setShowQuickIdeaPopup((prev) => !prev)}
                >
                  <i className="bi bi-lightbulb-fill" style={{ fontSize: isDarkMode ? "14px" : "15px" }}></i>
                  Ideas
                </button>
                <QuickIdeaPopup
                  show={showQuickIdeaPopup}
                  onClose={() => setShowQuickIdeaPopup(false)}
                  onSaved={handleQuickIdeaSaved}
                  isDarkMode={isDarkMode}
                  userId={userId}
                  sessionId={chatSessionId}
                />
              </div>
              <div
                style={{
                  borderRadius: isDarkMode ? "5px" : "8px",
                  overflow: "hidden",
                  border: isDarkMode ? "1px solid var(--border-color)" : "none",
                }}
              >
                <button
                  title="Go to Main Topic"
                  className="z-main-session-btn"
                  style={{
                    height: "38px",
                    padding: "0 12px",
                    border: "none",
                    borderRadius: isDarkMode ? "5px" : "8px",
                    background: isDarkMode
                      ? "var(--teal-bg)"
                      : "linear-gradient(135deg, #0d9488, #14b8a6, #2dd4bf)",
                    color: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    transition: "all 0.2s ease",
                    fontWeight: isDarkMode ? "400" : "600",
                    fontSize: "13px",
                  }}
                  onClick={handleMainSessionClick}
                >
                  <i className="bi bi-house-door" style={{ fontSize: isDarkMode ? "14px" : "15px" }}></i>
                  Main Topic
                </button>
              </div>
              {/* Sync Now Button - between Main Topic and Catch Up */}
              {showRefreshButton && (
                <button
                  className="sync-now-btn"
                  onClick={handleRefreshClick}
                  title="Sync Chat"
                >
                 <i className="bi bi-arrow-repeat fs-5"></i> &nbsp;&nbsp;Sync Now
                </button>
              )}
              <div
                style={{
                  borderRadius: isDarkMode ? "5px" : "8px",
                  overflow: "hidden",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
              <button
                title={allCatchUpSourcesDisabled ? "Enable at least one source in settings" : "Get your catchup brief"}
                className="z-catchup-btn"
                style={{
                  height: "38px",
                  padding: "0 12px",
                  border: "none",
                  borderRadius: "0",
                  background: "var(--orange-bg)",
                  color: "#fff",
                  cursor: (catchUpLoading || allCatchUpSourcesDisabled) ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "all 0.2s ease",
                  fontWeight: isDarkMode ? "400" : "600",
                  fontSize: "13px",
                  opacity: (catchUpLoading || allCatchUpSourcesDisabled) ? 0.5 : 1,
                }}
                onClick={handleCatchUpClick}
                disabled={catchUpLoading || allCatchUpSourcesDisabled}
              >
                <i className={catchUpLoading ? "bi bi-arrow-repeat" : "bi bi-lightning-charge"} style={{ fontSize: isDarkMode ? "14px" : "15px", animation: catchUpLoading ? "spin 1s linear infinite" : "none" }}></i>
                {catchUpLoading ? "Preparing..." : "Catchup"}
              </button>
              <div style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.3)" }}></div>
              <button
                title="Catch Up Settings"
                className="z-catchup-settings-btn"
                style={{
                  height: "38px",
                  width: "36px",
                  border: "none",
                  borderRadius: "0",
                  outline: "none",
                  boxShadow: "none",
                  background: "var(--orange-bg)",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                }}
                onClick={() => setShowCatchUpSettings(true)}
              >
                <i className="bi bi-gear" style={{ fontSize: "14px" }}></i>
              </button>
              <div style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.3)" }}></div>
              <button
                title={unreadMorningCatchUpNotification ? "Open Morning Catch Up" : "Morning Catchup"}
                className="z-catchup-sun-btn"
                style={{
                  height: "38px",
                  width: "36px",
                  border: "none",
                  borderRadius: "0",
                  outline: "none",
                  boxShadow: "none",
                  background: unreadMorningCatchUpNotification ? "#fff7ed" : "var(--orange-bg)",
                  color: unreadMorningCatchUpNotification ? "var(--orange-bg)" : "#ffffff",
                  cursor: hasTodaysMorningCatchUp ? "pointer" : "default",
                  opacity: hasTodaysMorningCatchUp ? 1 : 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                  position: "relative",
                }}
                onClick={() => hasTodaysMorningCatchUp && setShowMorningCatchUpModal(true)}
              >
                <i
                  className="bi bi-brightness-high-fill"
                  style={{
                    fontSize: unreadMorningCatchUpNotification ? "16px" : "14px",
                    animation: unreadMorningCatchUpNotification && !showMorningCatchUpModal
                      ? "morningCatchUpSunBounce 7s linear infinite"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                ></i>
              </button>
              </div>
            </div>

            <ReplyModal
              show={showMorningCatchUpModal && !!(unreadMorningCatchUpNotification || (hasTodaysMorningCatchUp && todaysMorningCatchUp))}
              onHide={handleMorningCatchUpClose}
              notification={unreadMorningCatchUpNotification || todaysMorningCatchUp}
              handleSendReply={handleMorningCatchUpClose}
              workflowUrl={null}
              onNotificationUpdate={() => {}}
              alreadyReplied={false}
              onAlreadyRepliedChange={() => {}}
              userId={userId}
            />

            {/* Catch Up Settings Modal */}
            <Modal
              show={showCatchUpSettings}
              onHide={() => setShowCatchUpSettings(false)}
              centered
              className="catchup-settings-modal"
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: "16px", fontWeight: "600" }}>
                  <i className="bi bi-lightning-charge" style={{ marginRight: "8px", color: "var(--orange-bg)" }}></i>
                  Catch Up Settings
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: "16px 20px" }}>
                <p style={{ fontSize: "12px", color: "#888", margin: "0 0 14px 0" }}>
                  Configure your daily catch-up brief preferences.
                </p>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 0", marginBottom: "12px", borderBottom: "1px solid var(--border-color, #eee)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="bi bi-sunrise" style={{ fontSize: "16px", color: "var(--orange-bg)" }}></i>
                    <div>
                      <span style={{ fontSize: "13px", fontWeight: "600" }}>Morning Catchup</span>
                      <div style={{ fontSize: "11px", color: "#888" }}>Auto-generate brief every morning</div>
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    checked={catchUpSettings.morning_catchup_enabled}
                    onChange={(e) =>
                      setCatchUpSettings((prev) => ({ ...prev, morning_catchup_enabled: e.target.checked }))
                    }
                  />
                </div>
                {catchUpSettings.morning_catchup_enabled && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", paddingLeft: "26px" }}>
                    <label style={{ fontSize: "13px", fontWeight: "500", color: "#666" }}>Time:</label>
                    <Form.Select
                      value={catchUpSettings.morning_catchup_hour}
                      onChange={(e) =>
                        setCatchUpSettings((prev) => ({ ...prev, morning_catchup_hour: parseInt(e.target.value) }))
                      }
                      style={{ fontSize: "13px", width: "80px" }}
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      value={catchUpSettings.morning_catchup_minute}
                      onChange={(e) =>
                        setCatchUpSettings((prev) => ({ ...prev, morning_catchup_minute: parseInt(e.target.value) }))
                      }
                      style={{ fontSize: "13px", width: "80px" }}
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, "0")}</option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      value={catchUpSettings.morning_catchup_ampm}
                      onChange={(e) =>
                        setCatchUpSettings((prev) => ({ ...prev, morning_catchup_ampm: e.target.value }))
                      }
                      style={{ fontSize: "13px", width: "80px" }}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </Form.Select>
                  </div>
                )}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "600", marginBottom: "6px", display: "block" }}>
                    Brief Mode
                  </label>
                  <Form.Select
                    value={catchUpSettings.brief_mode}
                    onChange={(e) =>
                      setCatchUpSettings((prev) => ({ ...prev, brief_mode: e.target.value }))
                    }
                    style={{ fontSize: "13px" }}
                  >
                    <option value="simple">Simple — Short & quick overview</option>
                    <option value="smart">Smart — Balanced detail with priorities</option>
                    <option value="genius">Genius — Comprehensive deep analysis</option>
                  </Form.Select>
                </div>
                <div className="catchup-toggles-list" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                  {CATCHUP_SOURCE_OPTIONS.map((opt) => {
                    const isIntegration = INTEGRATION_TOGGLE_KEYS.has(opt.key);
                    const isCheckingConnection = isIntegration && integrationsLoading;
                    const isConnected = !isIntegration || connectedIntegrations.has(opt.key);
                    const isDisabled = isIntegration && (integrationsLoading || !isConnected);
                    return (
                      <div
                        key={opt.key}
                        className="catchup-toggle-row"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          borderBottom: "1px solid var(--border-color, #eee)",
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <i className={`bi ${opt.icon}`} style={{ fontSize: "15px", opacity: 0.7 }}></i>
                          <div>
                            <span style={{ fontSize: "13px", fontWeight: "500" }}>{opt.label}</span>
                            {isCheckingConnection && (
                              <div style={{ fontSize: "10px", color: "#888", display: "flex", alignItems: "center", gap: "4px" }}>
                                <i className="bi bi-arrow-repeat" style={{ animation: "spin 1s linear infinite", fontSize: "9px" }}></i>
                                Checking...
                              </div>
                            )}
                            {!isCheckingConnection && !isConnected && (
                              <div style={{ fontSize: "10px", color: "#e74c3c" }}>Not connected</div>
                            )}
                          </div>
                        </div>
                        <Form.Check
                          type="switch"
                          checked={isDisabled ? false : catchUpSettings[opt.key]}
                          disabled={isDisabled}
                          onChange={(e) =>
                            setCatchUpSettings((prev) => ({ ...prev, [opt.key]: e.target.checked }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: "16px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "600", marginBottom: "6px", display: "block" }}>
                    Custom Instructions
                  </label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="e.g., Focus on work tasks only, Skip groceries details..."
                    value={catchUpSettings.custom_instructions}
                    onChange={(e) =>
                      setCatchUpSettings((prev) => ({ ...prev, custom_instructions: e.target.value }))
                    }
                    style={{ fontSize: "13px", resize: "none" }}
                  />
                  <div style={{ textAlign: "right", fontSize: "11px", color: "#aaa", marginTop: "4px" }}>
                    {catchUpSettings.custom_instructions.trim().split(/\s+/).filter(Boolean).length} words
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer style={{ padding: "10px 20px" }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCatchUpSettings(false)}
                  style={{ fontSize: "13px" }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveCatchUpSettings}
                  disabled={catchUpSettingsLoading}
                  style={{
                    fontSize: "13px",
                    background: "var(--orange-bg)",
                    border: "none",
                  }}
                >
                  {catchUpSettingsLoading ? "Saving..." : "Save"}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Catch Up Progress Modal */}
            <Modal
              show={showCatchUpProgressModal}
              onHide={() => setShowCatchUpProgressModal(false)}
              centered
              className="catchup-progress-modal"
              onEntered={() => setTimeout(() => setShowCatchUpProgressModal(false), 3000)}
            >
              <Modal.Body style={{ padding: "30px 24px", textAlign: "center" }}>
                <div style={{ marginBottom: "16px" }}>
                  <i className="bi bi-hourglass-split" style={{ fontSize: "48px", color: "var(--orange-bg)" }}></i>
                </div>
                <h5 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "12px" }}>
                  Catch Up in Progress
                </h5>
                <p style={{ fontSize: "14px", color: "#666", margin: "0", lineHeight: "1.6" }}>
                  Please wait for approximately <strong>5 minutes</strong>.<br />
                  Your catch-up brief is being prepared.<br />
                  You will receive a <strong>notification</strong> once it's ready.
                </p>
              </Modal.Body>
            </Modal>

            {/* Context Limits Settings Modal */}
            <ContextSettingsModal
              show={showContextSettings}
              onHide={() => setShowContextSettings(false)}
              userId={userId}
              isDarkMode={isDarkMode}
              onSave={(limits) => setCustomContextLimits(limits)}
            />

            {/* Catch Up Toast */}
            {catchUpToast && (
              <div
                style={{
                  position: "absolute",
                  bottom: "50px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  background: catchUpToast.type === "success"
                    ? "linear-gradient(135deg, #0d9488, #14b8a6)"
                    : "linear-gradient(135deg, #dc2626, #ef4444)",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "500",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  animation: "fadeIn 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <i className={catchUpToast.type === "success" ? "bi bi-check-circle" : "bi bi-exclamation-circle"}></i>
                {catchUpToast.message}
                <i
                  className="bi bi-x"
                  style={{ cursor: "pointer", marginLeft: "8px", opacity: 0.8 }}
                  onClick={() => setCatchUpToast(null)}
                ></i>
              </div>
            )}

            {/* Unconnected (Standalone) Queue Button Group (RIGHT) */}
            <div
              className="queue-button-group"
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "5px",
                borderRadius: "5px",
                overflow: "hidden",
                border: queueMode === 'unconnected'
                  ? "none"
                  : "1px solid var(--border-color)",
              }}
            >
              {/* Up-Arrow Button to Open Unconnected Queue Popup */}
              <button
                title="Open standalone queue popup (preserves current input)"
                style={{
                  height: "38px",
                  padding: "0 10px",
                  border: "none",
                  borderRadius: "5px 0 0 5px",
                  background: queueMode === 'unconnected'
                    ? "var(--orange-bg)"
                    : "transparent",
                  color: queueMode === 'unconnected'
                    ? "var(--textwhite)"
                    : "var(--text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  opacity: connecting || isLoading || isSending || isStreaming ? 0.5 : 1,
                }}
                onClick={handleUnconnectedQueuePopup}
                disabled={connecting || isLoading || isSending || isStreaming}
              >
                <i className="bi bi-arrow-up-square" style={{ fontSize: "16px" }}></i>
              </button>

              {/* Divider */}
              <div style={{
                width: "1px",
                height: "24px",
                background: queueMode === 'unconnected'
                  ? "rgba(255,255,255,0.3)"
                  : "var(--border-color)",
              }}></div>

              {/* Unconnected Queue Toggle Button */}
              <button
                title={queueMode === 'unconnected' ? "Standalone queue ON - click to disable" : "Enable standalone queue (not linked to topic)"}
                style={{
                  height: "38px",
                  padding: "0 12px",
                  border: "none",
                  borderRadius: "0 5px 5px 0",
                  background: queueMode === 'unconnected'
                    ? "var(--orange-bg)"
                    : "transparent",
                  color: queueMode === 'unconnected'
                    ? "var(--textwhite)"
                    : "var(--text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  transition: "all 0.2s ease",
                  opacity: connecting || isLoading || isSending || isStreaming ? 0.5 : 1,
                }}
                onClick={handleUnconnectedQueueToggle}
                onTouchStart={handleUnconnectedTouchStart}
                onTouchEnd={handleUnconnectedTouchEnd}
                disabled={connecting || isLoading || isSending || isStreaming}
              >
                <span style={{ fontSize: "13px", fontWeight: queueMode === 'unconnected' ? "500" : "400" }}>Standalone</span>
                <i className="bi bi-box-arrow-right" style={{ fontSize: "14px" }}></i>
              </button>
            </div>

            {/* ...Queue button... */}
            {/* <button
              style={{
                width: "140px",
                height: "38px",
                borderRadius: "5px",
                marginTop: "5px",
                border: "none",
                background: "var(--danger)",
                color: "white",
                cursor: isSending || isStreaming ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                marginLeft: "8px",
                opacity: isSending || isStreaming ? 0.5 : 1,
              }}
              onClick={() => setShowClearQueueConfirm(true)}
              disabled={isSending || isStreaming || isLoading}
            >
              <i className="bi bi-trash"></i>&nbsp; Clear Queue
            </button> */}
            {/* <button
              style={{
                width: "150px",
                height: "40px",
                borderRadius: "5px",
                marginTop: "5px",
                border: "none",
                background: isDarkModeInternal ? "#1A73E8" : "#18181B",
                color: "white",
                cursor: isSending || isStreaming ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                marginLeft: "8px",
                opacity: isSending || isStreaming ? 0.5 : 1,
              }}
              onClick={() => window.location.reload()}
              disabled={isSending || isStreaming}
            >
              <i className="bi bi-arrow-clockwise"></i>&nbsp; Reset Topic
            </button> */}
          </div>
        </div>

        {/* Animation Keyframes */}
        <style>
          {`
                @keyframes pulse {
                  0%, 100% {
                    opacity: 0.5;
                    transform: scale(0.8);
                  }
                  50% {
                    opacity: 1;
                    transform: scale(1);
                  }
                }

                @keyframes morningCatchUpSunBounce {
                  0% {
                    transform: scale(1);
                    filter: drop-shadow(0 0 2px rgba(255, 220, 0, 0.3));
                  }
                  5% {
                    transform: scale(1);
                    filter: drop-shadow(0 0 2px rgba(255, 220, 0, 0.3));
                  }
                  15% {
                    transform: scale(1.45);
                    filter: drop-shadow(0 0 10px rgba(255, 180, 0, 0.9)) drop-shadow(0 0 20px rgba(255, 140, 0, 0.4));
                  }
                  22% {
                    transform: scale(0.7);
                    filter: drop-shadow(0 0 1px rgba(255, 220, 0, 0.2));
                  }
                  30% {
                    transform: scale(1.3);
                    filter: drop-shadow(0 0 8px rgba(255, 180, 0, 0.8)) drop-shadow(0 0 16px rgba(255, 140, 0, 0.3));
                  }
                  37% {
                    transform: scale(0.75);
                    filter: drop-shadow(0 0 1px rgba(255, 220, 0, 0.2));
                  }
                  44% {
                    transform: scale(1.15);
                    filter: drop-shadow(0 0 5px rgba(255, 200, 0, 0.7));
                  }
                  50% {
                    transform: scale(1);
                    filter: drop-shadow(0 0 2px rgba(255, 220, 0, 0.3));
                  }
                  100% {
                    transform: scale(1);
                    filter: drop-shadow(0 0 2px rgba(255, 220, 0, 0.3));
                  }
                }

                /* Textarea resize limits */
                textarea {
                  min-height: 44px !important;
                  max-height: 400px !important;
                  resize: none !important;
                }

                /* Hide any default resize handles */
                textarea::-webkit-resizer {
                  display: none;
                }
              `}
        </style>
      </div>

      {/* kuldeep */}
      {/* {showVoicePopup && ( */}
      <div
        ref={voicePopupRef}
        style={{
          position: "fixed",
          display: voicePopupVisibility ? "flex" : "none",
          left: voicePosition.x,
          top: voicePosition.y,
          zIndex: 2000,
          // background: isDarkModeInternal ? "var(--background)" : "#ffffff",
          // border: `1px solid var(--border-color)`,
          // boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          borderRadius: 16,
          padding: 20,
          flexDirection: "column",
          alignItems: "center",
          color: "var(--text)",
          fontFamily: "inherit",
        }}
      >
        {/* Audio Section */}
        {/* <div className="audioBox" style={{border:"1px solid"}}> */}
        <button
          onClick={() => setShowVoicePopup(false)}
          style={{
            position: "absolute",
            right: "12px",
            top: "8px",
            background: "none",
            border: "none",
            color: "var(--text)",
            cursor: "pointer",
            fontSize: "16px",
            opacity: 0.7,
            padding: "4px 2px",
            borderRadius: "6px",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.opacity = "1";
            e.target.style.backgroundColor = "var(--border-color)";
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = "0.7";
            e.target.style.backgroundColor = "transparent";
          }}
        >
          {/* <i className="bi bi-dash"></i> */}
        </button>
        {/* <div className="audioBox">
          {!isVideoActive ? (
            <VoiceAssistantUI
              state={
                voiceAnimationState === "listening"
                  ? "listening"
                  : voiceAnimationState === "ai-talking"
                  ? "speaking"
                  : voiceAnimationState === "tool-calling"
                  ? "processing"
                  : voiceAnimationState === "connecting"
                  ? "connecting"
                  : voiceAnimationState === "error"
                  ? "error"
                  : "processing"
              }
            />
          ) : (
            <div className="videoBox">
              <video ref={videoRef} autoPlay playsInline />
              <VoiceAssistantUI
                state={
                  voiceAnimationState === "listening"
                    ? "listening"
                    : voiceAnimationState === "ai-talking"
                    ? "speaking"
                    : voiceAnimationState === "tool-calling"
                    ? "processing"
                    : "processing"
                }
              />
            </div>
          )}
        </div> */}
      </div>
      {/* )} */}
      {/* </div> */}
      {showPublicKeyInvalidMessage ? <PleaseSetYourPublicKeyMessage /> : null}
      <SessionPopup
        show={showFormPopup}
        handleClose={handleClosePopup}
        title={isNewSession ? "New Topic" : "Rename Topic"}
        button={isNewSession ? "Create" : "Save"}
        handleConfirm={(newName, setError) => confirmRename(newName, setError)}
        initialValue=""
      />
      <AddToSessionModal
        show={showAddToSessionModal}
        handleClose={() => setShowAddToSessionModal(false)}
        activeSessionId={chatSessionId}
        isDarkMode={isDarkMode}
        defaultParentId={chatSessionParentId}
        addToChatText={addToChat}
        userId={userId}
      />
      {/* Notes Modal */}
      <NotesModal
        show={showNotesModal}
        onHide={() => setShowNotesModal(false)}
        isDarkMode={isDarkMode}
        userId={userId} // Pass userId to NotesModal
      />
      {/*  // Confirmation popup for clearing queue requests */}
      <ConfirmationPopup
        show={showClearQueueConfirm}
        handleClose={() => setShowClearQueueConfirm(false)}
        title="Clear All Queue Requests"
        body="Are you sure you want to clear all queue requests? This action cannot be undone."
        handleConfirm={async () => {
          await supabase
            .from("chat")
            .update({ is_delete: true })
            .eq("chat_session_id", chatSessionId)
            .eq("user_id", userId)
            .not("queue_id", "is", null);
          const { data: requestsToDelete } = await supabase
            .from("queue_request")
            .select("id")
            .eq("session_id", chatSessionId)
            .eq("user_id", userId);
          if (requestsToDelete && requestsToDelete.length > 0) {
            // Extract the IDs
            const requestIds = requestsToDelete.map((request) => request.id);

            // Delete from queue_chat first (child table) - only user's data
            await supabase
              .from("queue_chat")
              .delete()
              .in("queue_request_id", requestIds)
              .eq("user_auth", userId);

            // Then delete from queue_request (parent table) - only user's data
            await supabase
              .from("queue_request")
              .delete()
              .eq("session_id", chatSessionId);
          }
          // await supabase.from("queue_request").delete().eq("session_id", chatSessionId);
          setQueueRequests([]);
          setQueueRefreshKey((prev) => prev + 1);
          setShowClearQueueConfirm(false);
        }}
      />

      <ServerErrorModal show={showServerError}></ServerErrorModal>

      {/* Recharge Modal - shown when credits are exhausted */}
      {showRechargeModal && (
        <RechargeModal
          userId={userId}
          isDarkMode={isDarkMode}
          onClose={() => setShowRechargeModal(false)}
        />
      )}

      {/* Enhanced Queue Modal with Full Features */}
      <Modal
        size="lg"
        show={showQueueModal}
        onHide={handleQueueModalClose}
        centered
        className="queue-modal-enhanced"
      >
        <Modal.Header
          closeButton
          style={{
            background: queuePopupType === 'unconnected' ? "var(--orange-bg)" : "var(--teal-bg)",
            color: "white",
            borderBottom: "none",
          }}
        >
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className={queuePopupType === 'unconnected' ? "bi bi-box-arrow-right" : "bi bi-link-45deg"}></i>
            {queuePopupType === 'unconnected' ? "Standalone Queue" : "Connected Queue"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px" }}>
          {/* File Preview */}
          {queuePopupFile && (
            <div style={{
              marginBottom: "16px",
              padding: "12px",
              background: isDarkModeInternal ? "rgba(255,255,255,0.05)" : "#f8f9fa",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}>
              {queuePopupFilePreview ? (
                <img 
                  src={queuePopupFilePreview} 
                  alt="Preview" 
                  style={{ 
                    width: "48px", 
                    height: "48px", 
                    objectFit: "cover", 
                    borderRadius: "6px" 
                  }} 
                />
              ) : (
                <div style={{ width: "48px", height: "48px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {getFileIcon(queuePopupFile.name, 32)}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: "500", fontSize: "14px" }}>{queuePopupFile.name}</div>
                <div style={{ fontSize: "12px", opacity: 0.7 }}>
                  {(queuePopupFile.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button
                onClick={handleQueuePopupFileRemove}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  color: "var(--text)",
                  opacity: 0.6,
                }}
                title="Remove file"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}

          {/* Message Input with Mentions */}
          <div style={{ position: "relative", marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", fontSize: "14px" }}>
              Message
            </label>
            
            {/* Mentions Dropdown */}
            {queueShowMentions && queueMentions.length > 0 && (
              <div style={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                right: 0,
                background: isDarkModeInternal ? "#2d2d2d" : "#fff",
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                zIndex: 1000,
                maxHeight: "200px",
                overflowY: "auto",
                marginBottom: "4px",
              }}>
                <div style={{
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--border-color)",
                  fontSize: "11px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  color: "var(--teal-bg)",
                }}>
                  Actions
                </div>
                {queueMentions.map((mention, index) => (
                  <div
                    key={index}
                    onClick={() => handleQueueMentionSelect(mention)}
                    onMouseEnter={() => setQueueHighlightedIndex(index)}
                    style={{
                      padding: "10px 14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      backgroundColor: queueHighlightedIndex === index ? "var(--teal-bg)" : "transparent",
                      color: queueHighlightedIndex === index ? "#fff" : "var(--text)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <i className={`bi ${
                      mention.includes("Task") ? "bi-check2-square" :
                      mention.includes("Notes") ? "bi-journal-text" :
                      mention.includes("Gmail") ? "bi-envelope" :
                      mention.includes("Calendar") ? "bi-calendar3" :
                      mention.includes("Contacts") ? "bi-person-lines-fill" :
                      mention.includes("Notion") ? "bi-box" :
                      mention.includes("DeepResearch") ? "bi-search" :
                      mention.includes("WebSearch") ? "bi-globe" :
                      mention.includes("Reminders") ? "bi-bell" :
                      mention.includes("SecondBrain") ? "bi-lightbulb" :
                      mention.includes("Topics") ? "bi-chat-dots" :
                      mention.includes("ScrapeUrl") ? "bi-link-45deg" :
                      mention.includes("Lists") ? "bi-list-ul" :
                      "bi-at"
                    }`} style={{ fontSize: "14px" }}></i>
                    <span style={{ fontSize: "13px" }}>{mention.replace("@", "")}</span>
                  </div>
                ))}
              </div>
            )}
            
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <button
                onClick={() => {
                  const modes = ['simple', 'smart', 'genius'];
                  const idx = modes.indexOf(selectedMode);
                  setSelectedMode(modes[(idx + 1) % modes.length]);
                }}
                title="Click to switch mode"
                style={{
                  border: "none",
                  borderRadius: "8px",
                  padding: "0 14px",
                  height: "45px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  color: "#ffffff",
                  fontSize: "13px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  background: selectedMode === 'simple'
                    ? "var(--orange-bg, #f97316)"
                    : selectedMode === 'smart'
                    ? "linear-gradient(135deg, #8b5cf6, #6366f1)"
                    : "linear-gradient(135deg, #d97706, #9333ea)",
                  boxShadow: selectedMode === 'simple'
                    ? "0 2px 8px rgba(249, 115, 22, 0.3)"
                    : selectedMode === 'smart'
                    ? "0 2px 8px rgba(99, 102, 241, 0.3)"
                    : "0 2px 8px rgba(217, 119, 6, 0.3)",
                }}
              >
                <i className={`bi ${selectedMode === 'simple' ? 'bi-lightning-charge-fill' : selectedMode === 'smart' ? 'bi-stars' : 'bi-gem'}`}></i>
                {selectedMode.charAt(0).toUpperCase() + selectedMode.slice(1)}
              </button>
              <textarea
                ref={queueTextareaRef}
                value={queueMessage}
                onChange={handleQueueMessageChangeWithMentions}
                placeholder="Describe what you want queued (use @ for actions)"
                style={{
                  flex: 1,
                  minHeight: "120px",
                  maxHeight: "200px",
                  padding: "12px",
                  fontSize: "16px",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  resize: "vertical",
                  background: isDarkModeInternal ? "#2d2d2d" : "#fff",
                  color: "var(--text)",
                }}
              />
            </div>
          </div>

          {/* Details Input */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500",
              fontSize: "14px",
            }}>
              Helpful Context <span style={{ opacity: 0.5 }}>(optional)</span>
            </label>
            <input
              type="text"
              value={queueDetails}
              onChange={handleQueueDetailsChange}
              placeholder="Add helpful context (client, deadline, priority)..."
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "14px",
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                background: isDarkModeInternal ? "#2d2d2d" : "#fff",
                color: "var(--text)",
              }}
            />
          </div>

          {/* Action Bar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingTop: "8px",
            borderTop: "1px solid var(--border-color)",
          }}>
            {/* Hidden file input */}
            <input
              type="file"
              ref={queuePopupFileInputRef}
              onChange={handleQueuePopupFileSelect}
              style={{ display: "none" }}
              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
            />
            
            {/* Attach File Button */}
            <button
              onClick={() => queuePopupFileInputRef.current?.click()}
              title="Attach file"
              style={{
                background: "none",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "var(--text)",
                fontSize: "13px",
                transition: "all 0.2s ease",
              }}
            >
              <i className="bi bi-paperclip"></i>
              Attach
            </button>
            
            {/* Mention Button */}
            <button
              onClick={() => {
                setQueueMessage(prev => prev + "@");
                queueTextareaRef.current?.focus();
              }}
              title="Add action mention"
              style={{
                background: "none",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "var(--text)",
                fontSize: "13px",
                transition: "all 0.2s ease",
              }}
            >
              <i className="bi bi-at"></i>
              Action
            </button>

            {/* Voice Recording Button */}
            <MicButton
              onTranscript={(text) => {
                setQueueMessage(prev => prev ? `${prev} ${text}` : text);
                queueTextareaRef.current?.focus();
              }}
              onDirectSend={(text) => {
                setQueueMessage(text);
                setTimeout(() => handleQueueSend(), 100);
              }}
              size="sm"
            />
            
            <div style={{ flex: 1 }}></div>
            
            {/* Queue Status Indicator */}
            {queue && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 10px",
                background: "rgba(var(--teal-rgb), 0.1)",
                borderRadius: "6px",
                fontSize: "12px",
                color: "var(--teal-bg)",
              }}>
                <i className="bi bi-check-circle-fill"></i>
                Queue Mode Active
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ 
          borderTop: "1px solid var(--border-color)",
          padding: "12px 20px",
        }}>
          <Button 
            variant="outline-secondary" 
            onClick={handleQueueModalClose}
            style={{ borderRadius: "6px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleQueueSend}
            disabled={!queueMessage.trim()}
            style={{
              background: "var(--teal-bg)",
              border: "none",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="bi bi-plus-circle"></i>
            Add to Queue
          </Button>
        </Modal.Footer>
      </Modal>
      {/* END MODAL CODE */}
    </>
  );
};

const usePublicKeyInvalid = () => {
  const [showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage] =
    useState(false);

  // close public key invalid message after delay
  useEffect(() => {
    if (showPublicKeyInvalidMessage) {
      setTimeout(() => {
        setShowPublicKeyInvalidMessage(false);
      }, 1000);
    }
  }, [showPublicKeyInvalidMessage]);

  return {
    showPublicKeyInvalidMessage,
    setShowPublicKeyInvalidMessage,
  };
};

const PleaseSetYourPublicKeyMessage = () => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "25px",
        left: "25px",
        padding: "10px",
        color: "#fff",
        backgroundColor: "#f03e3e",
        borderRadius: "5px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
      }}
    ></div>
  );
};

export default ChatBot;
